import React, { useState, useEffect, useRef, useMemo } from 'react';
import StoryFrameConnect, { readStoryFrameRequest } from './StoryFrameConnect';
import ViralConnect, { readViralRequest } from './ViralConnect';
import HopeLabView from './hopelab/HopeLabView';
import HopeLabSettings from './hopelab/HopeLabSettings';
import FbStudio from './fb/FbStudio';
import { FbMark } from './fb/FbLogo';
import CopyrightRegistryModal, { CopyrightBadge, CopyrightDetailModal } from './CopyrightRegistryModal';
import { COPYRIGHT_STATUS, findCopyrightRecord, getTaskCopyrightInfo, normalizeBookTitle } from './copyrightRegistry';
import './hopelab/hopelab.css';
import VoiceView, { VoiceMiniBar } from './voice/VoiceView';
import { VoiceProvider } from './voice/VoiceProvider';
import './voice/voice.css';
import { NasProvider } from './nas/NasProvider';
import NasView from './nas/NasView';
import NasTaskButton from './nas/NasTaskButton';
import NasQuickResults from './nas/NasQuickResults';
import { NasTaskHint, AuthorOptions } from './nas/NasTaskHint';
import './nas/nas.css';
import { toPng } from 'html-to-image';
import { createPortal } from 'react-dom';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    setPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import {
    getFirestore,
    collection,
    addDoc,
    onSnapshot,
    doc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    query,
    orderBy,
    limit,
    getDocs,
    getDoc,
    setDoc,
    writeBatch
} from 'firebase/firestore';
import {
    LayoutDashboard,
    ListTodo,
    Youtube,
    Users,
    BookOpen,
    Plus,
    Trash2,
    CheckCircle2,
    Search,
    Bell,
    Menu,
    X,
    Star,
    Sparkles,
    Link as LinkIcon,
    Bot,
    Settings,
    Shield,
    ShieldAlert,
    Key,
    Lock,
    LogIn,
    LogOut,
    User,
    FileText,
    Folder,
    ChevronDown,
    ChevronRight,
    ChevronLeft,
    RefreshCw,
    PlayCircle,
    AlertTriangle,
    ThumbsUp,
    Calendar,
    ArrowRight,
    Activity,
    Cloud,
    Wrench,
    DownloadCloud,
    DollarSign,
    Percent,
    Calculator,
    FolderPlus,
    Clock,
    Wifi,
    WifiOff,
    Save,
    Video,
    Target,
    BarChart3,
    Edit2,
    MoreHorizontal,
    UserCog,
    Image as ImageIcon,
    ExternalLink,
    SortAsc,
    Eye,
    Wand2,
    Hash,
    Coins,
    Filter,
    FolderCog,
    MoveRight,
    Megaphone,
    AlertOctagon,
    Pin,
    Send,
    Share2,
    RotateCcw,
    Globe,
    Trophy,
    TrendingDown,
    FolderOpen,
    PiggyBank,
    Headphones,
    HardDrive,
    ArrowRightCircle,
    CheckCircle,
    ArrowUpRight,
    ArrowDownRight,
    Utensils,
    Monitor,
    Coffee,
    PartyPopper,
    ArrowLeft,
    TrendingUp,
    Mail
} from 'lucide-react';

// --- CẤU HÌNH BẢO MẬT (Đã khôi phục) ---

// --- Firebase Configuration ---
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Khởi tạo Firebase
let app, auth, db;
try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
} catch (e) {
    console.error("Lỗi khởi tạo Firebase.", e);
}

const appId = 'audiobook-manager-production';
const ITEMS_PER_PAGE = 10;
const getCachedGoogleSession = () => {
    try { return JSON.parse(localStorage.getItem('hopeteam_cached_session') || 'null'); }
    catch (_) { return null; }
};

const getEmbeddedBrowserName = () => {
    if (typeof navigator === 'undefined') return '';
    const ua = navigator.userAgent || '';
    if (/FBAN|FBAV|Messenger/i.test(ua)) return 'Messenger/Facebook';
    if (/Instagram/i.test(ua)) return 'Instagram';
    if (/TikTok|BytedanceWebview/i.test(ua)) return 'TikTok';
    if (/Zalo/i.test(ua)) return 'Zalo';
    if (/Line\//i.test(ua)) return 'LINE';
    return '';
};

const getGoogleAuthErrorMessage = (error) => {
    if (error?.code === 'auth/popup-closed-by-user') return 'Bạn đã đóng cửa sổ đăng nhập.';
    if (error?.code === 'auth/popup-blocked') return 'Trình duyệt đã chặn cửa sổ đăng nhập. Hãy cho phép popup hoặc thử lại.';
    if (error?.code === 'auth/unauthorized-domain') return 'Tên miền hiện tại chưa được cho phép trong Firebase Authentication.';
    if (error?.code === 'auth/network-request-failed') return 'Kết nối mạng không ổn định. Vui lòng kiểm tra mạng và thử lại.';
    if (error?.code === 'auth/web-storage-unsupported') return 'Trình duyệt đang chặn bộ nhớ đăng nhập. Hãy mở trang bằng Safari hoặc Chrome.';
    return 'Không thể đăng nhập Google. Vui lòng mở trang bằng Safari hoặc Chrome rồi thử lại.';
};

// --- Helper Functions ---
const callGeminiAPI = async (apiKeyTokens, prompt) => {
    if (!apiKeyTokens) throw new Error("Vui lòng nhập API Key trong Cài đặt");

    // Support multiple keys separated by comma
    const keys = apiKeyTokens.split(',').map(k => k.trim()).filter(k => k);
    let lastError = null;

    for (const key of keys) {
        try {
            const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${key}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
            });

            if (!response.ok) {
                // If 429 (Too Many Requests) or 403 (Quota), try next key
                if (response.status === 429 || response.status === 403) {
                    console.warn(`Key ${key.slice(0, 5)}... failed with ${response.status}. Rotating...`);
                    continue;
                }
                throw new Error(`API Error: ${response.statusText}`);
            }

            const data = await response.json();
            return data.candidates?.[0]?.content?.parts?.[0]?.text || "Không thể phân tích dữ liệu.";
        } catch (e) {
            console.error(`Key ${key.slice(0, 5)}... error:`, e);
            lastError = e;
            // Continue to next key
        }
    }

    throw lastError || new Error("All API Keys failed.");
};

// Helper parse JSON from AI response (Clean markdown code blocks)
const parseAIJSON = (text) => {
    try {
        const jsonMatch = text.match(/\[[\s\S]*\]/) || text.match(/\{[\s\S]*\}/);
        if (jsonMatch) return JSON.parse(jsonMatch[0]);
        return JSON.parse(text);
    } catch (e) {
        return null;
    }
};

const parseSubCount = (str) => {
    if (!str) return 0;
    if (typeof str === 'number') return str;
    let s = str.toUpperCase().replace(/,/g, '').replace(' SUBS', '').trim();
    if (s.includes('M')) return parseFloat(s) * 1000000;
    if (s.includes('K')) return parseFloat(s) * 1000;
    return parseFloat(s) || 0;
};

const fetchYouTubeRealData = async (url, youtubeKeyTokens) => {
    if (!youtubeKeyTokens) {
        alert("LỖI: Chưa có YouTube API Key! Vui lòng vào Cài đặt để thêm key.");
        return null;
    }

    const keys = youtubeKeyTokens.split(',').map(k => k.trim()).filter(k => k);

    try {
        const urlObj = new URL(url);
        let apiBaseUrl = "";
        let paramName = "";
        let paramValue = "";

        if (urlObj.pathname.startsWith('/channel/')) {
            apiBaseUrl = "https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics";
            paramName = "id";
            paramValue = urlObj.pathname.split('/')[2];
        } else if (urlObj.pathname.startsWith('/@')) {
            apiBaseUrl = "https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics";
            paramName = "forHandle";
            paramValue = urlObj.pathname.slice(1);
        } else if (urlObj.pathname.startsWith('/c/') || urlObj.pathname.startsWith('/user/')) {
            apiBaseUrl = "https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics";
            paramName = "forUsername";
            paramValue = urlObj.pathname.split('/')[2];
        } else {
            alert("Link không hợp lệ. Vui lòng dùng link dạng youtube.com/@handle hoặc youtube.com/channel/ID");
            return null;
        }

        // --- ROTATION LOGIC ---
        for (const key of keys) {
            try {
                const finalUrl = `${apiBaseUrl}&${paramName}=${paramValue}&key=${key}`;
                const res = await fetch(finalUrl);

                if (!res.ok) {
                    if (res.status === 403 || res.status === 429) {
                        console.warn(`YouTube Key ${key.slice(0, 5)}... quota exceeded. Rotating...`);
                        continue; // Try next key
                    }
                    throw new Error(`YouTube API Error: ${res.status}`);
                }

                const data = await res.json();

                if (data.items && data.items.length > 0) {
                    const item = data.items[0];
                    const subs = parseInt(item.statistics.subscriberCount);
                    let formattedSubs = subs;
                    if (subs >= 1000000) formattedSubs = (subs / 1000000).toFixed(1) + "M";
                    else if (subs >= 1000) formattedSubs = (subs / 1000).toFixed(1) + "K";

                    return {
                        name: item.snippet.title,
                        subscribers: `${formattedSubs} Subs`,
                        rawSubs: subs,
                        thumbnail: item.snippet.thumbnails.default.url,
                        viewCount: item.statistics.viewCount,
                        videoCount: item.statistics.videoCount
                    };
                } else {
                    // If no items found, but API worked, maybe ID is wrong. check next key just in case? No, usually ID error is consistent.
                    console.log("No items found with key", key);
                    // alert("Không tìm thấy kênh này qua API. Hãy kiểm tra lại Link.");
                    // Return null here or continue? Usually if success but no data, it's data error.
                    return null;
                }
            } catch (err) {
                console.error("Key failed", err);
            }
        }
        alert("Tất cả API Key đều hết hạn mức hoặc xảy ra lỗi.");
        return null;

    } catch (e) {
        console.error("Lỗi URL/Hệ thống:", e);
        return null;
    }
};

const fetchYouTubeVideoRealData = async (url, youtubeKeyTokens) => {
    if (!youtubeKeyTokens) {
        alert("LỖI: Chưa có YouTube API Key!");
        return null;
    }
    const keys = youtubeKeyTokens.split(',').map(k => k.trim()).filter(k => k);
    try {
        const urlObj = new URL(url);
        let videoId = "";
        if (urlObj.hostname.includes("youtube.com")) {
            videoId = urlObj.searchParams.get("v");
        } else if (urlObj.hostname.includes("youtu.be")) {
            videoId = urlObj.pathname.slice(1);
        }

        if (!videoId) {
            alert("Link video không hợp lệ. Vui lòng dùng link youtube.com/watch?v=ID hoặc youtu.be/ID");
            return null;
        }

        const apiBaseUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}`;
        for (const key of keys) {
            try {
                const res = await fetch(`${apiBaseUrl}&key=${key}`);
                if (!res.ok) {
                    if (res.status === 403 || res.status === 429) continue;
                    throw new Error(`API Error: ${res.status}`);
                }
                const data = await res.json();
                if (data.items && data.items.length > 0) {
                    const item = data.items[0];
                    return {
                        title: item.snippet.title,
                        channelTitle: item.snippet.channelTitle,
                        publishedAt: item.snippet.publishedAt
                    };
                } else {
                    return { isDead: true };
                }
            } catch (err) { }
        }
        // If all fail
        alert("Tất cả API Key đều hết hạn mức hoặc xảy ra lỗi.");
        return null;
    } catch (e) {
        console.error("Lỗi parse URL/System:", e);
        return null;
    }
};

const timeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " năm trước";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " tháng trước";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " ngày trước";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " giờ trước";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " phút trước";
    return Math.floor(seconds) + " giây trước";
};

const formatCurrency = (amount) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
const formatUSD = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Mock AI Advice
const mockAIAdvice = (tasks) => {
    const pending = tasks.filter(t => t.status === 'Chờ đăng').length;
    if (pending > 5) return "Cảnh báo: Tồn đọng công việc cao. Hãy ưu tiên xử lý các task cũ nhất.";
    return "Mọi thứ đang trong tầm kiểm soát. Hãy duy trì tiến độ này.";
};

// --- Components ---
const Card = ({ children, className = "" }) => (
    <div className={`liquid-glass rounded-2xl p-6 ${className}`}>{children}</div>
);

const Button = ({ children, onClick, variant = "primary", className = "", icon: Icon, disabled = false, isLoading = false, ...props }) => {
    const baseStyle = "relative overflow-hidden flex items-center justify-center px-5 py-2.5 rounded-xl font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group active:scale-[0.98]";
    const variants = {
        primary: "app-primary-button bg-[#DEDBC8] hover:bg-lime-300 text-black shadow-lg shadow-black/30 border border-white/30",
        secondary: "bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 hover:border-white/20 backdrop-blur-sm",
        danger: "bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20",
        ghost: "hover:bg-white/5 text-slate-400 hover:text-white",
        ai: "app-primary-button bg-[#DEDBC8] hover:bg-lime-300 text-black shadow-lg shadow-black/30 border border-white/30",
        success: "app-primary-button bg-[#DEDBC8] hover:bg-lime-300 text-black shadow-lg shadow-black/30 border border-white/30"
    };

    return (
        <button onClick={onClick} disabled={disabled} className={`${baseStyle} ${variants[variant]} ${className}`} {...props}>
            {Icon && <Icon size={18} className={`mr-2 ${isLoading ? 'animate-spin' : 'group-hover:scale-110 transition-transform'}`} />}
            {children}
        </button>
    );
};

const Badge = ({ status }) => {
    const styles = { 'Đã xong': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', 'Chờ đăng': 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
    return <span className={`task-status-badge task-status-badge-${status === 'Đã xong' ? 'done' : 'pending'} px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['Chờ đăng']}`}>{status}</span>;
};

// --- PAGINATION COMPONENT ---
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-end gap-2 mt-4 pt-2 border-t border-slate-800/50">
            <span className="text-xs text-slate-500 mr-2">Trang {currentPage} / {totalPages}</span>
            <div className="flex gap-1">
                <button
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
                >
                    <ChevronLeft size={16} />
                </button>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

// --- ADMIN ACTIVITY TOAST (Realtime Facebook-style) ---
const AdminActivityToast = ({ logs, currentUserData }) => {
    const [visibleToasts, setVisibleToasts] = useState([]);

    useEffect(() => {
        if (logs.length > 0) {
            const latestLog = logs[0];
            // Admin only notified of non-admin actions
            if (latestLog.user !== 'admin') {
                const isRecent = latestLog.createdAt && (new Date() - latestLog.createdAt.toDate() < 10000); // within 10 seconds
                if (isRecent) {
                    setVisibleToasts(prev => {
                        if (prev.find(t => t.id === latestLog.id)) return prev;
                        return [latestLog, ...prev].slice(0, 3);
                    });
                }
            }
        }
    }, [logs]);

    useEffect(() => {
        if (visibleToasts.length > 0) {
            const timer = setTimeout(() => {
                setVisibleToasts(prev => prev.slice(0, prev.length - 1));
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [visibleToasts]);

    if (visibleToasts.length === 0) return null;

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 pointer-events-none">
            {visibleToasts.map((log) => (
                <div key={log.id} className="bg-slate-900/95 backdrop-blur-md border border-slate-700 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] rounded-2xl p-4 flex gap-4 items-start w-80 animate-in slide-in-from-right-8 fade-in duration-300">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/30 shadow-inner">
                        <Bell size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-1">
                            <p className="text-sm font-bold text-white truncate">{log.author || 'Nhân sự'}</p>
                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-800 border border-slate-700 text-slate-400 rounded uppercase font-bold tracking-wider shrink-0">{log.action}</span>
                        </div>
                        <p className="text-xs text-slate-300 line-clamp-2">{log.details}</p>
                    </div>
                </div>
            ))}
        </div>
    );
};

// --- STAFF TASK REMINDER TOAST ---
const StaffTaskReminderToast = ({ tasks, currentUserData, onNavigate }) => {
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [newTasks, setNewTasks] = useState([]);

    const safeTasks = Array.isArray(tasks) ? tasks : [];
    const pendingTasks = safeTasks.filter(t => t.status === 'Chờ đăng');
    const todayStr = new Date().toISOString().split('T')[0];

    const hasCompared = useRef(false);

    // Compare with last page visit (localStorage stores previous visit's IDs)
    useEffect(() => {
        if (!currentUserData || dismissed) return;
        if (safeTasks.length === 0) return; // Wait for Firebase data to load
        if (hasCompared.current) return; // Only compare once per mount (F5 resets this)

        const storageKey = `staff_tasks_seen_${currentUserData.name}`;
        const seenIds = JSON.parse(localStorage.getItem(storageKey) || '[]');

        // Only show NEW + PENDING tasks (not completed ones)
        const brandNewTasks = pendingTasks.filter(t => !seenIds.includes(t.id));

        if (brandNewTasks.length > 0) {
            setNewTasks(brandNewTasks);
            setVisible(true);
        }

        // Save current IDs for next page visit
        const currentIds = safeTasks.map(t => t.id);
        localStorage.setItem(storageKey, JSON.stringify(currentIds));
        hasCompared.current = true;
    }, [safeTasks.length, currentUserData, dismissed]);

    // Auto-dismiss after 30 seconds
    useEffect(() => {
        if (visible && !dismissed) {
            const timer = setTimeout(() => { setVisible(false); setDismissed(true); }, 30000);
            return () => clearTimeout(timer);
        }
    }, [visible, dismissed]);

    // Only show if there are new tasks
    if (!visible || dismissed || newTasks.length === 0) return null;

    // Find nearest deadline among new tasks
    const sortedNew = [...newTasks].filter(t => t.date).sort((a, b) => new Date(a.date) - new Date(b.date));
    const nearestDate = sortedNew[0]?.date;
    let daysUntilDeadline = null;
    if (nearestDate) {
        daysUntilDeadline = Math.ceil((new Date(nearestDate) - new Date(todayStr)) / (1000 * 60 * 60 * 24));
    }
    const isOverdue = daysUntilDeadline !== null && daysUntilDeadline < 0;
    const isUrgent = daysUntilDeadline !== null && daysUntilDeadline <= 1 && daysUntilDeadline >= 0;

    return (
        <div className="fixed bottom-6 right-6 z-50 pointer-events-auto" style={{ animation: 'slideUpFadeIn 0.4s ease-out' }}>
            <style>{`
                @keyframes slideUpFadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
            <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 shadow-[0_10px_50px_-10px_rgba(0,0,0,0.7)] rounded-2xl p-5 w-[370px] relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-cyan-500`}></div>

                <button onClick={() => { setVisible(false); setDismissed(true); }} className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors p-1 rounded-full hover:bg-slate-800">
                    <X size={14} />
                </button>

                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-emerald-500/20 text-emerald-400">
                        <Sparkles size={20} />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-white">Bạn có {newTasks.length} task mới!</h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">Xin chào {currentUserData?.name}, hãy kiểm tra nhé.</p>
                    </div>
                </div>

                {/* New task list */}
                <div className="space-y-1.5 mb-4 max-h-[180px] overflow-y-auto pr-1 custom-scrollbar">
                    {newTasks.slice(0, 5).map(task => (
                        <div key={task.id} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-800/60 border border-slate-700/50">
                            <div className="w-7 h-7 rounded-lg bg-blue-500/15 text-blue-400 flex items-center justify-center shrink-0">
                                <FileText size={14} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-slate-200 truncate">{task.bookTitle}</p>
                                <p className="text-[10px] text-slate-500 truncate">{task.channel} • Hạn: {task.date || 'Chưa rõ'}</p>
                            </div>
                        </div>
                    ))}
                    {newTasks.length > 5 && (
                        <p className="text-[10px] text-slate-500 text-center pt-1">và {newTasks.length - 5} task khác...</p>
                    )}
                </div>

                {/* Summary row */}
                <div className="flex gap-2 mb-4">
                    <div className="flex-1 p-2 rounded-lg bg-slate-800/40 border border-slate-700/30 text-center">
                        <p className="text-[10px] text-slate-500">Tổng chờ xử lý</p>
                        <p className="text-sm font-bold text-white">{pendingTasks.length}</p>
                    </div>
                    {nearestDate && (
                        <div className={`flex-1 p-2 rounded-lg border text-center ${isOverdue ? 'bg-red-500/10 border-red-500/20' : isUrgent ? 'bg-amber-500/10 border-amber-500/20' : 'bg-slate-800/40 border-slate-700/30'}`}>
                            <p className={`text-[10px] ${isOverdue ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-slate-500'}`}>Deadline gần nhất</p>
                            <p className={`text-sm font-bold font-mono ${isOverdue ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-white'}`}>
                                {isOverdue ? `Trễ ${Math.abs(daysUntilDeadline)}d` : daysUntilDeadline === 0 ? 'Hôm nay!' : `${daysUntilDeadline} ngày`}
                            </p>
                        </div>
                    )}
                </div>

                <button onClick={() => { onNavigate('tasks'); setVisible(false); setDismissed(true); }} className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20">
                    Xem danh sách việc <ArrowRight size={14} />
                </button>
            </div>
        </div>
    );
};

// --- REDESIGNED NOTIFICATION PANEL ---
const NotificationPanel = ({ isOpen, onClose, logs, onLogClick, userRole, currentUserData, staff = [] }) => {
    const [showAll, setShowAll] = useState(false);
    const [dateFilter, setDateFilter] = useState('today');
    const [typeFilter, setTypeFilter] = useState('all');
    const [authorFilter, setAuthorFilter] = useState('all');

    if (!isOpen) return null;

    const roleFilteredLogs = userRole === 'admin'
        ? logs
        : logs.filter(l => l.author === (currentUserData ? currentUserData.name : 'Unknown'));
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date(todayStart);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    const filteredLogs = roleFilteredLogs.filter(log => {
        const createdDate = log.createdAt?.toDate ? log.createdAt.toDate() : null;
        const matchesDate = dateFilter === 'all' || (createdDate && createdDate >= (dateFilter === 'today' ? todayStart : sevenDaysAgo));
        const actionText = `${log.action || ''} ${log.details || ''}`.toLocaleLowerCase('vi-VN');
        const matchesType = typeFilter === 'all' || !!log.metadata?.taskId || actionText.includes('task');
        const matchesAuthor = authorFilter === 'all' || log.author === authorFilter;
        return matchesDate && matchesType && matchesAuthor;
    });
    const availableAuthors = [...new Set(roleFilteredLogs.map(log => log.author).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'vi'));

    const displayedLogs = showAll ? filteredLogs : filteredLogs.slice(0, 6);

    return (
        <div className="absolute top-16 right-4 w-[400px] bg-slate-900/95 border border-slate-700 rounded-3xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.7)] z-50 animate-in fade-in slide-in-from-top-4 overflow-hidden flex flex-col max-h-[85vh] backdrop-blur-xl">
            <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-800/20">
                <h3 className="text-lg font-black text-white tracking-tight flex items-center gap-2"><Bell className="text-blue-500" /> Nhật ký hoạt động</h3>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors"><X size={18} /></button>
            </div>
            <div className="activity-filters">
                <div className="activity-filter-tabs">
                    {[['today', 'Hôm nay'], ['7days', '7 ngày'], ['all', 'Tất cả']].map(([value, label]) => <button key={value} onClick={() => { setDateFilter(value); setShowAll(false); }} className={dateFilter === value ? 'active' : ''}>{label}</button>)}
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setShowAll(false); }}><option value="all">Mọi hoạt động</option><option value="tasks">Chỉ hoạt động Task</option></select>
                    {userRole === 'admin' ? <select value={authorFilter} onChange={e => { setAuthorFilter(e.target.value); setShowAll(false); }}><option value="all">Tất cả nhân sự</option>{availableAuthors.map(author => <option key={author} value={author}>{author}</option>)}</select> : <div className="activity-result-count">{filteredLogs.length} hoạt động</div>}
                </div>
            </div>
            <div className="overflow-y-auto p-3 space-y-2 custom-scrollbar">
                {filteredLogs.length === 0 ? <div className="p-10 flex flex-col items-center justify-center text-slate-500"><Activity size={32} className="mb-3 opacity-20" /><p className="text-sm font-medium">Chưa có hoạt động nào.</p></div> : displayedLogs.map((log) => {
                    const isError = log.type === 'error';
                    const isWarning = log.type === 'warning';
                    const logStaff = staff.find(person => person.name === log.author);

                    return (
                        <div
                            key={log.id}
                            onClick={() => {
                                if (log.metadata && log.metadata.taskId) onLogClick(log.metadata.taskId, 'tasks');
                                else onLogClick(null, 'dashboard');
                            }}
                            className={`group flex items-start gap-4 p-4 rounded-2xl transition-all cursor-pointer ${isError ? 'bg-red-500/10 hover:bg-red-500/20 border-red-500/20' : isWarning ? 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20' : 'bg-slate-800/50 hover:bg-slate-800 border-slate-700/50'} border`}
                        >
                            <div className="shrink-0 relative mt-1">
                                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold text-sm shadow-inner uppercase">
                                    {logStaff?.avatar ? <img src={logStaff.avatar} alt={log.author || 'HopeTeam'} className="w-full h-full object-cover rounded-full" /> : (log.author ? log.author.substring(0, 2) : 'HT')}
                                </div>
                                <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-slate-900 ${isError ? 'bg-red-500 text-white' : isWarning ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'}`}>
                                    {isError ? <AlertTriangle size={10} /> : isWarning ? <AlertOctagon size={10} /> : <CheckCircle2 size={10} />}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start mb-1">
                                    <p className="text-sm text-slate-200 font-medium leading-tight">
                                        Thao tác bởi: <strong className="text-white">{log.author || 'Hệ thống'}</strong>
                                    </p>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-800 border border-slate-700 text-slate-400 rounded uppercase font-bold tracking-wider">{log.action}</span>
                                        {log.metadata?.taskId && <ExternalLink size={12} className="text-slate-500 opacity-0 group-hover:opacity-100" />}
                                    </div>
                                </div>
                                <div className="text-xs text-slate-300 bg-slate-950/50 p-2 rounded-lg border border-slate-800 mt-2 mb-2 leading-relaxed">{log.details}</div>
                                <p className="text-[10px] text-slate-500 font-bold tracking-wider flex items-center gap-1">
                                    <Clock size={10} />
                                    {log.createdAt ? (
                                        <span>
                                            {timeAgo(log.createdAt.toDate())} &middot; <span className="font-mono font-normal">
                                                {new Date(log.createdAt.toDate()).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} {new Date(log.createdAt.toDate()).toLocaleDateString('vi-VN')}
                                            </span>
                                        </span>
                                    ) : 'Vừa xong'}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
            {filteredLogs.length > 6 && (
                <div className="p-3 border-t border-slate-800 bg-slate-800/20 text-center">
                    <button onClick={() => setShowAll(!showAll)} className="w-full py-2.5 rounded-xl bg-slate-800 text-sm hover:bg-slate-700 text-white transition-colors font-bold shadow-lg">
                        {showAll ? "Thu gọn bảng" : `Xem lịch sử cũ hơn (${filteredLogs.length})`}
                    </button>
                </div>
            )}
        </div>
    );
};

// --- DASHBOARD VIEW ---
// --- DASHBOARD VIEW (V2 - PREMIUM & SAFE) ---
const DashboardView = ({ tasks = [], channels = [], staff = [], allStaff = [], allChannels = [], allRevenues = [], currentUserData, apiKey, onNavigate, notices = [], onAddNotice, onDeleteNotice, userRole, revenues = [], exchangeRate, sessionNoticeShown, setSessionNoticeShown, introducedWebsites = [], strikes = [] }) => {
    // --- DATE HELPERS ---
    const getLocalYearMonth = (d) => {
        try {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            return `${year}-${month}`;
        } catch (e) { return '2024-01'; }
    };

    const todayObj = new Date();
    const todayStr = todayObj.toISOString().split('T')[0];
    const currentDay = todayObj.getDate();
    const currentHour = todayObj.getHours();

    const prevMonthObj = new Date();
    prevMonthObj.setDate(0);
    const previousMonth = getLocalYearMonth(prevMonthObj);
    const daysInMonth = new Date(todayObj.getFullYear(), todayObj.getMonth() + 1, 0).getDate();

    // --- STATE ---
    const [isNoticePopupOpen, setIsNoticePopupOpen] = useState(false);
    const [newNotice, setNewNotice] = useState({ content: '', priority: 'normal', validUntil: '' });

    // --- SAFE DATA DERIVATION ---
    const safeTasks = Array.isArray(tasks) ? tasks : [];
    const safeChannels = Array.isArray(channels) ? channels : [];
    const safeStaff = Array.isArray(staff) ? staff : [];
    const safeNotices = Array.isArray(notices) ? notices : [];

    const todayTasks = safeTasks.filter(t => t.date === todayStr);
    const pendingTasks = safeTasks.filter(t => t.status === 'Chờ đăng');
    const completedTasks = safeTasks.filter(t => t.status === 'Đã xong').length;
    const orphanChannels = safeChannels.filter(c => !c.manager);
    const urgentTasks = [...pendingTasks].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));

    // --- STRIKE LOGIC ---
    const dashboardTodayKey = getDateKey();
    const activeStrikes = Array.isArray(strikes) ? strikes.filter(strike => isActiveCopyrightStrike(strike, dashboardTodayKey)) : [];
    const activeStrikeChannelCount = new Set(activeStrikes.map(strike =>
        strike.channelId || strike.channelName || strike.email || strike.id
    ).filter(Boolean)).size;
    const nearestStrikeExpiry = activeStrikes
        .map(strike => getStrikeExpiryKey(strike))
        .filter(Boolean)
        .sort()[0] || '';
    const nearestStrikeDays = getDaysUntilDateKey(nearestStrikeExpiry, dashboardTodayKey);

    // --- DEADLINE LOGIC ---
    const daysUntilSalary = 25 - currentDay;
    const daysUntilEndOfMonth = daysInMonth - currentDay;

    // --- STAFF BACKLOG LOGIC ---
    const staffBacklog = useMemo(() => {
        const backlog = {};
        urgentTasks.forEach(task => {
            const assignee = task.assignee || 'Unassigned';
            if (!backlog[assignee]) backlog[assignee] = { count: 0, tasks: [], avatar: getStaffAvatar(assignee) };
            backlog[assignee].count++;
            backlog[assignee].tasks.push(task);
        });
        return Object.entries(backlog)
            .sort(([, a], [, b]) => b.count - a.count)
            .map(([name, data]) => ({ name, ...data }));
    }, [urgentTasks, safeStaff]);

    // Greeting
    const getGreeting = () => {
        if (currentHour < 12) return "Chào buổi sáng";
        if (currentHour < 18) return "Chào buổi chiều";
        return "Chào buổi tối";
    };

    // --- REVENUE LOGIC ---
    const getUniqueRevenue = (chId, month, customRevs = null) => {
        const targetRevs = customRevs || revenues;
        if (!Array.isArray(targetRevs)) return 0;
        const revs = targetRevs.filter(r => r.channelId === chId && r.month === month);
        if (revs.length === 0) return 0;
        revs.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
        return Number(revs[0].amount) || 0;
    };

    const sourceStaffForTop = allStaff && allStaff.length > 0 ? allStaff : safeStaff;
    const sourceChannelsForTop = allChannels && allChannels.length > 0 ? allChannels : safeChannels;
    const sourceRevenuesForTop = allRevenues && allRevenues.length > 0 ? allRevenues : revenues;

    const topStaff = sourceStaffForTop.map(s => {
        const staffChannels = sourceChannelsForTop.filter(c => c.manager === s.name);
        const staffRevenue = staffChannels.reduce((acc, ch) => acc + getUniqueRevenue(ch.id, previousMonth, sourceRevenuesForTop), 0);
        return { ...s, revenue: staffRevenue };
    }).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

    const adminTeamOverview = sourceStaffForTop.map(person => {
        const personTasks = safeTasks.filter(task => task.assignee === person.name);
        const personPending = personTasks.filter(task => task.status === 'Chờ đăng').sort((a, b) => String(a.date || '9999').localeCompare(String(b.date || '9999')));
        const overdue = personPending.filter(task => task.date && task.date < todayStr);
        const dueToday = personPending.filter(task => task.date === todayStr);
        const unsafe = personPending.filter(task => task.isUnsafe);
        const personChannels = sourceChannelsForTop.filter(channel => channel.manager === person.name);
        const revenue = personChannels.reduce((sum, channel) => sum + getUniqueRevenue(channel.id, previousMonth, sourceRevenuesForTop), 0);
        let attention = { label: 'Đang ổn', tone: 'ok' };
        if (overdue.length) attention = { label: `${overdue.length} task trễ hạn`, tone: 'danger' };
        else if (unsafe.length) attention = { label: `${unsafe.length} task cần lưu ý`, tone: 'warning' };
        else if (personPending.length >= 8) attention = { label: 'Khối lượng cao', tone: 'warning' };
        else if (!personChannels.length) attention = { label: 'Chưa được gán kênh', tone: 'muted' };
        else if (!personPending.length) attention = { label: 'Sẵn sàng nhận việc', tone: 'ready' };
        return { ...person, personPending, overdue, dueToday, personChannels, revenue, attention };
    }).sort((a, b) => b.overdue.length - a.overdue.length || b.personPending.length - a.personPending.length);

    // --- EFFECTS ---
    useEffect(() => {
        if (safeNotices.length > 0 && !sessionNoticeShown) {
            setIsNoticePopupOpen(true);
            setSessionNoticeShown(true);
        }
    }, [safeNotices.length, sessionNoticeShown, setSessionNoticeShown]);

    const handlePostNotice = (e) => {
        e.preventDefault();
        if (!newNotice.content.trim()) return;
        onAddNotice(newNotice);
        setNewNotice({ content: '', priority: 'normal', validUntil: '' });
    };

    function getStaffAvatar(name) { return safeStaff.find(s => s.name === name)?.avatar; }

    // --- INDIVIDUAL (STAFF PASSCODE) DASHBOARD ---
    if (currentUserData) {
        // Calculate MoM
        const twoMonthsAgoObj = new Date();
        twoMonthsAgoObj.setDate(0);
        twoMonthsAgoObj.setDate(0);
        const twoMonthsAgo = getLocalYearMonth(twoMonthsAgoObj);

        const currentRev = safeChannels.reduce((acc, ch) => acc + getUniqueRevenue(ch.id, previousMonth, allRevenues), 0);
        const pastRev = safeChannels.reduce((acc, ch) => acc + getUniqueRevenue(ch.id, twoMonthsAgo, allRevenues), 0);
        let growth = 0;
        if (pastRev > 0) growth = ((currentRev - pastRev) / pastRev) * 100;
        else if (currentRev > 0) growth = 100;

        // Calculate Weekly Tasks (Tasks due in the last 7 days)
        const aWeekAgo = new Date();
        aWeekAgo.setDate(aWeekAgo.getDate() - 7);
        const recentTasks = safeTasks.filter(t => {
            const td = new Date(t.date);
            return td >= aWeekAgo && td <= todayObj;
        });
        const completedRecent = recentTasks.filter(t => t.status === 'Đã xong').length;
        const totalRecent = recentTasks.length || 1; // avoid / 0

        // Admin announcements (NoticeBoard equivalent, take the first 1 or 2 as Hero)
        const latestNotice = safeNotices.length > 0 ? safeNotices[0] : null;
        const todayLabel = todayObj.toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit' });
        const currentRoleLabel = userRole === 'staff' ? 'Quản lý team' : 'Thành viên HopeTeam';

        return (
            <div className="space-y-6 pb-10 safe-mode-dashboard text-slate-200 animate-in fade-in duration-500">
                {/* NOTICE POPUP */}
                {isNoticePopupOpen && safeNotices.length > 0 && createPortal((
                    <div className="notice-popup-overlay fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
                        <div className="member-notice-modal">
                            <div className="member-notice-modal-header">
                                <div className="member-notice-modal-icon"><Megaphone size={19} /></div>
                                <div><span>HopeTeam cập nhật</span><h2>Bảng thông báo</h2></div>
                                <button onClick={() => setIsNoticePopupOpen(false)} aria-label="Đóng bảng thông báo"><X size={19} /></button>
                            </div>
                            <div className="member-notice-modal-list">
                                {safeNotices.map(n => (
                                    <article key={n.id} className={`member-notice-modal-item ${n.priority === 'urgent' ? 'is-urgent' : ''}`}>
                                        <div className="member-notice-modal-meta">
                                            <span>{n.priority === 'urgent' ? 'Quan trọng' : 'Thông báo'}</span>
                                            <time>{n.createdAt?.toDate ? `${timeAgo(n.createdAt.toDate())} • ${new Date(n.createdAt.toDate()).toLocaleDateString('vi-VN')}` : 'Gần đây'}</time>
                                        </div>
                                        <p>{n.content}</p>
                                    </article>
                                ))}
                            </div>
                            <div className="member-notice-modal-footer"><button onClick={() => setIsNoticePopupOpen(false)}>Đã đọc tất cả</button></div>
                        </div>
                    </div>
                ), document.body)}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* LEFT COLUMN: 2/3 */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* HERO: ADMIN ANNOUNCEMENT */}
                        <section className="member-welcome-panel">
                            <div className="member-welcome-profile">
                                <div className="member-welcome-avatar">
                                    {currentUserData.avatar ? <img src={currentUserData.avatar} alt={currentUserData.name} /> : <span>{currentUserData.name?.slice(0, 2).toUpperCase()}</span>}
                                </div>
                                <div className="member-welcome-copy">
                                    <div className="member-welcome-date">{todayLabel}</div>
                                    <div className="member-welcome-name-line">
                                        <h1>{getGreeting()}, <strong>{currentUserData.name}</strong>!</h1>
                                        <RoleBadge role={userRole || currentUserData.accessRole || 'member'} />
                                    </div>
                                    <p>{currentRoleLabel} <span>•</span> {pendingTasks.length > 0 ? `${pendingTasks.length} task đang chờ bạn xử lý` : 'Hôm nay không có task tồn đọng'}</p>
                                </div>
                            </div>

                            {latestNotice ? (
                                <article className={`member-latest-notice ${latestNotice.priority === 'urgent' ? 'is-urgent' : ''}`}>
                                    <div className="member-latest-notice-icon"><Megaphone size={18} /></div>
                                    <div className="member-latest-notice-content">
                                        <div className="member-latest-notice-meta">
                                            <span>{latestNotice.priority === 'urgent' ? 'Quan trọng' : 'Thông báo mới'}</span>
                                            <time>{latestNotice.createdAt?.toDate ? timeAgo(latestNotice.createdAt.toDate()) : 'Gần đây'}</time>
                                        </div>
                                        <p>{latestNotice.content}</p>
                                    </div>
                                    <button onClick={() => setIsNoticePopupOpen(true)}>
                                        <span>{safeNotices.length > 1 ? `Xem ${safeNotices.length} thông báo` : 'Xem chi tiết'}</span><ArrowRight size={15} />
                                    </button>
                                </article>
                            ) : (
                                <div className="member-latest-notice is-empty">
                                    <div className="member-latest-notice-icon"><CheckCircle2 size={18} /></div>
                                    <div className="member-latest-notice-content"><div className="member-latest-notice-meta"><span>Đã cập nhật</span></div><p>Không có thông báo mới. Chúc bạn một ngày làm việc hiệu quả!</p></div>
                                </div>
                            )}
                        </section>

                        {/* COUNTDOWNS (3 CARDS) */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg flex flex-col hover:border-blue-500/30 transition-colors">
                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1">Hết Tháng</p>
                                <div className="flex items-baseline gap-1 mt-auto">
                                    <span className="text-4xl font-black text-white">{daysUntilEndOfMonth}</span>
                                    <span className="text-sm font-bold text-slate-400">Ngày</span>
                                </div>
                                <div className="w-full h-1.5 bg-slate-800 rounded-full mt-4 overflow-hidden">
                                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(currentDay / daysInMonth) * 100}%` }}></div>
                                </div>
                            </div>
                            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg flex flex-col hover:border-emerald-500/30 transition-colors">
                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1">Nhận Lương</p>
                                <div className="flex items-baseline gap-1 mt-auto">
                                    <span className="text-4xl font-black text-emerald-400">{daysUntilSalary > 0 ? daysUntilSalary : 0}</span>
                                    <span className="text-sm font-bold text-slate-400">Ngày</span>
                                </div>
                                <div className="w-full h-1.5 bg-slate-800 rounded-full mt-4 overflow-hidden">
                                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${daysUntilSalary > 0 ? ((25 - daysUntilSalary) / 25) * 100 : 100}%` }}></div>
                                </div>
                            </div>
                            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg flex flex-col hover:border-amber-500/30 transition-colors">
                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1">Chốt Tháng</p>
                                <div className="flex items-baseline gap-1 mt-auto">
                                    <span className="text-4xl font-black text-amber-500">03</span>
                                    <span className="text-sm font-bold text-slate-400">Ngày</span>
                                </div>
                                <div className="w-full h-1.5 bg-slate-800 rounded-full mt-4 overflow-hidden">
                                    <div className="h-full bg-amber-500 rounded-full" style={{ width: '100%' }}></div>
                                </div>
                            </div>
                        </div>

                        {/* TASKS TO PROCESS */}
                        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-lg">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-white">Nhiệm Vụ Cần Xử Lý</h3>
                                <button onClick={() => onNavigate('tasks')} className="text-[13px] font-bold text-blue-400 hover:text-blue-300">View All</button>
                            </div>
                            <div className="space-y-4">
                                {urgentTasks.length > 0 ? urgentTasks.slice(0, 4).map(task => (
                                    <div key={task.id} className="flex items-center gap-4 py-2 group cursor-pointer border-b border-slate-800/50 last:border-0 pb-4 last:pb-2" onClick={() => onNavigate('tasks')}>
                                        <div className="bg-blue-500/10 w-12 h-12 flex items-center justify-center rounded-xl text-blue-400 shrink-0 group-hover:scale-105 transition-transform border border-blue-500/20">
                                            <FileText size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-white text-sm truncate group-hover:text-blue-300 transition-colors">{task.bookTitle}</h4>
                                            <p className="text-xs text-slate-500 mt-1 truncate">{task.channel} • <span className="text-slate-400">Hạn: {task.date}</span></p>
                                        </div>
                                        <div className="px-3 py-1 bg-amber-500/10 text-amber-500 rounded-full text-[10px] font-bold uppercase shrink-0 border border-amber-500/20">
                                            IN PROGRESS
                                        </div>
                                    </div>
                                )) : <div className="text-center py-6 text-slate-500 text-sm bg-slate-800/20 rounded-2xl border border-dashed border-slate-700">Tuyệt vời, bạn không có task nào đang tồn đọng.</div>}
                            </div>
                        </div>

                        {/* QUICK RESOURCES (WEBSITES) */}
                        <div className="pt-2">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Globe size={18} /> Trang Web Nổi Bật</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {introducedWebsites.slice(0, 4).map((site, idx) => (
                                    <a key={idx} href={site.url} target="_blank" rel="noreferrer" className="bg-slate-900 border border-slate-800 rounded-2xl p-6 hover:border-slate-600 transition-all flex flex-col items-center justify-center text-center cursor-pointer group">
                                        <div className="w-10 h-10 flex items-center justify-center mb-3">
                                            {site.logoUrl ? <img src={site.logoUrl} alt={site.title} className="w-8 h-8 object-contain grayscale group-hover:grayscale-0 transition-all opacity-80 group-hover:opacity-100" /> : <Globe size={28} className="text-slate-500 group-hover:text-white transition-colors" />}
                                        </div>
                                        <span className="text-[13px] font-bold text-slate-400 group-hover:text-white line-clamp-1">{site.title || "Website"}</span>
                                    </a>
                                ))}
                            </div>
                        </div>

                    </div>

                    {/* RIGHT COLUMN: 1/3 */}
                    <div className="lg:col-span-1 space-y-6">

                        {/* TOP REVENUE */}
                        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-lg">
                            <div className="mb-6 flex flex-col items-center text-center">
                                <div className="w-12 h-12 bg-yellow-500/10 text-yellow-500 rounded-full flex items-center justify-center mb-3">
                                    <Trophy size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-white">Top Chiến Thần</h3>
                                <p className="text-xs text-slate-500 mt-1">Doanh thu Tháng {parseInt(previousMonth.split('-')[1])}</p>
                            </div>

                            <div className="space-y-4">
                                {topStaff.map((s, idx) => {
                                    // Calc MoM for each staff in top
                                    const sCurrentRev = sourceChannelsForTop.filter(c => c.manager === s.name).reduce((acc, ch) => acc + getUniqueRevenue(ch.id, previousMonth, sourceRevenuesForTop), 0);
                                    const sPastRev = sourceChannelsForTop.filter(c => c.manager === s.name).reduce((acc, ch) => acc + getUniqueRevenue(ch.id, twoMonthsAgo, sourceRevenuesForTop), 0);
                                    let sGrowth = 0;
                                    if (sPastRev > 0) sGrowth = ((sCurrentRev - sPastRev) / sPastRev) * 100;
                                    else if (sCurrentRev > 0) sGrowth = 100;

                                    return (
                                        <div key={idx} className="flex items-center gap-3 py-2 border-b border-slate-800/50 last:border-0">
                                            <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${idx === 0 ? 'bg-blue-600 text-white shadow-lg' : idx === 1 ? 'bg-slate-700 text-slate-200' : idx === 2 ? 'bg-slate-800 text-slate-400' : 'bg-transparent text-slate-500 text-xs'}`}>
                                                {idx + 1}
                                            </div>
                                            <div className="flex-1 min-w-0 pr-2">
                                                <div className="flex items-center gap-2 min-w-0"><h4 className="font-bold text-sm text-white truncate">{s.name}</h4><RoleBadge role={s.accessRole || 'member'} /></div>
                                                <p className="text-[10px] text-slate-500 truncate">{s.role || 'Staff'}</p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-bold text-sm text-white">{formatUSD(s.revenue)}</div>
                                                <div className={`text-[10px] font-bold flex items-center justify-end gap-0.5 mt-0.5 ${sGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {sGrowth >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                                                    {Math.abs(sGrowth).toFixed(1)}%
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <button className="w-full mt-6 py-3 text-xs font-bold text-slate-400 hover:text-white transition-colors bg-slate-800/50 hover:bg-slate-800 rounded-xl">Bảng Xếp Hạng Đầy Đủ</button>
                        </div>

                        {/* WEEKLY SUMMARY */}
                        <div className="work-summary-card rounded-3xl p-6 md:p-8 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                            <h3 className="text-xl font-bold text-white mb-8 relative z-10">Tóm Tắt Công Việc</h3>

                            <div className="space-y-8 relative z-10">
                                <div>
                                    <div className="flex justify-between items-end mb-3">
                                        <span className="text-slate-400 text-sm">Task xong (7 ngày)</span>
                                        <span className="text-white font-bold text-lg">{completedRecent}/{totalRecent}</span>
                                    </div>
                                    <div className="w-full h-2.5 bg-black/20 rounded-full overflow-hidden">
                                        <div className="h-full bg-white rounded-full transition-all duration-1000" style={{ width: `${(completedRecent / totalRecent) * 100}%` }}></div>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex justify-between items-end mb-3">
                                        <span className="text-slate-400 text-sm">Doanh Thu (Tháng {parseInt(previousMonth.split('-')[1])})</span>
                                        <span className="text-white font-bold text-lg">{formatUSD(currentRev)}</span>
                                    </div>
                                    <div className="w-full h-2.5 bg-black/20 rounded-full overflow-hidden">
                                        <div className="h-full bg-white rounded-full" style={{ width: '100%' }}></div>
                                    </div>
                                    <div className="mt-3 flex justify-between items-center text-xs">
                                        <span className="text-white/60">So với tháng trước</span>
                                        <span className={`flex items-center gap-1 font-bold bg-white/10 px-2 py-1 rounded-md ${growth >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                                            {growth >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                            {Math.abs(growth).toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <button onClick={() => onNavigate('tasks')} className="w-full mt-8 py-3.5 bg-white hover:bg-slate-100 text-blue-600 rounded-xl font-bold transition-colors shadow-lg shadow-black/10 relative z-10">
                                Xem Phân Tích
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    // --- ADMIN DASHBOARD (ORIGINAL LAYOUT) ---
    return (
        <div className="space-y-6 pb-10 safe-mode-dashboard text-slate-200">
            {/* NOTICE POPUP */}
            {isNoticePopupOpen && safeNotices.length > 0 && createPortal((
                <div className="notice-popup-overlay fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-teal-500/30 rounded-2xl max-w-lg w-full p-6 shadow-2xl relative ring-1 ring-teal-500/20">
                        <button onClick={() => setIsNoticePopupOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={20} /></button>
                        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><Megaphone className="text-teal-400 animate-pulse" /> Thông Báo Mới</h2>
                        <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
                            {safeNotices.map(n => (
                                <div key={n.id} className="p-4 rounded-xl bg-slate-800/80 border border-slate-700 hover:border-teal-500/30 transition-colors">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${n.priority === 'urgent' ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' : 'bg-blue-500/20 text-blue-400'}`}>{n.priority === 'urgent' ? 'QUAN TRỌNG' : 'TIN TỨC'}</span>
                                        <span className="text-[10px] text-slate-500 font-mono">{n.createdAt?.toDate ? new Date(n.createdAt.toDate()).toLocaleDateString() : ''}</span>
                                    </div>
                                    <p className="text-sm text-slate-200 mt-2 leading-relaxed">{n.content}</p>
                                </div>
                            ))}
                        </div>
                        <Button onClick={() => setIsNoticePopupOpen(false)} className="w-full mt-6 bg-teal-600 hover:bg-teal-500 border-none shadow-lg shadow-teal-500/20">Đã đọc</Button>
                    </div>
                </div>
            ), document.body)}

            {/* UNIFIED GRID LAYOUT (2:1 Column Split) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* --- LEFT MAIN COLUMN --- */}
                <div className="lg:col-span-2 space-y-6">
                    {/* RED BANNER FOR COPYRIGHT STRIKES */}
                    {activeStrikes.length > 0 && (
                        <div className="bg-red-500/20 border-2 border-red-500 rounded-3xl p-6 flex flex-col md:flex-row items-start md:items-center gap-4 cursor-pointer hover:bg-red-500/30 transition-all shadow-[0_0_40px_-5px_rgba(239,68,68,0.3)] animate-in slide-in-from-top-4" onClick={() => onNavigate('channel-health')}>
                            <div className="p-4 bg-red-500 rounded-2xl animate-pulse text-white shrink-0">
                                <ShieldAlert size={32} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-red-400 font-bold text-xl mb-1 drop-shadow-md">ĐANG THEO DÕI BẢN QUYỀN ({activeStrikes.length} gậy hiệu lực)</h3>
                                <p className="text-red-200 text-sm">
                                    {activeStrikeChannelCount} kênh đang được theo dõi.
                                    {nearestStrikeDays !== null
                                        ? ` Gậy gần nhất tự hết sau ${nearestStrikeDays} ngày (${formatDateKeyVi(nearestStrikeExpiry)}).`
                                        : ' Mở chi tiết để kiểm tra dữ liệu và tiến độ Copyright School.'}
                                    {' '}Khi hết 90 ngày, hệ thống tự cập nhật và báo Zalo.
                                </p>
                            </div>
                            <button className="whitespace-nowrap px-6 py-3 bg-red-500 hover:bg-red-400 text-white font-bold rounded-xl transition shadow-lg shadow-red-500/30 flex items-center gap-2">Xem đếm ngược <ChevronRight size={18} /></button>
                        </div>
                    )}

                    {/* GREETING & HERO CARD */}
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-indigo-950/20 to-slate-900 border border-slate-700/50 p-8 shadow-2xl group">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-indigo-500/20 transition-all duration-700"></div>
                        <div className="relative z-10">
                            <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">{getGreeting()}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">{userRole === 'admin' ? 'BOSS' : (currentUserData ? currentUserData.name : 'Team')}</span> 👋</h1>
                            <p className="text-slate-400 text-sm mb-6">Chào mừng trở lại trung tâm chỉ huy.</p>

                            {/* DEADLINES WIDGET */}
                            <div className="flex flex-wrap gap-4">
                                <div className={`px-4 py-3 rounded-2xl border backdrop-blur-md flex items-center gap-3 ${daysUntilSalary <= 5 ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-slate-800/50 border-slate-700 text-slate-300'}`}>
                                    <div className="p-2 bg-slate-950/50 rounded-lg"><DollarSign size={18} /></div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold tracking-widest opacity-70">Lương (25th)</p>
                                        <p className="text-lg font-bold">{daysUntilSalary > 0 ? `Còn ${daysUntilSalary} ngày` : daysUntilSalary === 0 ? "HÔM NAY" : "Đã qua"}</p>
                                    </div>
                                </div>
                                <div className={`px-4 py-3 rounded-2xl border backdrop-blur-md flex items-center gap-3 ${daysUntilEndOfMonth <= 3 ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-800/50 border-slate-700 text-slate-300'}`}>
                                    <div className="p-2 bg-slate-950/50 rounded-lg"><Calendar size={18} /></div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold tracking-widest opacity-70">Hết tháng</p>
                                        <p className="text-lg font-bold">Còn {daysUntilEndOfMonth} ngày</p>
                                    </div>
                                </div>
                                <div className="px-4 py-3 rounded-2xl border bg-slate-800/50 border-slate-700 text-emerald-400 backdrop-blur-md flex items-center gap-3">
                                    <div className="p-2 bg-slate-950/50 rounded-lg"><BarChart3 size={18} /></div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold tracking-widest opacity-70">Chốt Doanh Thu</p>
                                        <p className="text-lg font-bold">Ngày 3</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ADMIN TEAM OPERATIONS */}
                    {userRole === 'admin' && <section className="team-operations-panel">
                        <div className="team-operations-header">
                            <div><p className="team-operations-eyebrow">TEAM PULSE</p><h2>Nhân sự đang làm gì?</h2><p>Theo dõi khối lượng công việc và điểm cần hỗ trợ trong một màn hình.</p></div>
                            <button onClick={() => onNavigate('staff')} className="team-operations-manage"><Users size={16} /> Quản lý nhân sự</button>
                        </div>
                        <div className="team-operations-summary">
                            <div><span>Cần hỗ trợ</span><strong>{adminTeamOverview.filter(person => ['danger', 'warning'].includes(person.attention.tone)).length}</strong></div>
                            <div><span>Task trễ</span><strong>{adminTeamOverview.reduce((sum, person) => sum + person.overdue.length, 0)}</strong></div>
                            <div><span>Đến hạn hôm nay</span><strong>{adminTeamOverview.reduce((sum, person) => sum + person.dueToday.length, 0)}</strong></div>
                            <div><span>Sẵn sàng nhận việc</span><strong>{adminTeamOverview.filter(person => person.attention.tone === 'ready').length}</strong></div>
                        </div>
                        <div className="team-operations-grid">
                            {adminTeamOverview.map(person => (
                                <article key={person.id || person.name} className="team-member-ops-card">
                                    <div className="team-member-ops-profile">
                                        <div className="team-member-ops-avatar">{person.avatar ? <img src={person.avatar} alt={person.name} /> : person.name?.slice(0, 2).toUpperCase()}</div>
                                        <div className="min-w-0"><div className="flex items-center gap-2 min-w-0"><h3 className="truncate">{person.name}</h3><RoleBadge role={person.accessRole || 'member'} /></div><p>{person.role || 'HopeTeam Member'} · {person.personChannels.length} kênh</p></div>
                                        <span className={`team-attention team-attention-${person.attention.tone}`}>{person.attention.label}</span>
                                    </div>
                                    <div className="team-member-ops-metrics">
                                        <div><span>Đang xử lý</span><strong>{person.personPending.length}</strong></div><div><span>Hôm nay</span><strong>{person.dueToday.length}</strong></div><div><span>Doanh thu T{parseInt(previousMonth.split('-')[1])}</span><strong>{formatUSD(person.revenue)}</strong></div>
                                    </div>
                                    <div className="team-member-current-work">
                                        <span>Ưu tiên hiện tại</span>
                                        {person.personPending.length ? person.personPending.slice(0, 2).map(task => <button key={task.id} onClick={() => onNavigate('tasks', person.name)}><span className={`team-task-dot ${task.date && task.date < todayStr ? 'late' : ''}`} /><span className="truncate">{task.bookTitle}</span><small>{task.date || 'Chưa có hạn'}</small></button>) : <p>Không có task tồn đọng.</p>}
                                    </div>
                                    <button onClick={() => onNavigate('tasks', person.name)} className="team-member-ops-action">Xem task của {person.name} <ArrowRight size={14} /></button>
                                </article>
                            ))}
                        </div>
                    </section>}

                    {/* QUICK STATS */}
                    {userRole !== 'admin' && <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                            { label: "Hôm nay", val: todayTasks.length, icon: Calendar, color: "text-blue-400", bg: "bg-blue-400/10" },
                            { label: "Cần xử lý", val: pendingTasks.length, icon: ListTodo, color: "text-amber-400", bg: "bg-amber-400/10" },
                            { label: "Đã xong", val: completedTasks.length, icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-400/10" },
                            { label: "Tổng Kênh", val: safeChannels.length, icon: Youtube, color: "text-red-400", bg: "bg-red-400/10" }
                        ].map((stat, i) => (
                            <div key={i} className="bg-slate-900 border border-slate-800 hover:border-slate-600 p-5 rounded-2xl flex flex-col justify-between transition-all hover:-translate-y-1 cursor-pointer group" onClick={() => onNavigate('tasks')}>
                                <div className={`p-3 rounded-xl w-fit mb-3 ${stat.bg} ${stat.color} group-hover:scale-110 transition-transform`}><stat.icon size={20} /></div>
                                <div><p className="text-3xl font-bold text-white mb-1">{stat.val}</p><p className="text-xs text-slate-500 font-bold uppercase tracking-wider">{stat.label}</p></div>
                            </div>
                        ))}
                    </div>}

                    {/* NOTICE BOARD */}
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 min-h-[300px] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2"><Megaphone className="text-blue-500" size={18} /> Bảng Thông Báo</h3>
                        </div>
                        {userRole === 'admin' && (
                            <form onSubmit={handlePostNotice} className="flex gap-2 mb-4 bg-slate-800/50 p-2 rounded-xl border border-slate-700/50">
                                <input value={newNotice.content} onChange={e => setNewNotice({ ...newNotice, content: e.target.value })} placeholder="Nhập thông báo mới..." className="flex-1 bg-transparent border-none text-sm text-white px-3 outline-none placeholder:text-slate-600" required />
                                <div className="w-px h-6 bg-slate-700 my-auto"></div>
                                <select value={newNotice.priority} onChange={e => setNewNotice({ ...newNotice, priority: e.target.value })} className="bg-transparent text-slate-300 text-xs font-bold outline-none cursor-pointer"><option value="normal">Thường</option><option value="urgent">Quan trọng</option></select>
                                <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white p-2 rounded-lg transition-colors shadow-lg shadow-blue-500/20"><Send size={16} /></button>
                            </form>
                        )}
                        <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar max-h-[400px]">
                            {safeNotices.length > 0 ? safeNotices.map(n => (
                                <div key={n.id} className="group relative p-4 rounded-2xl bg-slate-800/20 border border-slate-700/50 hover:bg-slate-800/50 transition-all">
                                    <div className="flex items-start gap-4">
                                        <div className={`mt-1 p-2 rounded-lg ${n.priority === 'urgent' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                            {n.priority === 'urgent' ? <AlertOctagon size={18} /> : <Pin size={18} />}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{n.content}</p>
                                            <p className="text-[10px] text-slate-600 mt-2 font-mono flex items-center gap-2">
                                                <span>Admin</span> • <span>{n.createdAt?.toDate ? timeAgo(n.createdAt.toDate()) : 'Vừa xong'}</span>
                                            </p>
                                        </div>
                                        {userRole === 'admin' && <button onClick={() => onDeleteNotice(n.id)} className="absolute top-2 right-2 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>}
                                    </div>
                                </div>
                            )) : <div className="h-full flex flex-col items-center justify-center text-slate-600 space-y-2 opacity-50"><div className="p-4 bg-slate-800 rounded-full"><Megaphone size={24} /></div><p className="text-sm">Chưa có thông báo nào</p></div>}
                        </div>
                    </div>

                    {/* NEW: INTRODUCED WEBSITES SECTION */}
                    {introducedWebsites && introducedWebsites.length > 0 && (
                        <div className="bg-gradient-to-br from-blue-900/40 via-indigo-900/20 to-purple-900/40 border border-blue-500/30 rounded-3xl p-6 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-blue-500/20 transition-all duration-700 pointer-events-none"></div>

                            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2 relative z-10">
                                <Globe className="text-blue-400 animate-pulse" size={24} />
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">Trang Web Nổi Bật</span>
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                                {introducedWebsites.map((site, idx) => (
                                    <div key={idx} className="featured-website-card bg-slate-900/60 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:border-blue-500/50 hover:bg-slate-800/80 transition-all duration-300 group/card shadow-xl shadow-black/20 flex flex-col justify-between min-h-[140px] min-w-0 overflow-hidden">
                                        <div className="flex items-start gap-4 mb-4">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shrink-0 border border-white/5 group-hover/card:scale-110 transition-transform duration-300 shadow-inner overflow-hidden">
                                                {site.logoUrl ? (
                                                    <img src={site.logoUrl} alt={site.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                                                ) : null}
                                                <Globe size={24} className={`text-blue-400 ${site.logoUrl ? 'hidden' : 'block'}`} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="text-lg font-bold text-white leading-tight group-hover/card:text-blue-300 transition-colors line-clamp-2">{site.title}</h4>
                                                <p className="featured-website-url text-xs text-slate-400 mt-1">{site.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</p>
                                            </div>
                                        </div>
                                        <a href={site.url} target="_blank" rel="noreferrer" className="w-full block">
                                            <button className="w-full py-2.5 bg-blue-600/20 hover:bg-blue-500 hover:text-white text-blue-300 rounded-xl text-sm font-bold border border-blue-500/30 hover:border-blue-500 transition-all flex items-center justify-center gap-2 group/btn">
                                                {site.displayText || 'Mở trang web'} <ExternalLink size={14} className="group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5 transition-transform" />
                                            </button>
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* --- RIGHT SIDEBAR COLUMN --- */}
                <div className="space-y-6">
                    {/* STAFF BACKLOG OR YOUR TASKS WIDGET (Interactive) */}
                    {currentUserData ? (
                        <div className="bg-slate-900 border border-blue-500/20 rounded-3xl p-6 relative overflow-hidden group hover:border-blue-500/40 transition-colors shadow-lg shadow-blue-900/10">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><ListTodo className="text-blue-400" size={18} /> Nhiệm Vụ Của Bạn</h3>
                            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                                {urgentTasks.length > 0 ? urgentTasks.slice(0, 5).map(task => (
                                    <div key={task.id} className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:bg-slate-800 cursor-pointer group/item transition-colors" onClick={() => onNavigate('tasks')}>
                                        <h4 className="font-bold text-slate-200 text-sm mb-1 group-hover/item:text-blue-400 line-clamp-1">{task.bookTitle}</h4>
                                        <div className="flex justify-between items-center text-xs mt-2">
                                            <span className="text-slate-500 bg-slate-900 px-2 py-0.5 rounded truncate max-w-[140px] border border-slate-800">{task.channel}</span>
                                            <span className="text-red-400 font-mono font-bold bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20">{task.date}</span>
                                        </div>
                                    </div>
                                )) : <div className="text-center text-slate-500 text-sm py-8"><CheckCircle2 className="mx-auto mb-2 opacity-30 text-emerald-500" size={32} /> Hoàn thành xuất sắc!<br />Bạn không có task nào trễ hạn.</div>}
                                {urgentTasks.length > 5 && (
                                    <button onClick={() => onNavigate('tasks')} className="w-full text-xs font-bold text-blue-400 hover:text-white py-2 mt-2 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg transition-colors">Xem tất cả +{urgentTasks.length - 5} việc...</button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 relative overflow-hidden">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><AlertTriangle className="text-amber-500" size={18} /> Cần Đẩy Nhanh</h3>
                            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                                {staffBacklog.length > 0 ? staffBacklog.slice(0, 4).map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-amber-500/30 transition-colors cursor-pointer group" onClick={() => onNavigate('tasks', item.name)}>
                                        <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-slate-700 bg-slate-800 group-hover:border-amber-500 transition-colors">
                                            {item.avatar ? <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs text-slate-500 font-bold">{item.name.charAt(0)}</div>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 min-w-0"><p className="text-sm font-bold text-slate-200 truncate group-hover:text-amber-400 transition-colors">{item.name}</p><RoleBadge role={safeStaff.find(person => person.name === item.name)?.accessRole || 'member'} /></div>
                                            <p className="text-xs text-slate-500">{item.count} việc chờ</p>
                                        </div>
                                        <div className="px-2 py-1 bg-amber-500/10 text-amber-500 rounded text-xs font-bold group-hover:bg-amber-500 group-hover:text-black transition-colors">{item.count}</div>
                                    </div>
                                )) : <div className="text-center text-slate-500 text-sm py-4">Tuyệt vời! Không có ai bị chậm tiến độ.</div>}
                            </div>
                        </div>
                    )}

                    {/* TOP SALES */}
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 overflow-hidden">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2"><Target className="text-yellow-400" size={18} /> Top Chiến Thần</h3>
                            <span className="text-xs text-slate-500 px-2 py-1 bg-slate-800 rounded">Tháng {parseInt(previousMonth.split('-')[1]) || '--'}</span>
                        </div>
                        <div className="space-y-3">
                            {topStaff.length > 0 ? topStaff.slice(0, 3).map((s, idx) => (
                                <div key={s.id || idx} className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] ${idx === 0 ? 'bg-yellow-400 text-black shadow-lg shadow-yellow-400/20' : 'bg-slate-700 text-slate-400'}`}>{idx + 1}</div>
                                    <div className="flex-1 min-w-0 flex items-center gap-2"><span className="text-sm font-bold text-slate-300 truncate">{s.name}</span><RoleBadge role={s.accessRole || 'member'} /></div>
                                    <div className="text-sm font-bold text-emerald-400">{formatUSD(s.revenue)}</div>
                                </div>
                            )) : <p className="text-center text-slate-500 text-xs py-2">Đang cập nhật...</p>}
                        </div>
                    </div>

                    {/* ORPHAN WARNINGS */}
                    {orphanChannels.length > 0 && (
                        <div className="bg-red-950/20 border border-red-900/50 rounded-3xl p-6 relative overflow-hidden group hover:border-red-500/30 transition-colors">
                            <div className="absolute -right-6 -top-6 text-red-900/20 group-hover:text-red-900/30 transition-colors"><AlertTriangle size={150} /></div>
                            <h3 className="text-lg font-bold text-white mb-2 relative z-10 flex items-center gap-2"><ShieldAlert className="text-red-500" size={20} /> Cảnh Báo</h3>
                            <p className="text-sm text-slate-400 mb-6 relative z-10 leading-relaxed">
                                Hệ thống phát hiện <strong className="text-red-400">{orphanChannels.length} kênh</strong> chưa được phân công quản lý.
                            </p>
                            <button onClick={() => onNavigate('channels')} className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold shadow-lg shadow-red-900/20 transition-all relative z-10">
                                Phân công ngay
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- TASKS VIEW ---
const TaskDownloadSources = ({ task, compact = false }) => {
    const downloads = Array.isArray(task.seriesDownloads) ? task.seriesDownloads.filter(item => item?.sources?.length) : [];
    if (!downloads.length) {
        if (!task.link) return <span className="text-slate-600">-</span>;
        return <div className={compact ? 'flex flex-wrap gap-1' : 'flex flex-wrap justify-center gap-1 text-[10px] max-w-[120px] mx-auto'}>
            {task.link.split('\n').filter(link => link.trim()).map((link, index) => (
                <a key={index} href={link.trim()} target="_blank" rel="noreferrer" className={compact ? 'text-blue-400 p-1.5 bg-blue-500/10 rounded text-[10px] flex items-center gap-1' : 'inline-flex items-center justify-center w-6 h-6 rounded bg-slate-700 text-slate-300 hover:bg-blue-600 hover:text-white transition-colors'} title={link}>
                    {compact && <LinkIcon size={12} />}{index + 1}
                </a>
            ))}
        </div>;
    }
    const sourceCount = downloads.reduce((total, item) => total + item.sources.length, 0);
    return (
        <details className={'task-series-downloads ' + (compact ? 'is-mobile' : '')}>
            <summary><LinkIcon size={12} /> {downloads.length} cuốn · {sourceCount} nguồn</summary>
            <div className="task-series-download-list">
                {downloads.map((item, index) => (
                    <div key={(item.title || 'book') + index}>
                        <strong>{index + 1}. {item.title}</strong>
                        <span>
                            {item.sources.map((source, sourceIndex) => (
                                <a key={(source.url || '') + sourceIndex} href={source.url} target="_blank" rel="noreferrer">
                                    {source.type === 'magnet' ? 'Magnet' : source.label || 'Nguồn tải'}
                                </a>
                            ))}
                        </span>
                    </div>
                ))}
            </div>
        </details>
    );
};

const TasksView = ({ tasks, channels, staff, onAddTask, onDeleteTask, onUpdateTask, canEdit, canCreate = false, canUseAi = false, canUseRepository = false, canReturnTask = false, canDelete, highlightedTaskId, apiKey, unusedBooks, onDeleteUnusedBook, initialFilterAssignee, onClearInitialFilter, onReturnToLibrary, copyrightBooks = [], userRole, currentUserData, onSaveCopyright, onDeleteCopyright, canManageCopyright = false, canDeleteCopyright = false }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCopyrightRegistryOpen, setIsCopyrightRegistryOpen] = useState(false);
    const [copyrightFilter, setCopyrightFilter] = useState('all');
    const [copyrightDetail, setCopyrightDetail] = useState(null);
    const [newTaskCopyrightAck, setNewTaskCopyrightAck] = useState(false);
    const [editTaskCopyrightAck, setEditTaskCopyrightAck] = useState(false);

    useEffect(() => {
        if (initialFilterAssignee) {
            setFilterAssignee(initialFilterAssignee);
            if (onClearInitialFilter) onClearInitialFilter();
        }
    }, [initialFilterAssignee, onClearInitialFilter]);
    const [isAiModalOpen, setIsAiModalOpen] = useState(false); // Modal AI
    const [isRepoModalOpen, setIsRepoModalOpen] = useState(false); // Modal Repo
    const [repoSearch, setRepoSearch] = useState('');
    const [deleteFromRepo, setDeleteFromRepo] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false); // Modal Edit Task
    const [newTask, setNewTask] = useState({ bookTitle: '', author: '', channel: '', assignee: '', date: '', link: '', status: 'Chờ đăng', isUnsafe: false, resultLink: '' });
    const [editTaskData, setEditTaskData] = useState(null); // Data for Edit Modal
    const [filterStatus, setFilterStatus] = useState('pending'); // DEFAULT TAB: CHỜ ĐĂNG
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAssignee, setFilterAssignee] = useState(''); // NEW: Filter by Staff
    const [filterChannel, setFilterChannel] = useState(''); // NEW: Filter by Channel

    const [completeModalTask, setCompleteModalTask] = useState(null);
    const [resultLinkInput, setResultLinkInput] = useState('');

    const legacyCopyrightRecords = useMemo(() => {
        const seen = new Set();
        return tasks.filter(task => task.isUnsafe && !findCopyrightRecord(task.bookTitle, copyrightBooks)).reduce((records, task) => {
            const key = normalizeBookTitle(task.bookTitle);
            if (!key || seen.has(key)) return records;
            seen.add(key);
            records.push({ id: 'legacy:' + key, title: task.bookTitle, normalizedTitle: key, aliases: [], normalizedAliases: [], status: COPYRIGHT_STATUS.COPYRIGHT, reason: 'Đánh dấu bản quyền từ dữ liệu task cũ.', note: 'Admin hoặc Staff nên lưu hồ sơ này vào Kho bản quyền.', revisionKey: 'legacy' });
            return records;
        }, []);
    }, [tasks, copyrightBooks]);
    const allCopyrightRecords = useMemo(() => [...copyrightBooks, ...legacyCopyrightRecords], [copyrightBooks, legacyCopyrightRecords]);
    const getCopyrightInfo = task => getTaskCopyrightInfo(task, allCopyrightRecords);
    const getCopyrightRevision = info => info?.record?.revisionKey || info?.record?.id || '';
    const isCopyrightAcknowledged = (task, info) => info?.status !== COPYRIGHT_STATUS.COPYRIGHT || task?.copyrightAcknowledgedRevision === getCopyrightRevision(info);
    const newTaskCopyrightInfo = getCopyrightInfo(newTask);
    const editTaskCopyrightInfo = editTaskData ? getCopyrightInfo(editTaskData) : null;


    // PAGINATION
    const [currentPage, setCurrentPage] = useState(1);

    // State cho AI Input
    const [aiInputList, setAiInputList] = useState('');
    const [isAiProcessing, setIsAiProcessing] = useState(false);

    // Ref để scroll
    const taskRefs = useRef({});

    // Effect scroll khi nhận được highlightedTaskId
    useEffect(() => {
        if (highlightedTaskId && taskRefs.current[highlightedTaskId]) {
            taskRefs.current[highlightedTaskId].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [highlightedTaskId]);

    // Reset page when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filterStatus, searchTerm, filterAssignee, filterChannel, copyrightFilter]);

    const filteredTasks = tasks.filter(task => {
        const matchesSearch = !searchTerm.trim() || normalizeBookTitle(`${task.bookTitle || ''} ${task.author || ''}`).includes(normalizeBookTitle(searchTerm));
        const matchesStatus = filterStatus === 'all' ? true : filterStatus === 'done' ? task.status === 'Đã xong' : task.status === 'Chờ đăng';
        const matchesAssignee = filterAssignee ? task.assignee === filterAssignee : true;
        const matchesChannel = filterChannel ? task.channel === filterChannel : true;
        const matchesCopyright = copyrightFilter === 'all' || getCopyrightInfo(task).status === copyrightFilter;
        return matchesSearch && matchesStatus && matchesAssignee && matchesChannel && matchesCopyright;
    });

    // Calculate Pagination Props
    const totalPages = Math.ceil(filteredTasks.length / ITEMS_PER_PAGE);
    const displayedTasks = filteredTasks.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    // --- FILTER CHANNELS HELPER ---
    const getFilteredChannels = (assigneeName) => {
        if (!assigneeName) return channels;
        return channels.filter(c => c.manager === assigneeName);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newTaskCopyrightInfo.status === COPYRIGHT_STATUS.COPYRIGHT && !newTaskCopyrightAck) return alert('Hãy đọc và xác nhận cảnh báo bản quyền trước khi tạo task.');
        const payload = newTaskCopyrightInfo.status === COPYRIGHT_STATUS.COPYRIGHT ? { ...newTask, copyrightAcknowledgedRevision: getCopyrightRevision(newTaskCopyrightInfo), copyrightAcknowledgedAt: new Date().toISOString(), copyrightAcknowledgedBy: currentUserData?.name || userRole } : newTask;
        await onAddTask(payload);
        setIsModalOpen(false);
        setNewTaskCopyrightAck(false);
        setNewTask({ bookTitle: '', author: '', channel: '', assignee: '', date: '', link: '', status: 'Chờ đăng', isUnsafe: false, resultLink: '' });
    };

    const handleEditClick = (task) => {
        const info = getCopyrightInfo(task);
        setEditTaskCopyrightAck(isCopyrightAcknowledged(task, info));
        setEditTaskData(task);
        setIsEditModalOpen(true);
    };

    const handleUpdateSubmit = async () => {
        if (!editTaskData || !editTaskData.bookTitle) return alert("Vui lòng nhập tên sách!");
        if (editTaskCopyrightInfo?.status === COPYRIGHT_STATUS.COPYRIGHT && !editTaskCopyrightAck) return alert('Hãy đọc và xác nhận cảnh báo bản quyền trước khi lưu.');
        const payload = editTaskCopyrightInfo?.status === COPYRIGHT_STATUS.COPYRIGHT ? { ...editTaskData, copyrightAcknowledgedRevision: getCopyrightRevision(editTaskCopyrightInfo), copyrightAcknowledgedAt: editTaskData.copyrightAcknowledgedAt || new Date().toISOString(), copyrightAcknowledgedBy: editTaskData.copyrightAcknowledgedBy || currentUserData?.name || userRole } : editTaskData;
        await onUpdateTask(editTaskData.id, payload, editTaskData.bookTitle);
        setIsEditModalOpen(false);
        setEditTaskData(null);
    };

    const handleTaskStatusClick = task => {
        if (task.status !== 'Chờ đăng') { onUpdateTask(task.id, { status: 'Chờ đăng' }, task.bookTitle); return; }
        const info = getCopyrightInfo(task);
        if (info.status === COPYRIGHT_STATUS.COPYRIGHT && !isCopyrightAcknowledged(task, info)) { setCopyrightDetail({ info, task, continueCompletion: true }); return; }
        setResultLinkInput(task.resultLink || '');
        setCompleteModalTask(task);
    };

    const acknowledgeCopyright = async () => {
        if (!copyrightDetail?.task) return setCopyrightDetail(null);
        const { task, info, continueCompletion } = copyrightDetail;
        await onUpdateTask(task.id, { copyrightAcknowledgedRevision: getCopyrightRevision(info), copyrightAcknowledgedAt: new Date().toISOString(), copyrightAcknowledgedBy: currentUserData?.name || userRole }, task.bookTitle);
        setCopyrightDetail(null);
        if (continueCompletion) { setResultLinkInput(task.resultLink || ''); setCompleteModalTask(task); }
    };
    const handleCopyrightBadgeClick = async task => {
        const info = getCopyrightInfo(task);
        if (info.record) { setCopyrightDetail({ info, task }); return; }
        if (!canManageCopyright) { setIsCopyrightRegistryOpen(true); return; }
        if (!window.confirm(`Đánh dấu "${task.bookTitle}" là cuốn có bản quyền? Mọi task cùng tên hoặc cùng series sẽ nhận cảnh báo.`)) return;
        try {
            await onSaveCopyright(null, {
                title: task.bookTitle.trim(), author: (task.author || '').trim(), normalizedTitle: normalizeBookTitle(task.bookTitle),
                aliases: [], normalizedAliases: [], status: COPYRIGHT_STATUS.COPYRIGHT,
                reason: 'Đánh dấu nhanh từ Quản lý Task', detectedAt: new Date().toISOString().slice(0, 10),
                evidenceUrl: '', note: 'Được đánh dấu trực tiếp tại cột Bản quyền của task.',
            });
        } catch (error) { alert(error?.message || 'Không thể đánh dấu bản quyền.'); }
    };
    const confirmCompletion = () => {
        if (!resultLinkInput) return alert("Vui lòng nhập link kết quả!");
        // Truyền title để ghi log chi tiết
        onUpdateTask(completeModalTask.id, { status: 'Đã xong', resultLink: resultLinkInput }, completeModalTask.bookTitle);
        setCompleteModalTask(null);
        setResultLinkInput('');
    };

    // --- AI AUTO ASSIGN LOGIC ---
    const handleAiAutoAssign = async () => {
        if (!aiInputList.trim()) return alert("Vui lòng nhập danh sách sách!");
        if (!apiKey) return alert("Chưa cấu hình Gemini API Key trong phần Cài đặt!");

        setIsAiProcessing(true);
        try {
            // Chuẩn bị dữ liệu cho AI context
            const channelInfo = channels.map(c => `${c.name} [Hashtags: ${c.genre || 'General'}] (Quản lý: ${c.manager || 'Random'})`).join('; ');
            const staffNames = staff.map(s => s.name).join(', ');

            // --- NEW: Get existing schedule ---
            const existingTasks = tasks.map(t => `[${t.date}] ${t.channel}`).join('; ');

            const prompt = `
            Tôi có danh sách các cuốn sách và chủ đề của chúng (định dạng: "Tên sách - Chủ đề"):
            ${aiInputList}

            Danh sách kênh hiện có và hashtag chủ đề của kênh: ${channelInfo}
            Danh sách nhân viên: ${staffNames}

            Lịch trình ĐANG CÓ (Giữ nguyên, không chỉnh sửa, chỉ dùng để tính toán slot trống):
            ${existingTasks}

            Hãy đóng vai trợ lý ảo thông minh, thực hiện các bước sau:
            1. Phân tích chủ đề của từng cuốn sách trong input.
            2. Tìm kênh có [Hashtags] phù hợp nhất với chủ đề cuốn sách đó.
            3. "assignee" (người được giao) phải là người quản lý kênh đó.
            4. Lên lịch đăng bài (RẤT QUAN TRỌNG):
               - Kiểm tra kỹ "Lịch trình ĐANG CÓ" để tránh trùng lặp ngày.
               - Mỗi kênh chỉ đăng tối đa 2 video/tuần (tính cả task cũ và mới).
               - Các video trên cùng một kênh phải cách nhau ít nhất 4 ngày (ví dụ: thứ 2, 6).
               - Bắt đầu từ ngày mai (${new Date(Date.now() + 86400000).toISOString().split('T')[0]}).
               - Nếu tuần này đã đầy slot, hãy tự động đẩy sang tuần kế tiếp.
            
            Trả về kết quả dưới dạng JSON mảng các object. KHÔNG viết thêm lời dẫn. Format:
            [
                { "bookTitle": "Tên sách", "channel": "Tên kênh", "assignee": "Tên NV", "date": "YYYY-MM-DD", "status": "Chờ đăng" }
            ]
          `;

            const responseText = await callGeminiAPI(apiKey, prompt);
            const aiTasks = parseAIJSON(responseText);

            if (Array.isArray(aiTasks)) {
                let skippedCopyright = 0;
                let addedTasks = 0;
                for (const task of aiTasks) {
                    if (getCopyrightInfo(task).status === COPYRIGHT_STATUS.COPYRIGHT) { skippedCopyright++; continue; }
                    await onAddTask({ ...task, link: '', status: 'Chờ đăng', isUnsafe: false, resultLink: '' });
                    addedTasks++;
                }
                alert('Đã thêm ' + addedTasks + ' task từ AI.' + (skippedCopyright ? ' Bỏ qua ' + skippedCopyright + ' cuốn có bản quyền.' : ''));
                setIsAiModalOpen(false);
                setAiInputList('');
            } else {
                alert("AI trả về dữ liệu không đúng định dạng. Vui lòng thử lại.");
            }

        } catch (error) {
            console.error(error);
            alert("Lỗi khi xử lý AI: " + error.message);
        } finally {
            setIsAiProcessing(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div><h2 className="text-2xl font-bold text-white">Quản lý Task</h2><p className="text-slate-400 text-sm">Lên lịch và theo dõi tiến độ đăng bài</p></div>
                <div className="flex gap-2 sm:gap-3 w-full md:w-auto flex-wrap">
                    <button type="button" className="copyright-manager-button flex-1 sm:flex-none" onClick={() => setIsCopyrightRegistryOpen(true)}><ShieldAlert size={15} /> Kho bản quyền</button>
                    {canUseAi && <Button onClick={() => setIsAiModalOpen(true)} variant="ai" icon={Wand2} className="text-xs sm:text-sm flex-1 sm:flex-none">AI Lên lịch</Button>}
                    {canCreate && <Button onClick={() => setIsModalOpen(true)} icon={Plus} className="text-xs sm:text-sm flex-1 sm:flex-none">Thêm Task</Button>}
                </div>
            </div>
            <div className="bg-slate-900/50 p-3 sm:p-4 rounded-xl border border-slate-800 space-y-3">
                <div className="relative app-search">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <input type="text" placeholder="Tìm kiếm theo tên sách..." className="app-search-input w-full pl-9 pr-4 py-2.5 text-white text-sm" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                        <select className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-8 py-2 text-white text-sm outline-none focus:border-blue-500 appearance-none cursor-pointer" value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
                            <option value="">Tất cả NV</option>
                            {staff.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={14} />
                    </div>
                    <div className="relative">
                        <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                        <select className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-8 py-2 text-white text-sm outline-none focus:border-blue-500 appearance-none cursor-pointer" value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
                            <option value="">Tất cả Kênh</option>
                            {channels.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={14} />
                    </div>
                    <div className="relative col-span-2 sm:col-span-1">
                        <ShieldAlert className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                        <select className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-8 py-2 text-white text-sm outline-none focus:border-lime-300 appearance-none cursor-pointer" value={copyrightFilter} onChange={e => setCopyrightFilter(e.target.value)}>
                            <option value="all">Mọi trạng thái bản quyền</option><option value={COPYRIGHT_STATUS.UNCHECKED}>Chưa kiểm tra</option><option value={COPYRIGHT_STATUS.SAFE}>Đã kiểm tra an toàn</option><option value={COPYRIGHT_STATUS.COPYRIGHT}>Có bản quyền</option>
                        </select><ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={14} />
                    </div>
                    <div className="task-status-filter flex bg-slate-800 rounded-lg p-1 col-span-2 sm:col-span-1">
                        {['pending', 'done', 'all'].map(s => <button key={s} onClick={() => setFilterStatus(s)} className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${filterStatus === s ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>{s === 'all' ? 'Tất cả' : s === 'pending' ? 'Chờ đăng' : 'Đã xong'}</button>)}
                    </div>
                </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
                {/* --- MOBILE VIEW (CARDS) --- */}
                <div className="md:hidden divide-y divide-slate-700">
                    {displayedTasks.map((task) => (
                        <div key={task.id} className={`task-mobile-card p-4 space-y-4 ${task.id === highlightedTaskId ? 'bg-yellow-500/10' : ''} ${getCopyrightInfo(task).status === COPYRIGHT_STATUS.COPYRIGHT ? 'task-row-copyright' : ''}`}>
                            <div className="task-mobile-heading">
                                <div className={`task-mobile-kicker ${task.status === 'Đã xong' ? 'is-done' : 'is-pending'}`}>{task.status === 'Đã xong' ? 'NỘI DUNG ĐÃ HOÀN THÀNH' : 'NỘI DUNG CHỜ ĐĂNG'}</div>
                                <h4 className="task-mobile-title">{task.bookTitle}{task.author && <span className="task-author-line">{task.author}</span>}</h4>
                                <CopyrightBadge compact status={getCopyrightInfo(task).status} onClick={() => handleCopyrightBadgeClick(task)} />
                            </div>

                            <div className="task-mobile-meta">
                                <div><span>KÊNH</span><strong><Youtube size={14} /> {task.channel || 'Chưa gán kênh'}</strong></div>
                                <div><span>NHÂN SỰ</span><strong><User size={14} /> {task.assignee || 'Chưa gán nhân sự'}</strong></div>
                            </div>

                            <div className="task-mobile-schedule">
                                <div><span>NGÀY ĐĂNG</span><strong className={`${new Date(task.date) < new Date() && task.status !== 'Đã xong' ? 'text-red-400' : ''}`}>{task.date || 'Chưa đặt ngày'}</strong></div>
                                <button onClick={() => handleTaskStatusClick(task)}><Badge status={task.status} /></button>
                            </div>

                            <div className="task-mobile-links">
                                <TaskDownloadSources task={task} compact />
                                <NasTaskButton task={task} compact blocked={getCopyrightInfo(task).status === COPYRIGHT_STATUS.COPYRIGHT} />
                                {task.resultLink && <a href={task.resultLink} target="_blank" rel="noreferrer" className="text-red-400 px-2.5 py-1.5 bg-red-500/10 rounded flex items-center gap-1.5 text-xs"><PlayCircle size={14} /> Kết quả</a>}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <a href={"/prompt?taskId=" + encodeURIComponent(task.id)} className="task-mobile-action task-prompt-launch"><Sparkles size={14} /> Tạo prompt</a>
                                {canEdit && <button onClick={() => handleEditClick(task)} className="task-mobile-action"><Edit2 size={14} /> Sửa task</button>}
                                {canReturnTask && <button onClick={() => { if (window.confirm(`Bạn có chắc muốn trả sách "${task.bookTitle}" về kho chờ?`)) { onReturnToLibrary({ title: task.bookTitle, link: task.link || '' }); onDeleteTask(task.id, task.bookTitle); } }} className="task-mobile-action" title="Trả về kho"><RotateCcw size={14} /> Trả về kho</button>}
                                {canDelete && <button onClick={() => onDeleteTask(task.id, task.bookTitle)} className="task-mobile-action task-mobile-action-danger col-span-2"><Trash2 size={14} /> Xóa task</button>}
                            </div>
                        </div>

                    ))}
                </div>

                {/* --- DESKTOP VIEW (TABLE) --- */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                                <th className="p-4 font-semibold text-center">Bản quyền</th>
                                <th className="p-4 font-semibold">Tên Sách</th>
                                <th className="p-4 font-semibold">Kênh / Nhân sự</th>
                                <th className="p-4 font-semibold text-center">Nguồn</th>
                                <th className="p-4 font-semibold text-center">Kết quả</th>
                                <th className="p-4 font-semibold">Ngày đăng</th>
                                <th className="p-4 font-semibold">Trạng thái</th>
                                <th className="p-4 font-semibold text-right">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {displayedTasks.map((task) => (
                                <tr
                                    key={task.id}
                                    ref={el => taskRefs.current[task.id] = el}
                                    className={`text-sm transition-all duration-500 ${task.id === highlightedTaskId ? 'bg-yellow-500/20 border-l-4 border-l-yellow-500' : getCopyrightInfo(task).status === COPYRIGHT_STATUS.COPYRIGHT ? 'task-row-copyright border-l-2' : 'hover:bg-slate-800/50 border-l-2 border-l-transparent'}`}
                                >
                                    <td className="p-4 text-center"><CopyrightBadge compact status={getCopyrightInfo(task).status} onClick={() => handleCopyrightBadgeClick(task)} /></td>
                                    <td className="p-4 font-medium text-white">{task.bookTitle}{task.author && <span className="task-author-line">{task.author}</span>}</td>
                                    <td className="p-4"><div className="flex flex-col"><span className="text-white flex items-center gap-1"><Youtube size={12} className="text-red-500" /> {task.channel}</span><span className="text-slate-500 text-xs flex items-center gap-1 mt-0.5"><User size={10} /> {task.assignee}</span></div></td>
                                    <td className="p-4 text-center">
                                        <TaskDownloadSources task={task} />
                                        <NasTaskButton task={task} blocked={getCopyrightInfo(task).status === COPYRIGHT_STATUS.COPYRIGHT} />
                                    </td>
                                    <td className="p-4 text-center">{task.resultLink ? (<a href={task.resultLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-500/10 text-red-400 hover:bg-red-600 hover:text-white transition-colors"><PlayCircle size={16} /></a>) : <span className="text-slate-600">-</span>}</td>
                                    <td className="p-4 text-slate-400 font-mono">{task.date}</td>
                                    <td className="p-4"><button disabled={!canEdit} onClick={() => handleTaskStatusClick(task)} className={`flex items-center gap-2 ${canEdit ? 'hover:opacity-80' : 'cursor-not-allowed opacity-80'}`}><Badge status={task.status} /></button></td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-1">
                                            {canEdit && (
                                                <><a href={"/prompt?taskId=" + encodeURIComponent(task.id)} className="header-prompt-launch task-prompt-launch" title="Tạo prompt từ thông tin sách của task"><Sparkles size={14} /><span>Tạo prompt</span></a>
                                                <Button variant="ghost" onClick={() => handleEditClick(task)} className="p-2 hover:text-blue-400">
                                                    <Edit2 size={16} />
                                                </Button></>
                                            )}
                                            {canReturnTask && (
                                                <Button variant="ghost" onClick={() => { if (window.confirm(`Bạn có chắc muốn trả sách "${task.bookTitle}" về kho chờ?`)) { onReturnToLibrary({ title: task.bookTitle, link: task.link || '' }); onDeleteTask(task.id, task.bookTitle); } }} className="p-2 hover:text-blue-400" title="Trả về kho">
                                                    <RotateCcw size={16} />
                                                </Button>
                                            )}
                                            {canDelete && (
                                                <Button variant="ghost" onClick={() => onDeleteTask(task.id, task.bookTitle)} className="p-2 hover:text-red-400">
                                                    <Trash2 size={16} />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="p-2">
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
            </div>

            {isCopyrightRegistryOpen && createPortal(
                <CopyrightRegistryModal records={allCopyrightRecords} tasks={tasks} canManage={canManageCopyright} canDelete={canDeleteCopyright} onSave={onSaveCopyright} onDelete={onDeleteCopyright} onClose={() => setIsCopyrightRegistryOpen(false)} />,
                document.body
            )}
            {copyrightDetail && createPortal(
                <CopyrightDetailModal info={copyrightDetail.info} task={copyrightDetail.task} canManage={canManageCopyright} onClose={() => setCopyrightDetail(null)} onAcknowledge={copyrightDetail.info.status === COPYRIGHT_STATUS.COPYRIGHT && copyrightDetail.task && !isCopyrightAcknowledged(copyrightDetail.task, copyrightDetail.info) ? acknowledgeCopyright : null} />,
                document.body
            )}
            {/* ADD TASK MODAL */}
            {
                isModalOpen && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                        <Card className="w-full max-w-md bg-slate-900 border-slate-700">
                            <div className="flex justify-between items-center mb-6"><h3 className="text-xl font-bold text-white">Thêm Nhiệm vụ mới</h3><button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white"><X size={20} /></button></div>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-xs font-medium text-slate-400">Tác phẩm (tên sách / series)</label>
                                        {canUseRepository && <button type="button" onClick={() => setIsRepoModalOpen(true)} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"><BookOpen size={12} /> Lấy từ Kho</button>}
                                    </div>
                                    <input required type="text" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={newTask.bookTitle} onChange={e => { setNewTask({ ...newTask, bookTitle: e.target.value }); setNewTaskCopyrightAck(false); }} placeholder="Nhập tên audiobook..." />
                                    <NasTaskHint title={newTask.bookTitle} author={newTask.author} />
                                    {newTask.bookTitle && <div className={"task-copyright-warning is-" + newTaskCopyrightInfo.status}><ShieldAlert size={16} /><span><strong>{newTaskCopyrightInfo.status === COPYRIGHT_STATUS.COPYRIGHT ? 'Phát hiện cuốn có bản quyền.' : newTaskCopyrightInfo.status === COPYRIGHT_STATUS.SAFE ? 'Cuốn này đã được kiểm tra an toàn.' : 'Cuốn này chưa được kiểm tra bản quyền.'}</strong> {newTaskCopyrightInfo.record?.reason || ''}</span>{newTaskCopyrightInfo.record && <button type="button" onClick={() => setCopyrightDetail({ info: newTaskCopyrightInfo })}>Chi tiết</button>}</div>}
                                </div>
                                <div><label className="block text-xs font-medium text-slate-400 mb-1">Tác giả</label><input type="text" list="task-author-options" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={newTask.author || ''} onChange={e => setNewTask({ ...newTask, author: e.target.value })} placeholder="VD: Aaron Renfroe (nhiều tác giả cách nhau dấu phẩy)" /></div>
                                {newTaskCopyrightInfo.status === COPYRIGHT_STATUS.COPYRIGHT && <label className="task-copyright-ack"><input type="checkbox" checked={newTaskCopyrightAck} onChange={e => setNewTaskCopyrightAck(e.target.checked)} /><span>Tôi đã đọc cảnh báo và vẫn muốn tạo task này.</span></label>}
                                <div><label className="block text-xs font-medium text-slate-400 mb-1">Link Nguồn (Nhiều link xuống dòng)</label><textarea rows={3} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" value={newTask.link} onChange={e => setNewTask({ ...newTask, link: e.target.value })} placeholder="Link 1&#10;Link 2..." /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-xs font-medium text-slate-400 mb-1">Nhân sự</label><select required className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={newTask.assignee} onChange={e => setNewTask({ ...newTask, assignee: e.target.value, channel: '' })}><option value="">-- Chọn NV --</option>{staff.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
                                    <div><label className="block text-xs font-medium text-slate-400 mb-1">Kênh</label><select required className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={newTask.channel} onChange={e => setNewTask({ ...newTask, channel: e.target.value })}><option value="">-- Chọn kênh --</option>{getFilteredChannels(newTask.assignee).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
                                </div>
                                <div><label className="block text-xs font-medium text-slate-400 mb-1">Ngày đăng</label><input required type="date" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={newTask.date} onChange={e => setNewTask({ ...newTask, date: e.target.value })} /></div>
                                <div className="pt-4"><Button type="submit" className="w-full">Tạo Task</Button></div>
                            </form>
                        </Card>
                    </div>
                )
            }

            <AuthorOptions id="task-author-options" authors={[...tasks.map(task => task.author), ...copyrightBooks.map(record => record.author)]} />

            {/* REPO SELECTION MODAL */}
            {isRepoModalOpen && unusedBooks && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl flex flex-col max-h-[80vh]">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-white flex items-center gap-2"><BookOpen size={18} /> Kho Sách Chờ</h3>
                            <button onClick={() => setIsRepoModalOpen(false)}><X size={18} className="text-slate-400 hover:text-white" /></button>
                        </div>
                        <input
                            className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white mb-3"
                            placeholder="Tìm sách..."
                            value={repoSearch}
                            onChange={e => setRepoSearch(e.target.value)}
                            autoFocus
                        />
                        <div className="flex-1 overflow-y-auto space-y-2 pr-1 mb-4">
                            {unusedBooks.filter(b => b.title.toLowerCase().includes(repoSearch.toLowerCase())).map(book => (
                                <div key={book.id} className="p-3 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-blue-500/50 rounded-lg cursor-pointer transition-colors group"
                                    onClick={() => {
                                        setNewTask(prev => ({ ...prev, bookTitle: book.title, link: book.link }));
                                        if (deleteFromRepo) onDeleteUnusedBook(book.id, book.title);
                                        setIsRepoModalOpen(false);
                                    }}>
                                    <p className="font-medium text-sm text-white group-hover:text-blue-400">{book.title}</p>
                                    <p className="text-[10px] text-slate-500 truncate">{book.link}</p>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 bg-slate-800 p-2 rounded border border-slate-700">
                            <input type="checkbox" checked={deleteFromRepo} onChange={e => setDeleteFromRepo(e.target.checked)} id="delRepo" className="accent-blue-500 w-4 h-4" />
                            <label htmlFor="delRepo" className="text-xs text-slate-300 cursor-pointer select-none">Tự động xóa khỏi kho sau khi chọn</label>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT TASK MODAL */}
            {
                isEditModalOpen && editTaskData && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                        <Card className="w-full max-w-md bg-slate-900 border-slate-700">
                            <div className="flex justify-between items-center mb-6"><h3 className="text-xl font-bold text-white">Chỉnh sửa Task</h3><button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-white"><X size={20} /></button></div>
                            <div className="space-y-4">
                                <div><label className="block text-xs font-medium text-slate-400 mb-1">Tác phẩm (tên sách / series)</label><input type="text" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={editTaskData.bookTitle} onChange={e => { setEditTaskData({ ...editTaskData, bookTitle: e.target.value }); setEditTaskCopyrightAck(false); }} />{editTaskCopyrightInfo && <div className={"task-copyright-warning is-" + editTaskCopyrightInfo.status}><ShieldAlert size={16} /><span><strong>{editTaskCopyrightInfo.status === COPYRIGHT_STATUS.COPYRIGHT ? 'Có bản quyền.' : editTaskCopyrightInfo.status === COPYRIGHT_STATUS.SAFE ? 'Đã kiểm tra an toàn.' : 'Chưa kiểm tra.'}</strong> {editTaskCopyrightInfo.record?.reason || ''}</span>{editTaskCopyrightInfo.record && <button type="button" onClick={() => setCopyrightDetail({ info: editTaskCopyrightInfo, task: editTaskData })}>Chi tiết</button>}</div>}</div>
                                <div><label className="block text-xs font-medium text-slate-400 mb-1">Tác giả</label><input type="text" list="task-author-options" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={editTaskData.author || ''} onChange={e => setEditTaskData({ ...editTaskData, author: e.target.value })} placeholder="Tên tác giả" /></div>
                                {editTaskCopyrightInfo?.status === COPYRIGHT_STATUS.COPYRIGHT && <label className="task-copyright-ack"><input type="checkbox" checked={editTaskCopyrightAck} onChange={e => setEditTaskCopyrightAck(e.target.checked)} /><span>Tôi đã đọc cảnh báo và vẫn muốn lưu task này.</span></label>}
                                <div><label className="block text-xs font-medium text-slate-400 mb-1">Link Nguồn (Nhiều link xuống dòng)</label><textarea rows={3} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" value={editTaskData.link || ''} onChange={e => setEditTaskData({ ...editTaskData, link: e.target.value })} placeholder="Link 1&#10;Link 2..." /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-xs font-medium text-slate-400 mb-1">Nhân sự</label><select className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={editTaskData.assignee} onChange={e => setEditTaskData({ ...editTaskData, assignee: e.target.value, channel: '' })}>{staff.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
                                    <div><label className="block text-xs font-medium text-slate-400 mb-1">Kênh</label><select className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={editTaskData.channel} onChange={e => setEditTaskData({ ...editTaskData, channel: e.target.value })}>{getFilteredChannels(editTaskData.assignee).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-xs font-medium text-slate-400 mb-1">Ngày đăng</label><input type="date" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={editTaskData.date} onChange={e => setEditTaskData({ ...editTaskData, date: e.target.value })} /></div>
                                    <div><label className="block text-xs font-medium text-slate-400 mb-1">Trạng thái</label><select className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" value={editTaskData.status} onChange={e => setEditTaskData({ ...editTaskData, status: e.target.value })}><option value="Chờ đăng">Chờ đăng</option><option value="Đã xong">Đã xong</option></select></div>
                                </div>
                                <div className="pt-4 flex gap-3">
                                    <Button variant="secondary" onClick={() => setIsEditModalOpen(false)} className="flex-1">Hủy</Button>
                                    <Button onClick={handleUpdateSubmit} className="flex-1">Lưu thay đổi</Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                )
            }

            {/* AI AUTO SCHEDULE MODAL */}
            {
                isAiModalOpen && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                        <Card className="w-full max-w-lg bg-slate-900 border-slate-700">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2"><Sparkles className="text-purple-400" /> AI Lên lịch thông minh</h3>
                                <button onClick={() => setIsAiModalOpen(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
                            </div>
                            <div className="space-y-4">
                                <div className="bg-purple-900/10 border border-purple-500/20 p-3 rounded-lg text-xs text-purple-300">
                                    AI sẽ tự động so khớp <b>Chủ đề sách</b> với <b>Hashtag kênh</b> để phân công chính xác nhất.
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Nhập danh sách: Tên sách - Chủ đề (Mỗi dòng 1 cuốn)</label>
                                    <textarea
                                        className="w-full h-40 bg-slate-800 border border-slate-700 rounded-lg p-3 text-white text-sm focus:border-purple-500 outline-none placeholder:text-slate-600"
                                        placeholder="Ví dụ:&#10;Đắc Nhân Tâm - Phát triển bản thân&#10;Sherlock Holmes - Trinh thám&#10;Mắt Biếc - Tiểu thuyết lãng mạn..."
                                        value={aiInputList}
                                        onChange={(e) => setAiInputList(e.target.value)}
                                    ></textarea>
                                </div>
                                <div className="pt-2">
                                    <Button
                                        variant="ai"
                                        className="w-full"
                                        onClick={handleAiAutoAssign}
                                        disabled={isAiProcessing}
                                        isLoading={isAiProcessing}
                                        icon={Wand2}
                                    >
                                        {isAiProcessing ? "AI đang phân tích & Matching..." : "Phân tích & Lên lịch"}
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                )
            }

            {
                completeModalTask && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                        <div className="w-full max-w-md bg-slate-900 border border-emerald-500/50 rounded-2xl p-6 shadow-2xl">
                            <h3 className="text-xl font-bold text-white mb-4 text-center">Xác nhận hoàn thành</h3>
                            <input autoFocus type="url" className="w-full bg-slate-950 border border-emerald-500/30 rounded-lg px-4 py-3 text-white mb-4" value={resultLinkInput} onChange={e => setResultLinkInput(e.target.value)} placeholder="https://youtube.com/watch?v=..." />
                            <div className="flex gap-3"><button onClick={() => setCompleteModalTask(null)} className="flex-1 py-3 rounded-lg bg-slate-800 text-slate-300">Hủy</button><button onClick={confirmCompletion} className="flex-1 py-3 rounded-lg bg-emerald-600 text-white">Xác nhận</button></div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

// --- RESOURCES VIEW ---
const ResourcesView = ({ viewMode = 'channels', channels, staff, onAddChannel, onDeleteChannel, canEdit, canCreateChannel = false, canEditResources = false, canEditRevenue = false, canMoveToMonetized = false, canDelete, canManageMonetization = false, apiKey, youtubeApiKey, resources, onAddResource, onDeleteResource, onUpdateResource, onRefreshChannel, onSaveRevenue, revenues, exchangeRate }) => {
    // STATE: Channels
    const [newChannel, setNewChannel] = useState({ name: '', link: '', subscribers: '', manager: '', folder: '', genre: '', category: 'nurture', email: '', monetizationStatus: '', monetizationRetryDate: '', monetizationNote: '' });
    const [isAddChannelModalOpen, setIsAddChannelModalOpen] = useState(false);
    const [editingChannelId, setEditingChannelId] = useState(null);
    const [editData, setEditData] = useState({});
    const [editChannelError, setEditChannelError] = useState('');
    const [isSavingChannelEdit, setIsSavingChannelEdit] = useState(false);
    const [quickMoveChannel, setQuickMoveChannel] = useState(null);
    const [quickMoveError, setQuickMoveError] = useState('');
    const [isQuickMoving, setIsQuickMoving] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    // STATE: Revenue Modal
    const [revenueModal, setRevenueModal] = useState({ isOpen: false, channelId: null, amount: '', month: '' });

    // STATE: Resources
    const [newRes, setNewRes] = useState({ type: 'info', title: '', content: '', link: '', download: '', manual: '' });

    const [detailRes, setDetailRes] = useState(null);
    const [editRes, setEditRes] = useState(null);

    // STATE: UI & Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [resPage, setResPage] = useState(1);
    const [activeResTab, setActiveResTab] = useState('all');
    const [activeChannelTab, setActiveChannelTab] = useState('all');
    const [channelPage, setChannelPage] = useState(1);
    const [sortBy, setSortBy] = useState('name');
    const CHANNELS_PER_PAGE = 10;

    // REVENUE: Current Month Calculation for Cards
    const currentMonth = new Date().toISOString().slice(0, 7);
    const previousMonth = new Date();
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    const previousMonthStr = previousMonth.toISOString().slice(0, 7);

    // Helpers for 3-Month View
    const getLast3Months = () => {
        const dates = [];
        for (let i = 1; i <= 3; i++) {
            const d = new Date();
            d.setMonth(d.getMonth() - i);
            dates.push(d.toISOString().slice(0, 7));
        }
        return dates;
    };
    const last3Months = getLast3Months();

    // HANDLER: Save Revenue Modal
    const handleSaveRevenueModal = () => {
        if (!revenueModal.channelId || !revenueModal.amount || !revenueModal.month) return;
        onSaveRevenue({ channelId: revenueModal.channelId, amount: revenueModal.amount, month: revenueModal.month });
        setRevenueModal({ isOpen: false, channelId: null, amount: '', month: '' });
    };

    // HANDLER: Bulk Fetch
    const handleBulkFetch = async () => {
        if (!confirm("Bạn có chắc muốn cập nhật thông tin (Subs, Thumbnail, Name) cho TOÀN BỘ kênh? Quá trình này có thể tốn thời gian và quota API.")) return;
        setIsFetching(true);
        let count = 0;
        for (const ch of channels) {
            if (!ch.link) continue;
            try {
                // Delay 300ms to avoid rate limit
                await new Promise(r => setTimeout(r, 300));
                const data = await fetchYouTubeRealData(ch.link, youtubeApiKey);
                if (data) {
                    await onRefreshChannel(ch.id, { name: data.name, subscribers: data.subscribers, thumbnail: data.thumbnail });
                    count++;
                }
            } catch (e) { console.error("Update failed for", ch.name, e); }
        }
        setIsFetching(false);
        alert(`Đã cập nhật xong ${count}/${channels.length} kênh.`);
    };

    // --- CHANNEL TABLE LOGIC ---
    const displayedChannels = useMemo(() => {
        let list = [...channels];

        // 1. Search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(c => c.name?.toLowerCase().includes(term) || c.manager?.toLowerCase().includes(term) || c.genre?.toLowerCase().includes(term));
        }

        // 2. Tab filter
        if (activeChannelTab !== 'all') {
            list = list.filter(c => (c.category || 'nurture') === activeChannelTab);
        }

        // 3. Sort
        if (sortBy === 'manager') {
            list.sort((a, b) => (a.manager || 'zzz').localeCompare(b.manager || 'zzz'));
        } else if (sortBy === 'subs') {
            const parseSubs = (str) => { if (!str) return 0; const n = parseFloat(str.replace(/[^0-9.]/g, '')); if (str.toLowerCase().includes('m')) return n * 1e6; if (str.toLowerCase().includes('k')) return n * 1e3; return n; };
            list.sort((a, b) => parseSubs(b.subscribers) - parseSubs(a.subscribers));
        } else {
            list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }

        return list;
    }, [channels, searchTerm, activeChannelTab, sortBy]);

    const totalChannelPages = Math.ceil(displayedChannels.length / CHANNELS_PER_PAGE);
    const paginatedChannels = displayedChannels.slice((channelPage - 1) * CHANNELS_PER_PAGE, channelPage * CHANNELS_PER_PAGE);

    // Helper function for local year-month formatting
    const getLocalYearMonth = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    // Summary calculations
    const channelStats = useMemo(() => {
        const activeBkt = channels.filter(c => c.category === 'monetized').length;
        // Basic naive parse of subscribers count for summary
        const parseSubs = (str) => {
            if (!str) return 0;
            const num = parseFloat(str.replace(/[^0-9.]/g, ''));
            if (str.toLowerCase().includes('k')) return num * 1000;
            if (str.toLowerCase().includes('m')) return num * 1000000;
            if (str.toLowerCase().includes('tr')) return num * 1000000;
            return num;
        };
        const totalSubsNum = channels.reduce((sum, c) => sum + parseSubs(c.subscribers), 0);
        let totalSubsStr = totalSubsNum >= 1000000 ? (totalSubsNum / 1000000).toFixed(1) + 'M' : (totalSubsNum >= 1000 ? (totalSubsNum / 1000).toFixed(1) + 'K' : totalSubsNum);

        let estRevenue = 0;
        let pastRevenue = 0;

        const currentMonthDate = new Date();
        currentMonthDate.setDate(0);
        const revMonth1 = getLocalYearMonth(currentMonthDate);
        currentMonthDate.setDate(0);
        const revMonth2 = getLocalYearMonth(currentMonthDate);

        // Calculate revenue for all visible channels (based on role/filter)
        channels.forEach(ch => {
            const r1 = revenues.find(r => r.channelId === ch.id && r.month === revMonth1);
            if (r1) estRevenue += parseFloat(r1.amount || 0);

            const r2 = revenues.find(r => r.channelId === ch.id && r.month === revMonth2);
            if (r2) pastRevenue += parseFloat(r2.amount || 0);
        });

        let revenueGrowth = 0;
        if (pastRevenue > 0) revenueGrowth = ((estRevenue - pastRevenue) / pastRevenue) * 100;
        else if (estRevenue > 0) revenueGrowth = 100;

        // Channels Growth (Mock calculation, since we don't have historical channel count)
        const channelsGrowth = 5;

        return {
            total: channels.length,
            channelsGrowth,
            activeBkt,
            bktGrowth: 2, // Mock
            totalSubs: totalSubsStr,
            subsGrowth: -1, // Mock
            estRevenue,
            revenueGrowth,
            revMonth1,
            revMonth2
        };
    }, [channels, revenues]);

    // --- HANDLERS ---
    const handleAutoFetch = async () => {
        if (!newChannel.link) return;
        setIsFetching(true);
        const data = await fetchYouTubeRealData(newChannel.link, youtubeApiKey);
        if (data) {
            setNewChannel(prev => ({ ...prev, name: data.name, subscribers: data.subscribers, thumbnail: data.thumbnail }));
            alert(`Đã tìm thấy: ${data.name}`);
        }
        setIsFetching(false);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        const channelWithManager = {
            ...newChannel,
            manager: newChannel.manager || (staff.length === 1 ? staff[0].name : '')
        };
        const channelPayload = canManageMonetization
            ? channelWithManager
            : Object.fromEntries(Object.entries(channelWithManager).filter(([key]) => !['monetizationStatus', 'monetizationRetryDate', 'monetizationNote'].includes(key)));
        await onAddChannel(channelPayload);
        setNewChannel({ name: '', link: '', subscribers: '', manager: '', folder: '', genre: '', category: 'nurture', email: '', monetizationStatus: '', monetizationRetryDate: '', monetizationNote: '' });
        setIsAddChannelModalOpen(false);
    };

    const handleEditSave = async () => {
        if (!editingChannelId || isSavingChannelEdit) return;
        setEditChannelError('');
        setIsSavingChannelEdit(true);
        try {
            const editableFields = ['link', 'name', 'manager', 'category', 'genre', 'email', 'folder', 'subscribers', 'thumbnail', 'viewCount', 'videoCount'];
            const updatePayload = {};
            editableFields.forEach(field => {
                if (Object.prototype.hasOwnProperty.call(editData, field) && editData[field] !== undefined) {
                    updatePayload[field] = editData[field];
                }
            });
            if (canManageMonetization) {
                ['monetizationStatus', 'monetizationRetryDate', 'monetizationNote'].forEach(field => {
                    if (Object.prototype.hasOwnProperty.call(editData, field) && editData[field] !== undefined) {
                        updatePayload[field] = editData[field];
                    }
                });
            }
            await onRefreshChannel(editingChannelId, updatePayload);
            setEditingChannelId(null);
            setEditData({});
        } catch (error) {
            console.error('Edit channel error:', error);
            setEditChannelError(error?.code === 'permission-denied'
                ? 'Không thể lưu vì quyền tài khoản chưa khớp với kênh này. Hãy tải lại trang rồi thử lại.'
                : 'Không thể lưu thay đổi. Vui lòng kiểm tra mạng và thử lại.');
        } finally {
            setIsSavingChannelEdit(false);
        }
    };

    const handleQuickMove = async (category) => {
        if (!quickMoveChannel || category === (quickMoveChannel.category || 'nurture')) {
            setQuickMoveChannel(null);
            return;
        }
        setQuickMoveError('');
        setIsQuickMoving(true);
        try {
            const updatePayload = { category };
            if (canManageMonetization && category === 'eligible' && quickMoveChannel.category !== 'eligible') {
                updatePayload.monetizationStatus = '';
                updatePayload.monetizationRetryDate = '';
                updatePayload.monetizationNote = '';
            }
            await onRefreshChannel(quickMoveChannel.id, updatePayload);
            setQuickMoveChannel(null);
        } catch (error) {
            console.error('Quick move channel error:', error);
            setQuickMoveError(error?.code === 'permission-denied'
                ? 'Tài khoản chưa có quyền chuyển kênh này. Hãy tải lại trang; nếu vẫn lỗi, báo admin kiểm tra quyền Firestore.'
                : 'Không thể chuyển danh mục. Vui lòng kiểm tra mạng và thử lại.');
        } finally {
            setIsQuickMoving(false);
        }
    };

    const handleAddRes = async (e) => {
        e.preventDefault();
        await onAddResource(newRes);
        setNewRes({ type: 'info', title: '', content: '', link: '', download: '', manual: '' });
        alert("Đã chia sẻ tài nguyên!");
    };

    const handleUpdateRes = async () => {
        await onUpdateResource(editRes.id, editRes);
        setEditRes(null);
    };

    const handleTogglePin = async (res) => {
        await onUpdateResource(res.id, { isPinned: !res.isPinned });
    };

    // KANBAN CATEGORIES / TABS DEFINITION
    const CHANNEL_TABS = [
        { id: 'all', label: 'All', color: 'border-slate-500/50', bg: 'bg-slate-900/40', accent: 'bg-slate-500', badgeClass: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
        { id: 'nurture', label: 'Kênh nuôi', color: 'border-slate-500/50', bg: 'bg-slate-900/40', accent: 'bg-slate-500', badgeClass: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
        { id: 'eligible', label: 'Đủ điều kiện', color: 'border-blue-500/50', bg: 'bg-blue-900/10', accent: 'bg-blue-500', badgeClass: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
        { id: 'monetized', label: 'Kênh BKT', color: 'border-yellow-500/50', bg: 'bg-yellow-900/10', accent: 'bg-yellow-500', badgeClass: 'bg-yellow-600/20 text-yellow-500 border-yellow-600/30' },
        { id: 'claim', label: 'Kênh Claim', color: 'border-red-500/50', bg: 'bg-red-900/10', accent: 'bg-red-500', badgeClass: 'bg-red-500/10 text-red-500 border-red-500/20' }
    ];

    // KANBAN CATEGORIES DEFINITION
    const KANBAN_CATEGORIES = [
        { id: 'nurture', label: 'Kênh nuôi', color: 'border-slate-500/50', bg: 'bg-slate-900/40', accent: 'bg-slate-500' },
        { id: 'eligible', label: 'Đủ điều kiện', color: 'border-blue-500/50', bg: 'bg-blue-900/10', accent: 'bg-blue-500' },
        { id: 'monetized', label: 'Bật kiếm tiền', color: 'border-yellow-500/50', bg: 'bg-yellow-900/10', accent: 'bg-yellow-500' },
        { id: 'claim', label: 'Kênh Claim', color: 'border-red-500/50', bg: 'bg-red-900/10', accent: 'bg-red-500' }
    ];

    const formatRetryDate = (dateValue) => {
        if (!dateValue) return '';
        const [year, month, day] = String(dateValue).split('-');
        return day && month ? `${day}/${month}${year ? `/${year}` : ''}` : dateValue;
    };

    const parseChannelSubscribers = (value) => {
        if (!value) return 0;
        const normalized = String(value).toLowerCase().replace(/,/g, '.');
        const number = parseFloat(normalized.replace(/[^0-9.]/g, '')) || 0;
        if (normalized.includes('m') || normalized.includes('tr')) return number * 1000000;
        if (normalized.includes('k')) return number * 1000;
        return number;
    };

    const formatRevenuePeriod = (period) => {
        if (!period || !period.includes('-')) return period || '';
        const [year, month] = period.split('-');
        return `T${Number(month)}/${year}`;
    };

    const MonetizationRetryBadge = ({ channel }) => {
        if (channel.category !== 'eligible') return null;
        const status = channel.monetizationStatus === 'applied' ? 'applied' : channel.monetizationStatus === 'failed' ? 'failed' : 'pending';
        return (
            <>
                <span className={`channel-retry-badge channel-retry-${status}`} title={status === 'failed' ? (channel.monetizationRetryDate ? `Bấm lại ngày ${formatRetryDate(channel.monetizationRetryDate)}` : 'Chưa đặt ngày bấm lại') : status === 'applied' ? 'Admin đã bấm apply trên YouTube' : 'Đang chờ admin xử lý'}>
                    {status === 'applied' ? <CheckCircle2 size={11} /> : status === 'failed' ? <AlertTriangle size={11} /> : <Clock size={11} />}
                    <span>{status === 'applied' ? 'Đã bấm' : status === 'failed' ? 'Bật xịt' : 'Chờ admin'}</span>
                    {status === 'failed' && channel.monetizationRetryDate && <><span className="channel-retry-divider">•</span><span>{formatRetryDate(channel.monetizationRetryDate)}</span></>}
                </span>
                {channel.monetizationNote && <span className="channel-monetization-note" title={channel.monetizationNote}><FileText size={10} /> {channel.monetizationNote}</span>}
            </>
        );
    };

    const MonetizationStatusFields = ({ value, onChange }) => {
        if (!canManageMonetization || (value.category || 'nurture') !== 'eligible') return null;
        const normalizedStatus = ['applied', 'failed'].includes(value.monetizationStatus) ? value.monetizationStatus : '';
        return (
            <div className="channel-monetization-fields">
                <div>
                    <label className="channel-field-label">Trạng thái bật kiếm tiền</label>
                    <select
                        value={normalizedStatus}
                        onChange={e => onChange({ ...value, monetizationStatus: e.target.value, monetizationRetryDate: e.target.value === 'failed' ? (value.monetizationRetryDate || '') : '' })}
                        className="channel-field-control"
                    >
                        <option value="" disabled>Chờ xử lý</option>
                        <option value="applied">Đã bấm</option>
                        <option value="failed">Bật xịt</option>
                    </select>
                </div>
                {normalizedStatus === 'failed' && (
                    <div>
                        <label className="channel-field-label">Ngày bấm lại</label>
                        <input
                            type="date"
                            value={value.monetizationRetryDate || ''}
                            onChange={e => onChange({ ...value, monetizationRetryDate: e.target.value })}
                            className="channel-field-control"
                        />
                    </div>
                )}
                <div className="channel-note-field">
                    <label className="channel-field-label">Ghi chú</label>
                    <input
                        type="text"
                        value={value.monetizationNote || ''}
                        onChange={e => onChange({ ...value, monetizationNote: e.target.value })}
                        className="channel-field-control"
                        placeholder="Ghi chú ngắn cho lần xử lý này..."
                    />
                </div>
            </div>
        );
    };

    // Resources Pagination
    const filteredResources = resources.filter(res => {
        // Tab Filter
        if (activeResTab !== 'all' && res.type !== activeResTab) return false;

        // Search Filter
        if (!searchTerm.trim()) return true;
        const lower = searchTerm.toLowerCase();
        return res.title.toLowerCase().includes(lower) || (res.content && res.content.toLowerCase().includes(lower));
    }).sort((a, b) => {
        // Sort Pinned First
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
    });

    const totalResPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);
    const displayedResources = filteredResources.slice((resPage - 1) * ITEMS_PER_PAGE, resPage * ITEMS_PER_PAGE);

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    {viewMode === 'channels' ? <><Youtube className="text-red-500" /> Quản lý Kênh Vệ Tinh</> : <><Folder className="text-purple-500" /> Kho Tài Nguyên & Tool</>}
                </h2>
                <div className="flex gap-2 flex-wrap">
                    {viewMode === 'channels' && canEdit && (
                        <Button variant="secondary" onClick={handleBulkFetch} disabled={isFetching} icon={RefreshCw} className="text-xs sm:text-sm">
                            {isFetching ? "..." : "Sync All"}
                        </Button>
                    )}
                    {viewMode === 'channels' && canCreateChannel && (
                        <Button onClick={() => setIsAddChannelModalOpen(true)} icon={Plus} className="text-xs sm:text-sm">Thêm Kênh</Button>
                    )}
                </div>
            </div>

            {/* SEARCH + SORT */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="app-search flex-1 flex items-center px-4 py-2.5">
                    <Search size={18} className="text-slate-500 mr-3 shrink-0" />
                    <input
                        type="text"
                        placeholder={viewMode === 'channels' ? "Tìm kiếm kênh, người quản lý..." : "Tìm kiếm tài nguyên..."}
                        className="app-search-input bg-transparent border-none outline-none text-white text-sm w-full"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                {viewMode === 'channels' && (
                    <div className="relative shrink-0">
                        <SortAsc className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-8 py-2.5 text-white text-sm outline-none focus:border-blue-500 appearance-none cursor-pointer w-full sm:w-auto"
                        >
                            <option value="name">Sắp xếp: Tên kênh</option>
                            <option value="manager">Sắp xếp: Người quản lý</option>
                            <option value="subs">Sắp xếp: Subscribers</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={14} />
                    </div>
                )}
            </div>

            {/* REVENUE MODAL */}
            {revenueModal.isOpen && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
                    <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl relative">
                        <h3 className="text-lg font-bold text-white mb-4">Nhập Doanh Thu</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Tháng (Tính vào lương)</label>
                                <input type="month" className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white" value={revenueModal.month} onChange={e => setRevenueModal({ ...revenueModal, month: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Số tiền (USD)</label>
                                <input type="number" step="0.01" className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white font-mono text-lg" value={revenueModal.amount} onChange={e => setRevenueModal({ ...revenueModal, amount: e.target.value })} autoFocus />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button onClick={handleSaveRevenueModal} className="flex-1">Lưu</Button>
                                <Button onClick={() => setRevenueModal({ isOpen: false, channelId: null, amount: '', month: '' })} variant="secondary" className="flex-1">Hủy</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* QUICK MOVE CHANNEL MODAL */}
            {quickMoveChannel && (
                <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[130] p-4" onClick={() => { if (!isQuickMoving) { setQuickMoveChannel(null); setQuickMoveError(''); } }}>
                    <div className="quick-move-modal" onClick={e => e.stopPropagation()}>
                        <div className="quick-move-header">
                            <div className="min-w-0">
                                <span>Chuyển danh mục</span>
                                <strong title={quickMoveChannel.name}>{quickMoveChannel.name || 'Kênh chưa đặt tên'}</strong>
                            </div>
                            <button onClick={() => { setQuickMoveChannel(null); setQuickMoveError(''); }} disabled={isQuickMoving} aria-label="Đóng"><X size={18} /></button>
                        </div>
                        <div className="quick-move-options">
                            {CHANNEL_TABS.slice(1).filter(category => canMoveToMonetized || category.id !== 'monetized').map(category => {
                                const isCurrent = category.id === (quickMoveChannel.category || 'nurture');
                                return (
                                    <button
                                        key={category.id}
                                        type="button"
                                        disabled={isCurrent || isQuickMoving}
                                        onClick={() => handleQuickMove(category.id)}
                                        className={isCurrent ? 'is-current' : ''}
                                    >
                                        <span className={`quick-move-dot ${category.accent}`} />
                                        <span>{category.label}</span>
                                        {isCurrent ? <small>Hiện tại</small> : <ChevronRight size={15} />}
                                    </button>
                                );
                            })}
                        </div>
                        {quickMoveError && <p className="quick-move-error">{quickMoveError}</p>}
                        <p className="quick-move-hint">Chọn một danh mục để chuyển ngay. Trạng thái “Đã bấm / Bật xịt” trong mục Đủ điều kiện vẫn chỉ Admin được sửa.</p>
                    </div>
                </div>
            )}

            {/* EDIT RESOURCE MODAL */}
            {editRes && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
                    <Card className="w-full max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6"><h3 className="text-xl font-bold text-white">Chỉnh Sửa Tài Nguyên</h3><button onClick={() => setEditRes(null)} className="text-slate-400 hover:text-white"><X size={20} /></button></div>
                        <div className="grid grid-cols-1 gap-4">
                            <div><label className="text-xs text-slate-400">Tiêu đề</label><input value={editRes.title} onChange={e => setEditRes({ ...editRes, title: e.target.value })} className="w-full bg-slate-800 border-slate-700 rounded px-3 py-2 text-white" /></div>
                            <div><label className="text-xs text-slate-400">Loại</label><select value={editRes.type} onChange={e => setEditRes({ ...editRes, type: e.target.value })} className="w-full bg-slate-800 border-slate-700 rounded px-3 py-2 text-white"><option value="info">Kiến thức</option><option value="tool">Tool</option><option value="stock">Stock</option></select></div>
                            <div><label className="text-xs text-slate-400">Mô tả ngắn</label><textarea value={editRes.content} onChange={e => setEditRes({ ...editRes, content: e.target.value })} className="w-full bg-slate-800 border-slate-700 rounded px-3 py-2 text-white" rows={3} /></div>
                            <div><label className="text-xs text-slate-400">Hướng dẫn sử dụng (Chi tiết)</label><textarea value={editRes.manual || ''} onChange={e => setEditRes({ ...editRes, manual: e.target.value })} className="w-full bg-slate-800 border-slate-700 rounded px-3 py-2 text-white font-mono text-xs" rows={6} placeholder="Markdown hoặc text hướng dẫn..." /></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-xs text-slate-400">Link Truy cập</label><input value={editRes.link || ''} onChange={e => setEditRes({ ...editRes, link: e.target.value })} className="w-full bg-slate-800 border-slate-700 rounded px-3 py-2 text-white" /></div>
                                <div><label className="text-xs text-slate-400">Link Tải về (Optional)</label><input value={editRes.download || ''} onChange={e => setEditRes({ ...editRes, download: e.target.value })} className="w-full bg-slate-800 border-slate-700 rounded px-3 py-2 text-white" /></div>
                            </div>
                            <div className="flex gap-2 mt-4">
                                <Button onClick={handleUpdateRes}>Lưu Thay Đổi</Button>
                                <Button variant="secondary" onClick={() => setEditRes(null)}>Hủy</Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* RESOURCE DETAIL MODAL */}
            {detailRes && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
                    <Card className="w-full max-w-3xl bg-slate-900 border-slate-700 max-h-[85vh] flex flex-col">
                        <div className="flex justify-between items-start mb-4 shrink-0">
                            <div>
                                <div className={`text-xs font-bold uppercase tracking-wider mb-2 inline-block px-2 py-0.5 rounded ${detailRes.type === 'tool' ? 'bg-blue-500/20 text-blue-400' : detailRes.type === 'stock' ? 'bg-pink-500/20 text-pink-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{detailRes.type === 'tool' ? 'Tool / Phần mềm' : detailRes.type === 'stock' ? 'Kho Stock' : 'Kiến thức'}</div>
                                <h2 className="text-2xl font-bold text-white">{detailRes.title}</h2>
                            </div>
                            <button onClick={() => setDetailRes(null)} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"><X size={24} /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2 space-y-6">
                            {/* Main Content */}
                            {(detailRes.content || detailRes.description || detailRes.note) && (
                                <div className="text-slate-300 whitespace-pre-line leading-relaxed text-sm bg-slate-800/50 p-4 rounded-xl border border-slate-800">
                                    {detailRes.content || detailRes.description || detailRes.note}
                                </div>
                            )}

                            {/* Manual */}
                            {(detailRes.manual || detailRes.usage) && (
                                <div>
                                    <h4 className="text-white font-bold mb-2 flex items-center gap-2"><BookOpen size={16} className="text-yellow-500" /> Hướng dẫn sử dụng / Ghi chú</h4>
                                    <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 font-mono text-xs text-slate-300 whitespace-pre-wrap overflow-x-auto">
                                        {detailRes.manual || detailRes.usage}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pt-6 mt-4 border-t border-slate-800 flex gap-4 shrink-0">
                            {detailRes.link && (
                                <a href={detailRes.link} target="_blank" rel="noreferrer" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
                                    <ExternalLink size={18} /> Truy cập Link
                                </a>
                            )}
                            {detailRes.download && (
                                <a href={detailRes.download} target="_blank" rel="noreferrer" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
                                    <DownloadCloud size={18} /> Link Tải Về
                                </a>
                            )}
                            <button onClick={() => setDetailRes(null)} className="px-6 py-3 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors font-bold">Đóng</button>
                        </div>
                    </Card>
                </div>
            )}

            {/* FILTER TABS */}
            {viewMode === 'resources' && (
                <div className="resource-filter-tabs flex gap-2 mb-4 overflow-x-auto pb-2">
                    {[
                        { id: 'all', label: 'Tất cả', icon: Folder },
                        { id: 'tool', label: 'Tools / Phần mềm', icon: Wrench },
                        { id: 'info', label: 'Kiến thức / HDSD', icon: FileText },
                        { id: 'stock', label: 'Kho Stock / Assets', icon: ImageIcon },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { setResPage(1); setActiveResTab(tab.id); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeResTab === tab.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'}`}
                        >
                            <tab.icon size={16} /> {tab.label}
                        </button>
                    ))}
                    <div className="resource-filter-tip ml-auto flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-lg text-xs text-indigo-300">
                        <Sparkles size={12} className="text-yellow-400" />
                        <span>Mẹo: Click vào thẻ để xem chi tiết / HDSD</span>
                    </div>
                </div>
            )}

            {viewMode === 'channels' ? (
                <div className="space-y-8">
                    {/* ADD MODAL */}
                    {isAddChannelModalOpen && (
                        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                            <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
                                <div className="flex justify-between items-center mb-6"><h3 className="text-xl font-bold text-white">Thêm Kênh Mới</h3><button onClick={() => setIsAddChannelModalOpen(false)} className="text-slate-400 hover:text-white"><X size={20} /></button></div>
                                <div className="flex gap-2 mb-3">
                                    <input value={newChannel.link} onChange={e => setNewChannel({ ...newChannel, link: e.target.value })} className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Link kênh Youtube..." />
                                    <Button variant="secondary" onClick={handleAutoFetch} disabled={!newChannel.link || isFetching} className="whitespace-nowrap text-xs">{isFetching ? "..." : "Lấy Info"}</Button>
                                </div>
                                <form onSubmit={handleAdd} className="space-y-3">
                                    <input required value={newChannel.name} onChange={e => setNewChannel({ ...newChannel, name: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Tên Kênh" />
                                    <input type="email" value={newChannel.email} onChange={e => setNewChannel({ ...newChannel, email: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Email nhận thông báo kênh" />
                                    <input value={newChannel.subscribers} onChange={e => setNewChannel({ ...newChannel, subscribers: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Subs (VD: 100K)" />
                                    <select className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" value={newChannel.manager} onChange={e => setNewChannel({ ...newChannel, manager: e.target.value })}>
                                        <option value="">-- Chọn Quản lý (Để trống nếu chưa gán) --</option>
                                        {staff.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                    </select>
                                    <select className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" value={newChannel.category} onChange={e => setNewChannel({ ...newChannel, category: e.target.value })}>
                                        {CHANNEL_TABS.slice(1).filter(cat => canMoveToMonetized || cat.id !== 'monetized').map(cat => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                                    </select>
                                    <MonetizationStatusFields value={newChannel} onChange={setNewChannel} />
                                    <input value={newChannel.genre} onChange={e => setNewChannel({ ...newChannel, genre: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Hashtags/Chủ đề" />
                                    <Button type="submit" variant="primary" icon={Plus} className="w-full">Lưu Kênh</Button>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* SUMMARY CARDS */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
                            <div className="text-sm text-slate-400 font-medium mb-2">Total Channels</div>
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-black text-white">{channelStats.total}</span>
                                {channelStats.channelsGrowth ? <span className="text-emerald-400 flex items-center text-sm font-bold bg-emerald-400/10 px-2 py-0.5 rounded"><TrendingUp size={14} className="mr-1" />+{channelStats.channelsGrowth}%</span> : null}
                            </div>
                        </div>
                        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
                            <div className="text-sm text-slate-400 font-medium mb-2">Active Kênh BKT</div>
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-black text-white">{channelStats.activeBkt}</span>
                                {channelStats.bktGrowth ? <span className="text-emerald-400 flex items-center text-sm font-bold bg-emerald-400/10 px-2 py-0.5 rounded"><TrendingUp size={14} className="mr-1" />+{channelStats.bktGrowth}%</span> : null}
                            </div>
                        </div>
                        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
                            <div className="text-sm text-slate-400 font-medium mb-2">Total Subscribers</div>
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-black text-white">{channelStats.totalSubs}</span>
                                {channelStats.subsGrowth ? <span className="text-red-400 flex items-center text-sm font-bold bg-red-400/10 px-2 py-0.5 rounded"><TrendingDown size={14} className="mr-1" />{channelStats.subsGrowth}%</span> : null}
                            </div>
                        </div>
                        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
                            <div className="text-sm text-slate-400 font-medium mb-2">Est. Revenue</div>
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-black text-emerald-400">{formatUSD(channelStats.estRevenue)}</span>
                                <span className={`flex items-center text-sm font-bold px-2 py-0.5 rounded ${channelStats.revenueGrowth >= 0 ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>
                                    {channelStats.revenueGrowth >= 0 ? <TrendingUp size={14} className="mr-1" /> : <TrendingDown size={14} className="mr-1" />}
                                    {channelStats.revenueGrowth > 0 ? '+' : ''}{channelStats.revenueGrowth.toFixed(1)}%
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* FILTER TABS & SEARCH */}
                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between bg-slate-900/50 p-2 rounded-xl border border-slate-800">
                        <div className="flex gap-1.5 sm:gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 custom-scrollbar">
                            {CHANNEL_TABS.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => { setChannelPage(1); setActiveChannelTab(tab.id); }}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeChannelTab === tab.id ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        {/* SEARCH COMPONENT FROM TOP NAV MOVED/DUPLICATED HERE OR LEFT ALONE */}
                    </div>

                    {/* MOBILE CHANNEL CARDS */}
                    <div className="md:hidden space-y-3">
                        {paginatedChannels.length === 0 && (
                            <div className="p-12 text-center text-slate-500 border-2 border-dashed border-slate-800 rounded-xl">
                                <FolderOpen size={40} className="mx-auto mb-3 opacity-30" />
                                <p className="font-medium">Không tìm thấy kênh nào</p>
                            </div>
                        )}
                        {paginatedChannels.map(ch => {
                            const catDef = CHANNEL_TABS.find(t => t.id === (ch.category || 'nurture')) || CHANNEL_TABS[1];
                            const revCurrent = revenues.find(r => r.channelId === ch.id && r.month === channelStats.revMonth1)?.amount || 0;
                            const mobileCategory = ch.category || 'nurture';
                            const mobileSubsProgress = Math.min((parseChannelSubscribers(ch.subscribers) / 1000) * 100, 100);
                            return (
                                <div key={ch.id} className="bg-slate-900/80 border border-slate-700/80 rounded-xl p-4 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 border border-slate-700 shrink-0">
                                            {ch.thumbnail ? <img src={ch.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-500"><Youtube size={16} /></div>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <a href={ch.link} target="_blank" rel="noopener noreferrer" className="font-bold text-white text-sm hover:text-blue-400 truncate block">{ch.name || 'Chưa có tên'}</a>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                <span className="text-xs text-slate-400 flex items-center gap-1"><Users size={10} /> {ch.subscribers || '0'}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${catDef.badgeClass}`}>{catDef.label}</span>
                                                <MonetizationRetryBadge channel={ch} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between text-xs border-t border-slate-800 pt-2">
                                        <div className="flex items-center gap-1.5 text-slate-400">
                                            <User size={12} className="text-slate-500" />
                                            <span>{ch.manager || 'Chưa gán'}</span>
                                        </div>
                                        {(mobileCategory === 'monetized' || mobileCategory === 'claim') ? (
                                            <span className="text-emerald-400 font-bold font-mono">{revCurrent > 0 ? formatUSD(revCurrent) : '$0.00'}</span>
                                        ) : mobileCategory === 'nurture' ? (
                                            <span className="text-[10px] font-bold text-[#D9FF73]">{Math.round(mobileSubsProgress)}% tới 1K</span>
                                        ) : null}
                                    </div>
                                    {canEdit && (
                                        <div className="flex gap-2 pt-1 flex-wrap"><a href={"/prompt?channelId=" + encodeURIComponent(ch.id)} className="header-prompt-launch" title="Tạo prompt cho kênh này"><BookOpen size={14} /><span>Tạo prompt</span></a>
                                            <button onClick={() => { setEditingChannelId(ch.id); setEditData(ch); setEditChannelError(''); }} className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg border border-slate-700 transition flex items-center justify-center gap-1"><Edit2 size={12} /> Sửa</button>
                                            {(canMoveToMonetized || mobileCategory !== 'monetized') && <button onClick={() => setQuickMoveChannel(ch)} className="flex-1 py-2 bg-slate-800 hover:bg-[#D9FF73]/10 text-slate-300 hover:text-[#D9FF73] text-xs font-bold rounded-lg border border-slate-700 transition flex items-center justify-center gap-1"><MoveRight size={12} /> Move</button>}
                                            {canEditRevenue && (mobileCategory === 'monetized' || mobileCategory === 'claim') && <button onClick={() => setRevenueModal({ isOpen: true, channelId: ch.id, amount: revCurrent || '', month: channelStats.revMonth1 })} className="flex-1 py-2 bg-slate-800 hover:bg-emerald-600/20 text-slate-300 hover:text-emerald-400 text-xs font-bold rounded-lg border border-slate-700 transition flex items-center justify-center gap-1"><DollarSign size={12} /> Doanh thu</button>}
                                            {canDelete && <button onClick={() => onDeleteChannel(ch.id, ch.name)} className="py-2 px-3 bg-slate-800 hover:bg-red-600/20 text-slate-400 hover:text-red-400 text-xs rounded-lg border border-slate-700 transition"><Trash2 size={12} /></button>}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {totalChannelPages > 1 && <Pagination currentPage={channelPage} totalPages={totalChannelPages} onPageChange={setChannelPage} />}
                    </div>

                    {editingChannelId && (
                        <div className="md:hidden fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm p-3 flex items-end justify-center">
                            <div className="w-full max-w-md max-h-[92dvh] overflow-y-auto bg-[#0c0c0c] border border-white/15 rounded-2xl p-4 space-y-4 shadow-2xl">
                                <div className="flex items-center justify-between"><div><p className="text-[10px] uppercase tracking-widest text-slate-500">Quản lý kênh</p><h3 className="text-lg font-bold text-white mt-1">Chỉnh sửa kênh</h3><a href={"/prompt?channelId=" + encodeURIComponent(editingChannelId)} className="header-prompt-launch mt-2">Tạo prompt cho kênh này</a></div><button onClick={() => { setEditingChannelId(null); setEditData({}); setEditChannelError(''); }} disabled={isSavingChannelEdit} className="p-2 text-slate-400"><X size={20} /></button></div>
                                <div className="space-y-3">
                                    <input value={editData.link || ''} onChange={e => setEditData({ ...editData, link: e.target.value })} className="w-full bg-black border border-white/10 rounded-lg px-3 py-3 text-white text-sm" placeholder="Link kênh" />
                                    <button onClick={async () => { setIsFetching(true); const d = await fetchYouTubeRealData(editData.link, youtubeApiKey); if (d) setEditData({ ...editData, name: d.name, subscribers: d.subscribers, thumbnail: d.thumbnail, viewCount: d.viewCount, videoCount: d.videoCount }); setIsFetching(false); }} disabled={!editData.link || isFetching} className="w-full py-2.5 border border-white/10 rounded-lg text-sm font-bold text-[#DEDBC8]">{isFetching ? 'Đang lấy thông tin...' : 'Lấy thông tin từ YouTube'}</button>
                                    <input value={editData.name || ''} onChange={e => setEditData({ ...editData, name: e.target.value })} className="w-full bg-black border border-white/10 rounded-lg px-3 py-3 text-white text-sm" placeholder="Tên kênh" />
                                    <select value={editData.manager || ''} onChange={e => setEditData({ ...editData, manager: e.target.value })} className="w-full bg-black border border-white/10 rounded-lg px-3 py-3 text-white text-sm"><option value="">-- Quản lý --</option>{staff.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select>
                                    <select disabled={!canMoveToMonetized && editData.category === 'monetized'} value={editData.category || 'nurture'} onChange={e => setEditData({ ...editData, category: e.target.value })} className="w-full bg-black border border-white/10 rounded-lg px-3 py-3 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed">{CHANNEL_TABS.slice(1).map(cat => <option key={cat.id} value={cat.id} disabled={!canMoveToMonetized && cat.id === 'monetized'}>{cat.label}</option>)}</select>
                                    <MonetizationStatusFields value={editData} onChange={setEditData} />
                                    <input value={editData.genre || ''} onChange={e => setEditData({ ...editData, genre: e.target.value })} className="w-full bg-black border border-white/10 rounded-lg px-3 py-3 text-white text-sm" placeholder="Hashtags / Thể loại" />
                                    <input type="email" value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.target.value })} className="w-full bg-black border border-white/10 rounded-lg px-3 py-3 text-white text-sm" placeholder="Email chính của kênh" />
                                </div>
                                {editChannelError && <p className="quick-move-error !m-0">{editChannelError}</p>}
                                <div className="grid grid-cols-2 gap-2 pt-2"><button onClick={() => { setEditingChannelId(null); setEditData({}); setEditChannelError(''); }} disabled={isSavingChannelEdit} className="py-3 border border-white/10 rounded-lg text-sm font-bold text-slate-300 disabled:opacity-50">Hủy</button><button onClick={handleEditSave} disabled={isSavingChannelEdit} className="py-3 bg-[#DEDBC8] text-black rounded-lg text-sm font-bold disabled:opacity-60">{isSavingChannelEdit ? 'Đang lưu...' : 'Lưu thay đổi'}</button></div>
                            </div>
                        </div>
                    )}

                    {/* DESKTOP TABLE */}
                    <div className="hidden md:block bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden shadow-xl relative min-h-[400px]">
                        {isFetching && (
                            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
                                <div className="flex items-center gap-3 text-blue-400 font-bold bg-slate-800 px-6 py-3 rounded-full shadow-2xl border border-blue-500/20">
                                    <RefreshCw size={18} className="animate-spin" /> Đang đồng bộ...
                                </div>
                            </div>
                        )}
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-800 text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-900/80">
                                    <th className="p-4 pl-6 font-medium">Channel Name</th>
                                    <th className="p-4 font-medium">Subscribers</th>
                                    <th className="p-4 font-medium">Status / Manager</th>
                                    <th className="p-4 font-medium">Thông tin / Tiến độ</th>
                                    <th className="p-4 pr-6 text-right font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                                {paginatedChannels.map(ch => {
                                    const isEditing = editingChannelId === ch.id;
                                    const catDef = CHANNEL_TABS.find(t => t.id === (ch.category || 'nurture')) || CHANNEL_TABS[1];

                                    const revCurrent = revenues.find(r => r.channelId === ch.id && r.month === channelStats.revMonth1)?.amount || 0;
                                    const revPast = revenues.find(r => r.channelId === ch.id && r.month === channelStats.revMonth2)?.amount || 0;
                                    const channelCategory = ch.category || 'nurture';
                                    const showsRevenue = channelCategory === 'monetized' || channelCategory === 'claim';
                                    const subscriberCount = parseChannelSubscribers(ch.subscribers);
                                    const eligibilityProgress = Math.min((subscriberCount / 1000) * 100, 100);
                                    const revenueScale = Math.max(Number(revCurrent) || 0, Number(revPast) || 0, 1);
                                    const currentRevenueWidth = revCurrent > 0 ? Math.max(((Number(revCurrent) || 0) / revenueScale) * 100, 6) : 0;
                                    const pastRevenueWidth = revPast > 0 ? Math.max(((Number(revPast) || 0) / revenueScale) * 100, 6) : 0;
                                    let revGrowth = 0;
                                    if (revPast > 0) revGrowth = ((revCurrent - revPast) / revPast) * 100;
                                    else if (revCurrent > 0) revGrowth = 100;

                                    if (isEditing) {
                                        return (
                                            <tr key={ch.id} className="bg-slate-800/50">
                                                <td colSpan="5" className="p-4">
                                                    {/* Inline Edit Form */}
                                                    <div className="flex flex-col gap-4 bg-slate-950 p-4 rounded-xl border border-blue-500/30 shadow-inner">
                                                        <div className="flex gap-2">
                                                            <input value={editData.link || ''} onChange={e => setEditData({ ...editData, link: e.target.value })} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none" placeholder="Link kênh..." />
                                                            <Button variant="secondary" onClick={async () => {
                                                                setIsFetching(true);
                                                                const d = await fetchYouTubeRealData(editData.link, youtubeApiKey);
                                                                if (d) setEditData({ ...editData, name: d.name, subscribers: d.subscribers, thumbnail: d.thumbnail, viewCount: d.viewCount, videoCount: d.videoCount });
                                                                setIsFetching(false);
                                                            }} disabled={!editData.link || isFetching} className="whitespace-nowrap px-4 py-2 text-sm">{isFetching ? "..." : "Lấy Info API"}</Button>
                                                        </div>
                                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                                            <input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} className="md:col-span-2 bg-slate-900 text-white text-sm px-3 py-2 rounded-lg border border-slate-700 focus:border-blue-500 outline-none" placeholder="Tên kênh" />
                                                            <select className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none" value={editData.manager || ''} onChange={e => setEditData({ ...editData, manager: e.target.value })}>
                                                                <option value="">-- Quản lý --</option>
                                                                {staff.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                                            </select>
                                                            <select disabled={!canMoveToMonetized && editData.category === 'monetized'} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed" value={editData.category || 'nurture'} onChange={e => setEditData({ ...editData, category: e.target.value })}>
                                                                {CHANNEL_TABS.slice(1).map(cat => <option key={cat.id} value={cat.id} disabled={!canMoveToMonetized && cat.id === 'monetized'}>{cat.label}</option>)}
                                                            </select>
                                                        </div>
                                                        <MonetizationStatusFields value={editData} onChange={setEditData} />
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <input value={editData.genre || ''} onChange={e => setEditData({ ...editData, genre: e.target.value })} className="bg-slate-900 text-white text-sm px-3 py-2 rounded-lg border border-slate-700 focus:border-blue-500 outline-none" placeholder="Hashtags (VD: Space Opera, Sci-Fi)" />
                                                            <input type="email" value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.target.value })} className="bg-slate-900 text-white text-sm px-3 py-2 rounded-lg border border-slate-700 focus:border-blue-500 outline-none" placeholder="Email chính của kênh" />
                                                        </div>
                                                        <div className="flex gap-3 justify-end mt-2">
                                                            <button onClick={() => { setEditingChannelId(null); setEditChannelError(''); }} disabled={isSavingChannelEdit} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm rounded-lg border border-slate-700 transition disabled:opacity-50">Hủy</button>
                                                            <button onClick={handleEditSave} disabled={isSavingChannelEdit} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm rounded-lg transition shadow-lg shadow-emerald-600/20 disabled:opacity-60">{isSavingChannelEdit ? 'Đang lưu...' : 'Lưu Xong'}</button>
                                                        </div>
                                                        {editChannelError && <p className="quick-move-error !m-0">{editChannelError}</p>}
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    }

                                    return (
                                        <tr key={ch.id} className="hover:bg-slate-800/30 transition-colors group cursor-default">
                                            <td className="p-4 pl-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 border border-slate-700 shrink-0">
                                                        {ch.thumbnail ? <img src={ch.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-500"><Youtube size={16} /></div>}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <a href={ch.link} target="_blank" rel="noopener noreferrer" className="font-bold text-white text-sm hover:text-blue-400 hover:underline truncate block max-w-[250px]" title={ch.name}>{ch.name || <span className="italic text-slate-500">Chưa có tên</span>}</a>
                                                        <div className="text-[10px] text-slate-500 font-mono mt-0.5 opacity-80">ID: {ch.id.slice(-8)}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4 align-middle">
                                                <div className="flex flex-col gap-1">
                                                    <span className="font-bold text-slate-200 text-sm flex items-center gap-1.5"><Users size={12} className="text-slate-400" /> {ch.subscribers || '0'}</span>
                                                    <span className="text-xs text-slate-500 font-mono flex items-center gap-1.5"><Video size={10} className="text-slate-500" /> {ch.videoCount ? parseInt(ch.videoCount).toLocaleString() : '0'}</span>
                                                </div>
                                            </td>
                                            <td className="p-4 align-middle">
                                                <div className="flex flex-col gap-2 items-start">
                                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border whitespace-nowrap ${catDef.badgeClass}`}>{catDef.label}</span>
                                                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400 max-w-[150px] truncate" title={ch.manager}>
                                                        {ch.manager ? <><User size={10} className="shrink-0 text-slate-500" /> {ch.manager}</> : <span className="italic text-slate-600">Chưa gán</span>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4 align-middle">
                                                {showsRevenue ? (
                                                    <div className="channel-revenue-comparison">
                                                        <div className="channel-revenue-bars">
                                                            <div className="channel-revenue-row">
                                                                <span>{formatRevenuePeriod(channelStats.revMonth2)}</span>
                                                                <div className="channel-revenue-track"><i className="channel-revenue-past" style={{ width: `${pastRevenueWidth}%` }} /></div>
                                                                <strong>{formatUSD(revPast)}</strong>
                                                            </div>
                                                            <div className="channel-revenue-row channel-revenue-current-row">
                                                                <span>{formatRevenuePeriod(channelStats.revMonth1)}</span>
                                                                <div className="channel-revenue-track"><i className="channel-revenue-current" style={{ width: `${currentRevenueWidth}%` }} /></div>
                                                                <strong>{formatUSD(revCurrent)}</strong>
                                                            </div>
                                                        </div>
                                                        <div className="channel-revenue-actions">
                                                            <span className={`channel-growth-badge ${revGrowth >= 0 ? 'is-positive' : 'is-negative'}`} title="Thay đổi so với kỳ trước">
                                                                {revGrowth >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{revPast === 0 && revCurrent === 0 ? '—' : `${revGrowth > 0 ? '+' : ''}${revGrowth.toFixed(0)}%`}
                                                            </span>
                                                            {canEditRevenue && <button onClick={() => setRevenueModal({ isOpen: true, channelId: ch.id, amount: revCurrent || '', month: channelStats.revMonth1 })} className="channel-revenue-edit" title="Nhập doanh thu kỳ hiện tại"><DollarSign size={13} /></button>}
                                                        </div>
                                                    </div>
                                                ) : channelCategory === 'eligible' ? (
                                                    <div className="channel-eligibility-state">
                                                        <span className="channel-info-label">Xử lý bật kiếm tiền</span>
                                                        <MonetizationRetryBadge channel={ch} />
                                                    </div>
                                                ) : (
                                                    <div className="channel-subscriber-progress">
                                                        <div className="channel-progress-heading">
                                                            <span>Tiến độ 1K subs</span>
                                                            <strong>{Math.round(eligibilityProgress)}%</strong>
                                                        </div>
                                                        <div className="channel-progress-track"><i style={{ width: `${eligibilityProgress}%` }} /></div>
                                                        <span className="channel-progress-caption">{Math.round(subscriberCount).toLocaleString('vi-VN')} / 1.000 subscribers</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-4 align-middle pr-6">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    {canEdit && (
                                                        <>
                                                            <button onClick={async () => {
                                                                if (isFetching) return;
                                                                setIsFetching(true);
                                                                const d = await fetchYouTubeRealData(ch.link, youtubeApiKey);
                                                                if (d) await onRefreshChannel(ch.id, { name: d.name, subscribers: d.subscribers, thumbnail: d.thumbnail, viewCount: d.viewCount, videoCount: d.videoCount });
                                                                setIsFetching(false);
                                                            }} className="px-3 py-1.5 bg-slate-800 hover:bg-blue-600/20 hover:text-blue-400 text-slate-400 text-xs font-bold rounded-lg border border-slate-700 transition" title="Sync API">
                                                                Sync
                                                            </button>
                                                            {(canMoveToMonetized || channelCategory !== 'monetized') && <button onClick={() => setQuickMoveChannel(ch)} className="px-3 py-1.5 bg-slate-800 hover:bg-[#D9FF73]/10 hover:text-[#D9FF73] text-slate-300 text-xs font-bold rounded-lg border border-slate-700 transition" title="Chuyển nhanh danh mục">
                                                                Move
                                                            </button>}
                                                            <a href={"/prompt?channelId=" + encodeURIComponent(ch.id)} className="header-prompt-launch" title="Tạo prompt cho kênh này"><BookOpen size={14} /><span>Tạo prompt</span></a><button onClick={() => { setEditingChannelId(ch.id); setEditData(ch); setEditChannelError(''); }} className="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-slate-800 rounded-lg transition" title="Sửa channel"><Edit2 size={16} /></button>
                                                            <button onClick={() => onDeleteChannel(ch.id, ch.name)} className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition opacity-0 group-hover:opacity-100" title="Xóa channel"><Trash2 size={16} /></button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {paginatedChannels.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="p-16 text-center text-slate-500">
                                            <FolderOpen size={48} className="mx-auto mb-4 opacity-50 text-slate-500" />
                                            <p className="font-medium text-lg text-slate-400">Không tìm thấy kênh nào ở đây</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {totalChannelPages > 1 && (
                            <div className="p-4 border-t border-slate-800 bg-slate-900/50 flex items-center justify-between">
                                <span className="text-sm text-slate-400 font-medium hidden sm:block">
                                    Showing {(channelPage - 1) * CHANNELS_PER_PAGE + 1}-{Math.min(channelPage * CHANNELS_PER_PAGE, displayedChannels.length)} of {displayedChannels.length} channels
                                </span>
                                <Pagination currentPage={channelPage} totalPages={totalChannelPages} onPageChange={setChannelPage} />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                // --- RESOURCES GRID VIEW (NEW) ---
                <div className="flex flex-col gap-6">
                    {/* ADD RESOURCE (TOP) */}
                    {canEditResources && (
                        <div className="resource-add-panel bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/20 rounded-xl p-4 flex gap-4 items-center animate-in fade-in slide-in-from-top-2">
                            <div className="p-3 bg-blue-500/10 rounded-lg text-blue-400 hidden lg:block"><Sparkles size={24} /></div>
                            <form onSubmit={handleAddRes} className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-12 gap-2">
                                <div className="md:col-span-3"><input required value={newRes.title} onChange={e => setNewRes({ ...newRes, title: e.target.value })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none transition-colors" placeholder="Tiêu đề tài nguyên" /></div>
                                <div className="md:col-span-2"><select value={newRes.type} onChange={e => setNewRes({ ...newRes, type: e.target.value })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none transition-colors"><option value="info">Kiến thức / HDSD</option><option value="tool">Tool / Phần mềm</option><option value="stock">Kho Stock / Assets</option></select></div>
                                <div className="md:col-span-5"><input value={newRes.link} onChange={e => setNewRes({ ...newRes, link: e.target.value })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none transition-colors" placeholder="Link truy cập..." /></div>
                                <div className="md:col-span-2"><Button type="submit" className="w-full h-full text-sm">Chia sẻ</Button></div>
                                <div className="md:col-span-12">
                                    <div className="grid grid-cols-2 gap-2">
                                        <textarea value={newRes.content} onChange={e => setNewRes({ ...newRes, content: e.target.value })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none transition-colors" placeholder="Mô tả ngắn..." rows={1} />
                                        <textarea value={newRes.manual || ''} onChange={e => setNewRes({ ...newRes, manual: e.target.value })} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none transition-colors font-mono" placeholder="Link File HDSD hoặc Nội dung hướng dẫn (Markdown)..." rows={1} />
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* RESOURCE GRID */}
                    {displayedResources.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {displayedResources.map(res => {
                                const isTool = res.type === 'tool';
                                const isStock = res.type === 'stock';
                                const colorClass = isTool ? 'border-blue-500/20 bg-blue-900/5 hover:bg-blue-900/10' : isStock ? 'border-pink-500/20 bg-pink-900/5 hover:bg-pink-900/10' : 'border-emerald-500/20 bg-emerald-900/5 hover:bg-emerald-900/10';
                                const iconClass = isTool ? 'text-blue-400' : isStock ? 'text-pink-400' : 'text-emerald-400';

                                return (
                                    <div key={res.id} className={`p-5 rounded-xl border ${colorClass} ${res.isPinned ? 'ring-2 ring-yellow-500/40 bg-yellow-900/10' : ''} transition-all duration-300 relative group flex flex-col h-full hover:shadow-lg`} onClick={() => setDetailRes(res)}>
                                        <div className="flex items-start justify-between mb-3">
                                            <div className={`p-2 rounded-lg bg-slate-950 border border-slate-800 ${iconClass}`}>
                                                {isTool ? <Wrench size={20} /> : isStock ? <ImageIcon size={20} /> : <FileText size={20} />}
                                            </div>
                                            {canEditResources && (
                                                <div className="resource-card-actions flex gap-1 opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                                                    <button onClick={() => handleTogglePin(res)} className={`p-1.5 rounded-lg transition-colors ${res.isPinned ? 'text-yellow-400 bg-yellow-400/10' : 'text-slate-600 hover:text-yellow-400'}`} title="Ghim lên đầu"><Pin size={14} className={res.isPinned ? 'fill-yellow-400' : ''} /></button>
                                                    <button onClick={() => setEditRes(res)} className="p-1.5 rounded-lg text-slate-600 hover:text-blue-400 transition-colors" title="Chỉnh sửa"><Edit2 size={14} /></button>
                                                    {canDelete && <button onClick={() => onDeleteResource(res.id)} className="text-slate-600 hover:text-red-400 p-1.5" title="Xóa"><Trash2 size={14} /></button>}
                                                </div>
                                            )}
                                        </div>

                                        <h4 className="font-bold text-white text-base mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors cursor-pointer" title={res.title}>{res.title}</h4>
                                        <p className="text-sm text-slate-400 mb-4 line-clamp-3 text-ellipsis overflow-hidden flex-1">
                                            {res.content || res.description || res.note || "Không có mô tả chi tiết."}
                                        </p>

                                        <div className="resource-card-footer mt-auto pt-4 border-t border-white/5 grid grid-cols-2 gap-2">
                                            {res.link ? (
                                                <a href={res.link} onClick={e => e.stopPropagation()} target="_blank" rel="noreferrer" className={`flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-colors ${isTool ? 'bg-blue-600 hover:bg-blue-500 text-white' : isStock ? 'bg-pink-600 hover:bg-pink-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}>
                                                    {isTool ? <DownloadCloud size={14} /> : <ExternalLink size={14} />}
                                                    <span>TRUY CẬP</span>
                                                </a>
                                            ) : (
                                                <div className="py-2 text-center text-xs text-slate-500 bg-slate-900/50 rounded-lg">No Link</div>
                                            )}
                                            <button onClick={(e) => { e.stopPropagation(); setDetailRes(res); }} className="px-3 py-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors text-xs font-bold border border-slate-700">CHI TIẾT</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-12 flex flex-col items-center justify-center text-slate-500 border-2 border-dashed border-slate-800 rounded-xl">
                            <Cloud size={48} className="mb-4 opacity-50" />
                            <p>Không tìm thấy tài nguyên nào trong mục này.</p>
                        </div>
                    )}
                    <Pagination currentPage={resPage} totalPages={totalResPages} onPageChange={setResPage} />
                </div>
            )}
        </div>
    );
};


// --- REVENUE BOARD VIEW (NEW) ---
const RevenueBoardView = ({ staff, channels, revenues, exchangeRate }) => {
    const [viewMonth, setViewMonth] = useState(new Date().toISOString().slice(0, 7)); // Month of Payout (Salary Log)

    // Calculate Previous Month for Revenue Source
    const getPreviousMonth = (m) => {
        const d = new Date(m + "-01");
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().slice(0, 7);
    }
    const revenueMonth = getPreviousMonth(viewMonth);

    // Helper to get revenue
    const getStaffMetrics = (staffName, commissionRate) => {
        const staffChannels = channels.filter(c => c.manager === staffName);
        const totalUSD = staffChannels.reduce((sum, ch) => {
            // Revenue Source: Dashboard & KPI use De-duplication. We must do same here.
            const revs = revenues.filter(r => r.channelId === ch.id && r.month === revenueMonth);
            if (revs.length === 0) return sum + 0;
            // Sort by latest
            revs.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
            return sum + Number(revs[0].amount);
        }, 0);
        const commissionUSD = totalUSD * (commissionRate / 100);
        const salaryVND = commissionUSD * exchangeRate;
        return { totalUSD, commissionUSD, salaryVND, channelCount: staffChannels.length };
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="text-2xl font-bold text-white">Bảng Doanh Thu Tổng Hợp</h2>
                <div className="flex items-center gap-3 bg-slate-800 rounded-lg border border-slate-700 p-2">
                    <span className="text-sm text-slate-300">Tháng trả lương:</span>
                    <input type="month" value={viewMonth} onChange={(e) => setViewMonth(e.target.value)} className="bg-transparent font-bold text-white outline-none" />
                </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-500/50 p-4 rounded-xl flex items-center gap-4 animate-in fade-in slide-in-from-top-2">
                <div className="p-3 bg-amber-500/10 rounded-full text-amber-500">
                    <AlertTriangle size={24} />
                </div>
                <div>
                    <h4 className="font-bold text-amber-500">Lưu ý Quan trọng</h4>
                    <p className="text-sm text-slate-300">
                        Lương tháng <span className="text-white font-bold">{viewMonth}</span> sẽ được trả vào ngày 25 nếu tiền của tháng <span className="text-white font-bold">{revenueMonth}</span> về tài khoản. Và thực nhận của các bạn mình đã trừ đi thuế TNCN nên các bạn không cần làm gì thêm.
                    </p>
                </div>
            </div>

            {staff.length === 1 ? (
                <div className="max-w-4xl mx-auto">
                    {staff.map(person => {
                        const metrics = getStaffMetrics(person.name, person.commissionRate || 15);
                        const staffChannels = channels.filter(c => c.manager === person.name);
                        return (
                            <div key={person.id} className="space-y-6">
                                {/* Single Staff Detailed Header */}
                                <Card className="revenue-profile-card bg-gradient-to-br from-indigo-900/40 via-slate-900 to-slate-900 border-indigo-500/30 p-4 sm:p-6 lg:p-8 shadow-2xl relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                                    <div className="flex flex-col lg:flex-row items-center gap-5 lg:gap-8 relative z-10 min-w-0">
                                        <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full border-4 border-indigo-500/50 overflow-hidden shadow-xl shrink-0">
                                            {person.avatar ? <img src={person.avatar} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-slate-800 flex items-center justify-center text-4xl text-slate-500 font-bold">{person.name.charAt(0)}</div>}
                                        </div>
                                        <div className="flex-1 min-w-0 text-center lg:text-left">
                                            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-2"><h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white break-words">{person.name}</h2><RoleBadge role={person.accessRole || 'member'} /></div>
                                            <p className="text-indigo-300 font-medium text-lg">{person.role} • {metrics.channelCount} kênh</p>
                                        </div>
                                        <div className="bg-slate-900/80 backdrop-blur border border-slate-700/50 p-4 sm:p-5 lg:p-6 rounded-2xl w-full lg:w-80 min-w-0 shadow-xl overflow-hidden">
                                            <p className="text-sm text-slate-400 mb-1 uppercase tracking-wider font-bold">Thực nhận (VND)</p>
                                            <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-emerald-400 mb-4 break-words">{formatCurrency(metrics.salaryVND)}</p>
                                            <div className="pt-4 border-t border-slate-700/50 space-y-2 text-sm">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-400">Tổng doanh thu ({revenueMonth}):</span>
                                                    <span className="text-white font-bold">{formatUSD(metrics.totalUSD)}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-400">Tỉ lệ hoa hồng:</span>
                                                    <span className="text-blue-400 font-bold">{person.commissionRate || 15}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>

                                {/* Channel Individual Breakdown */}
                                <h3 className="text-xl font-bold text-white flex items-center gap-2 pt-4">
                                    <Youtube className="text-red-500" /> Bảng phân bổ theo kênh ({metrics.channelCount})
                                </h3>
                                {staffChannels.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {staffChannels.map(ch => {
                                            const revs = revenues.filter(r => r.channelId === ch.id && r.month === revenueMonth);
                                            revs.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
                                            const revAmount = revs.length > 0 ? Number(revs[0].amount) : 0;
                                            return (
                                                <div key={ch.id} className="bg-slate-800/40 border border-slate-700/50 p-4 rounded-xl flex items-center gap-4 hover:border-blue-500/30 transition-colors">
                                                    <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-900 shrink-0 border border-slate-700">
                                                        {ch.thumbnail ? <img src={ch.thumbnail} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-500"><Youtube size={20} /></div>}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-sm font-bold text-slate-200 truncate">{ch.name}</h4>
                                                        <p className="text-xs text-slate-500 mt-0.5">{ch.subscribers} subs</p>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <p className="text-emerald-400 font-bold font-mono">{formatUSD(revAmount)}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : <p className="text-slate-400 text-sm">Chưa có dữ liệu kênh.</p>}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {staff.map(person => {
                        const metrics = getStaffMetrics(person.name, person.commissionRate || 15);
                        return (
                            <Card key={person.id} className="relative overflow-hidden border-slate-700/50 hover:border-blue-500/30 transition-all">
                                <div className="flex items-center gap-4 mb-4 border-b border-slate-800 pb-4">
                                    <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700 overflow-hidden">
                                        {person.avatar ? <img src={person.avatar} className="w-full h-full object-cover" /> : <span className="font-bold text-slate-400">{person.name.charAt(0)}</span>}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 min-w-0"><h3 className="font-bold text-white text-lg truncate">{person.name}</h3><RoleBadge role={person.accessRole || 'member'} /></div>
                                        <p className="text-xs text-slate-500">{person.role} • {metrics.channelCount} kênh</p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-slate-400">Doanh thu (USD)</span>
                                        <span className="text-emerald-400 font-mono font-bold">{formatUSD(metrics.totalUSD)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-slate-400">Hoa hồng ({person.commissionRate || 15}%)</span>
                                        <span className="text-blue-400 font-mono font-bold">{formatUSD(metrics.commissionUSD)}</span>
                                    </div>
                                    <div className="pt-3 border-t border-slate-800 flex justify-between items-center">
                                        <span className="text-slate-300 font-medium">Thực nhận (VND)</span>
                                        <span className="text-white font-bold text-lg">{formatCurrency(metrics.salaryVND)}</span>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div >
    );
};

// --- STAFF VIEW ---
const StaffView = ({ staff, tasks, channels, onAddStaff, onUpdateStaff, onDeleteStaff, canEdit, canDelete, apiKey, onSaveRevenue, revenues = [], exchangeRate = 25000 }) => {
    const [newStaff, setNewStaff] = useState({ name: '', role: '', rating: '5' });
    const [commissionModal, setCommissionModal] = useState(null);
    const [editStaffModal, setEditStaffModal] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
    const [rateInput, setRateInput] = useState(15);
    const [isExportingPayroll, setIsExportingPayroll] = useState(false);
    const payrollCardRef = useRef(null);

    const getStats = (name) => {
        const channelCount = channels.filter(c => c.manager === name).length;
        const taskCount = tasks.filter(t => t.assignee === name && t.status === 'Đã xong').length;
        return { channelCount, taskCount };
    }

    const handleAdd = async (e) => {
        e.preventDefault();
        await onAddStaff(newStaff);
        setNewStaff({ name: '', role: '', rating: '5' });
    };

    const handleEditStaff = async () => {
        if (editStaffModal) {
            await onUpdateStaff(editStaffModal.id, editStaffModal);
            setEditStaffModal(null);
        }
    }

    const openCommission = (person) => {
        setCommissionModal(person);
        setRateInput(person.commissionRate || 15);
    };

    const sharePayrollCard = async () => {
        if (!payrollCardRef.current || !commissionModal) return;
        setIsExportingPayroll(true);
        try {
            if (document.fonts?.ready) await document.fonts.ready;
            const dataUrl = await toPng(payrollCardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: '#0a0a09', filter: node => !node?.dataset?.shareControls });
            const blob = await (await fetch(dataUrl)).blob();
            const safeName = commissionModal.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
            const file = new File([blob], `hopeteam-${safeName}-${currentMonth}.png`, { type: 'image/png' });
            if (navigator.share && navigator.canShare?.({ files: [file] })) {
                await navigator.share({ title: `Tổng kết tháng ${commissionModal.name}`, text: `Bảng tổng kết ${currentMonth} của ${commissionModal.name}`, files: [file] });
            } else {
                const link = document.createElement('a');
                link.download = file.name;
                link.href = dataUrl;
                link.click();
            }
        } catch (error) {
            if (error?.name !== 'AbortError') alert('Không thể tạo ảnh chia sẻ. Hãy kiểm tra ảnh đại diện hoặc thử lại.');
        } finally { setIsExportingPayroll(false); }
    };

    const getRevenueForChannel = (channelId, month) => {
        const rev = revenues.find(r => r.channelId === channelId && r.month === month);
        return rev ? Number(rev.amount) : 0;
    };

    const getPreviousMonth = (monthStr) => {
        if (!monthStr) return '';
        const date = new Date(monthStr + '-01');
        date.setMonth(date.getMonth() - 1);
        return date.toISOString().slice(0, 7);
    };

    const revenueMonth = getPreviousMonth(currentMonth);
    const staffChannels = commissionModal ? channels.filter(c => c.manager === commissionModal.name) : [];

    // Revenue Logic Update: Payout on 23rd is for Previous Month's Revenue
    const totalRevenueUSD = staffChannels.reduce((sum, ch) => sum + getRevenueForChannel(ch.id, revenueMonth), 0);
    const bktChannelCount = staffChannels.filter(ch => ch.category === 'monetized').length;
    const completedTaskCount = commissionModal ? tasks.filter(task => task.assignee === commissionModal.name && task.status === 'Đã xong' && (!task.date || task.date.startsWith(currentMonth))).length : 0;

    // Tính hoa hồng: (Doanh thu USD * % Hoa hồng) * Tỷ giá
    const commissionUSD = totalRevenueUSD * (Number(rateInput) / 100);
    const commissionVND = commissionUSD * exchangeRate;

    return (
        <div className="space-y-8">
            <h2 className="text-2xl font-bold text-white">Nhân sự & Hoa hồng</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {staff.map((person) => {
                    const stats = getStats(person.name);
                    const ratingVal = parseInt(person.rating || 0);
                    return (
                        <Card key={person.id} className="relative group flex flex-col justify-between">
                            {canDelete && <button onClick={() => onDeleteStaff(person.id)} className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"><Trash2 size={16} /></button>}
                            <div className="relative">
                                {canEdit && <button onClick={() => setEditStaffModal(person)} className="absolute top-0 left-0 text-slate-500 hover:text-blue-400"><UserCog size={16} /></button>}
                                <div className="flex flex-col items-center text-center mb-4">
                                    <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center border-2 border-slate-700 overflow-hidden mb-3">
                                        {person.avatar ? <img src={person.avatar} alt={person.name} className="w-full h-full object-cover" /> : <span className="text-xl font-bold text-white">{person.name.charAt(0)}</span>}
                                    </div>
                                    <div className="flex items-center justify-center gap-2"><h3 className="text-lg font-bold text-white">{person.name}</h3><RoleBadge role={person.accessRole || 'member'} /></div>
                                    <span className="text-xs font-mono text-slate-400 px-2 py-1 bg-slate-700 rounded mt-1">{person.role}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-4 text-center">
                                    <div className="bg-slate-900/50 p-2 rounded border border-slate-700/50"><p className="text-xs text-slate-500">Kênh</p><p className="text-lg font-bold text-white">{stats.channelCount}</p></div>
                                    <div className="bg-slate-900/50 p-2 rounded border border-slate-700/50"><p className="text-xs text-slate-500">Task</p><p className="text-lg font-bold text-emerald-400">{stats.taskCount}</p></div>
                                </div>
                                <div className="space-y-2 mb-4">
                                    <div className="flex justify-between text-xs text-slate-400"><span>Rating:</span><span className="text-yellow-400 flex items-center gap-1 font-bold">{ratingVal} <Star size={10} fill="currentColor" /></span></div>
                                    <div className="w-full bg-slate-700 rounded-full h-1.5"><div className="bg-gradient-to-r from-yellow-600 to-yellow-400 h-1.5 rounded-full" style={{ width: `${(ratingVal / 5) * 100}%` }} /></div>
                                </div>
                            </div>
                            <Button onClick={() => openCommission(person)} variant="success" icon={Coins} className="w-full mt-2 text-sm h-9">Tính Lương</Button>
                        </Card>
                    );
                })}
                {canEdit && (
                    <Card className="border-dashed border-slate-600 bg-slate-800/30 flex flex-col justify-center">
                        <h3 className="text-white font-semibold mb-4 text-center">Thêm Nhân sự</h3>
                        <form onSubmit={handleAdd} className="space-y-3">
                            <input required placeholder="Tên" value={newStaff.name} onChange={e => setNewStaff({ ...newStaff, name: e.target.value })} className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm" />
                            <input required placeholder="Vị trí" value={newStaff.role} onChange={e => setNewStaff({ ...newStaff, role: e.target.value })} className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm" />
                            <Button type="submit" className="w-full">Thêm</Button>
                        </form>
                    </Card>
                )}
            </div>

            {/* EDIT STAFF MODAL */}
            {editStaffModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
                        <h3 className="text-lg font-bold text-white mb-4">Chỉnh sửa Nhân sự</h3>
                        <div className="space-y-3">
                            <div><label className="text-xs text-slate-400">Họ tên</label><input className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" value={editStaffModal.name} onChange={e => setEditStaffModal({ ...editStaffModal, name: e.target.value })} /></div>
                            <div><label className="text-xs text-slate-400">Chức vụ</label><input className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" value={editStaffModal.role} onChange={e => setEditStaffModal({ ...editStaffModal, role: e.target.value })} /></div>
                            <div><label className="text-xs text-slate-400">Quyền hệ thống</label><select className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" value={editStaffModal.accessRole || 'member'} onChange={e => setEditStaffModal({ ...editStaffModal, accessRole: e.target.value, accessStatus: 'approved' })}><option value="member">MEMBER — Nhân viên thường</option><option value="staff">STAFF — Quản lý team</option><option value="admin">ADMIN — Toàn quyền</option></select><p className="text-[11px] text-slate-500 mt-1.5">Thay đổi có hiệu lực khi người dùng đăng nhập lại.</p></div>
                            <div><label className="text-xs text-slate-400">Avatar URL</label><div className="flex gap-2"><input className="flex-1 bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" value={editStaffModal.avatar || ''} onChange={e => setEditStaffModal({ ...editStaffModal, avatar: e.target.value })} placeholder="https://..." /><div className="w-9 h-9 bg-slate-700 rounded overflow-hidden flex items-center justify-center">{editStaffModal.avatar ? <img src={editStaffModal.avatar} className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-slate-500" />}</div></div></div>
                            <div><label className="text-xs text-slate-400">% Hoa hồng mặc định</label><input type="number" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" value={editStaffModal.commissionRate || 15} onChange={e => setEditStaffModal({ ...editStaffModal, commissionRate: e.target.value })} /></div>
                            <div className="flex gap-3 pt-2"><button onClick={() => setEditStaffModal(null)} className="flex-1 py-2 bg-slate-800 text-slate-300 rounded text-sm">Hủy</button><button onClick={handleEditStaff} className="flex-1 py-2 bg-blue-600 text-white rounded text-sm">Lưu thay đổi</button></div>
                        </div>
                    </div>
                </div>
            )}

            {/* COMMISSION MODAL */}
            {commissionModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-4xl bg-slate-900 border border-slate-700 rounded-2xl p-4 sm:p-6 shadow-2xl max-h-[94vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2"><Calculator className="text-emerald-500" /> Bảng tính lương: {commissionModal.name}</h3>
                            <button onClick={() => setCommissionModal(null)} className="text-slate-400 hover:text-white"><X size={24} /></button>
                        </div>

                        <div ref={payrollCardRef} className="payroll-share-card mb-6">
                            <div className="payroll-share-topline"><span>HOPETEAM • PAYROLL STATEMENT</span><span>{currentMonth}</span></div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-5 mt-5">
                                <div className="w-20 h-20 rounded-full overflow-hidden border border-white/15 bg-white/5 shrink-0 mx-auto sm:mx-0">{commissionModal.avatar ? <img src={commissionModal.avatar} alt={commissionModal.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-2xl font-bold">{commissionModal.name.slice(0, 2).toUpperCase()}</div>}</div>
                                <div className="flex-1 min-w-0 text-center sm:text-left"><div className="flex flex-wrap items-center justify-center sm:justify-start gap-2"><h2 className="text-3xl font-bold text-white truncate">{commissionModal.name}</h2><RoleBadge role={commissionModal.accessRole || 'member'} /></div><p className="text-sm text-slate-400 mt-1">{commissionModal.role || 'HopeTeam Member'}</p></div>
                                <div className="text-center sm:text-right"><p className="text-[10px] uppercase tracking-[.18em] text-slate-500">Thu nhập tháng</p><p className="text-3xl sm:text-4xl font-extrabold text-[#DEDBC8] mt-1">{formatCurrency(commissionVND)}</p></div>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-6">
                                <div className="payroll-metric"><span>Tổng kênh</span><strong>{staffChannels.length}</strong></div>
                                <div className="payroll-metric"><span>Kênh BKT</span><strong>{bktChannelCount}</strong></div>
                                <div className="payroll-metric"><span>Task hoàn thành</span><strong>{completedTaskCount}</strong></div>
                                <div className="payroll-metric"><span>Doanh thu tháng</span><strong>{formatUSD(totalRevenueUSD)}</strong></div>
                            </div>
                            <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-white/10"><p className="text-[10px] text-white/35">Big Hopes, Bigger Future</p><button data-share-controls="true" disabled={isExportingPayroll} onClick={sharePayrollCard} className="px-3 py-2 bg-white text-black rounded-lg text-xs font-bold flex items-center gap-2 disabled:opacity-60"><Share2 size={14} /> {isExportingPayroll ? 'Đang tạo ảnh...' : 'Chia sẻ ảnh'}</button></div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-slate-800 p-3 rounded-lg border border-slate-700">
                                <label className="text-xs text-slate-400 block mb-1">Tháng tính lương</label>
                                <input type="month" value={currentMonth} onChange={e => setCurrentMonth(e.target.value)} className="bg-transparent text-white font-bold outline-none w-full" />
                            </div>
                            <div className="bg-slate-800 p-3 rounded-lg border border-slate-700">
                                <label className="text-xs text-slate-400 block mb-1">Tỷ lệ hoa hồng chung (%)</label>
                                <div className="flex items-center gap-2">
                                    <Percent size={16} className="text-emerald-500" />
                                    <input type="number" value={rateInput} onChange={e => setRateInput(e.target.value)} className="bg-transparent text-white font-bold outline-none w-full" />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3 mb-6">
                            <div className="flex items-center justify-between text-sm font-semibold text-slate-400">
                                <span>Chi tiết doanh thu kênh (Thực nhận từ T{revenueMonth.split('-')[1]})</span>
                                <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-1 rounded">Payout Cycle: 23rd hàng tháng</span>
                            </div>
                            {staffChannels.length === 0 ? <p className="text-slate-500 italic">Nhân viên này chưa quản lý kênh nào.</p> : staffChannels.map(ch => {
                                const rev = getRevenueForChannel(ch.id, revenueMonth);
                                return (
                                    <div key={ch.id} className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                                        <div className="flex items-center gap-3">
                                            <Youtube size={20} className="text-red-500" />
                                            <span className="text-white text-sm font-medium">{ch.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-500">Doanh thu:</span>
                                            <span className={`font-mono font-bold ${rev ? 'text-emerald-400' : 'text-slate-600'}`}>
                                                {rev ? formatUSD(rev) : '(Chưa nhập)'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="bg-emerald-900/20 p-4 rounded-xl border border-emerald-500/30 flex justify-between items-center mb-6">
                            <div>
                                <p className="text-emerald-400 text-xs uppercase font-bold">Tổng lương thực nhận (VND)</p>
                                <p className="text-3xl font-bold text-white">{formatCurrency(commissionVND)}</p>
                                <p className="text-10px text-slate-500 mt-1">
                                    ~ {formatUSD(commissionUSD)} (Tỷ giá: {formatCurrency(exchangeRate)})
                                </p>
                            </div>
                            <div className="text-right text-xs text-slate-400">
                                * Tự động tính dựa trên doanh thu USD<br /> đã nhập ở phần Kênh.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- SETTINGS VIEW ---
const SettingsView = ({ apiKey, setApiKey, exchangeRate, setExchangeRate, introducedWebsites = [], setIntroducedWebsites, competitorHighlightDays, setCompetitorHighlightDays, competitorAccess = [], setCompetitorAccess, allStaff = [], firebaseUser }) => {
    const [geminiKeys, setGeminiKeys] = useState(['']);
    const [youtubeKeys, setYoutubeKeys] = useState(['']);
    const [dbExchangeRate, setDbExchangeRate] = useState('');
    const [localWebsites, setLocalWebsites] = useState(introducedWebsites);

    // Competitor tracking config
    const [localHighlightDays, setLocalHighlightDays] = useState(competitorHighlightDays || 7);

    // Zalo Bot config
    const [zaloBotSecretToken, setZaloBotSecretToken] = useState('');
    const [localCompetitorAccess, setLocalCompetitorAccess] = useState(competitorAccess || []);

    // Gemini API test state
    const [geminiTestStatus, setGeminiTestStatus] = useState(null); // null | 'testing' | {results: [{key, ok, error?, model?}]}

    const handleTestGeminiKeys = async () => {
        const keysToTest = geminiKeys.map(k => k.trim()).filter(k => k);
        if (keysToTest.length === 0) return alert('Chưa có Gemini API Key nào để test!');
        setGeminiTestStatus('testing');
        const results = [];
        for (const key of keysToTest) {
            try {
                const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent?key=${key}`;
                const resp = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ contents: [{ parts: [{ text: 'Respond with exactly: OK' }] }], generationConfig: { maxOutputTokens: 10 } })
                });
                if (resp.ok) {
                    const data = await resp.json();
                    const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
                    results.push({ key: key.slice(0, 8) + '...', ok: true, model: 'gemini-2.5-flash', reply: text.trim().slice(0, 30) });
                } else {
                    const errData = await resp.json().catch(() => ({}));
                    const errMsg = errData?.error?.message || `HTTP ${resp.status}`;
                    results.push({ key: key.slice(0, 8) + '...', ok: false, error: errMsg });
                }
            } catch (e) {
                results.push({ key: key.slice(0, 8) + '...', ok: false, error: e.message });
            }
        }
        setGeminiTestStatus({ results });
    };

    useEffect(() => {
        setLocalWebsites(introducedWebsites);
    }, [introducedWebsites]);

    useEffect(() => {
        if (!db) return;
        const fetchSettings = async () => {
            try {
                const docSnap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'global_config'));
                if (docSnap.exists()) {
                    const settingsData = docSnap.data();
                    if (settingsData.geminiApiKey) {
                        setGeminiKeys(settingsData.geminiApiKey.split(',').map(k => k.trim()));
                    }
                    if (settingsData.youtubeApiKey) {
                        setYoutubeKeys(settingsData.youtubeApiKey.split(',').map(k => k.trim()));
                    }
                    if (settingsData.exchangeRate) {
                        setDbExchangeRate(settingsData.exchangeRate);
                    }
                    if (settingsData.competitorHighlightDays) setLocalHighlightDays(settingsData.competitorHighlightDays);
                    if (settingsData.competitorAccess) setLocalCompetitorAccess(settingsData.competitorAccess);
                    if (settingsData.zaloBotSecretToken) setZaloBotSecretToken(settingsData.zaloBotSecretToken);
                }
            } catch (e) { console.error("Error fetching settings", e); }
        };
        fetchSettings();
    }, [db]);

    const handleSaveKey = async () => {
        const cleanGemini = geminiKeys.map(k => k.trim()).filter(k => k).join(',');
        const cleanYoutube = youtubeKeys.map(k => k.trim()).filter(k => k).join(',');
        const cleanWebsites = localWebsites.filter(w => w.title.trim() && w.url.trim()); // Only keep items with title & url

        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'global_config'), {
            geminiApiKey: cleanGemini,
            youtubeApiKey: cleanYoutube,
            exchangeRate: Number(dbExchangeRate),
            introducedWebsites: cleanWebsites,
            competitorHighlightDays: Number(localHighlightDays),
            competitorAccess: localCompetitorAccess,
            zaloBotSecretToken: zaloBotSecretToken.trim()
        }, { merge: true });

        setApiKey(cleanGemini);
        setExchangeRate(Number(dbExchangeRate));
        if (setIntroducedWebsites) setIntroducedWebsites(cleanWebsites);
        if (setCompetitorHighlightDays) setCompetitorHighlightDays(Number(localHighlightDays));
        if (setCompetitorAccess) setCompetitorAccess(localCompetitorAccess);
        alert("Đã lưu Cấu hình vào Cloud (Vĩnh viễn)!");
    };

    const updateKey = (setter, keys, index, value) => {
        const newKeys = [...keys];
        newKeys[index] = value;
        setter(newKeys);
    }

    const addKey = (setter, keys) => setter([...keys, '']);
    const removeKey = (setter, keys, index) => setter(keys.filter((_, i) => i !== index));

    const handleAddWebsite = () => {
        setLocalWebsites([...localWebsites, { title: '', url: '', displayText: 'Mở trang web', logoUrl: '' }]);
    };

    const handleUpdateWebsite = (index, field, value) => {
        const updated = [...localWebsites];
        updated[index] = { ...updated[index], [field]: value };
        setLocalWebsites(updated);
    };

    const handleRemoveWebsite = (index) => {
        setLocalWebsites(localWebsites.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-8 pb-10 animate-in fade-in duration-500">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20 ring-1 ring-white/10">
                            <Settings className="text-white" size={24} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white tracking-tight">Cài đặt Hệ thống</h2>
                            <p className="text-xs text-slate-500 mt-0.5">Cấu hình được lưu trữ vĩnh viễn trên Cloud</p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 text-emerald-400 text-xs bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
                        <Cloud size={14} /> Đồng bộ Cloud
                    </div>
                </div>
            </div>

            {/* GRID LAYOUT */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* GEMINI API KEYS CARD */}
                <div className="bg-slate-900/60 backdrop-blur-md border border-white/5 rounded-2xl shadow-2xl ring-1 ring-white/5 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-800/80 bg-gradient-to-r from-purple-500/5 to-transparent flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                                <Sparkles size={18} className="text-purple-400" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-white">Gemini API Keys</h3>
                                <p className="text-[10px] text-slate-500">Dùng cho AI phân tích & đề xuất</p>
                            </div>
                        </div>
                        <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">{geminiKeys.filter(k => k.trim()).length} keys</span>
                    </div>
                    <div className="p-6 space-y-3">
                        {geminiKeys.map((k, i) => (
                            <div key={i} className="flex gap-2 items-center group">
                                <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-500 border border-slate-700 shrink-0">{i + 1}</div>
                                <input type="password" value={k} onChange={(e) => updateKey(setGeminiKeys, geminiKeys, i, e.target.value)} className="flex-1 bg-slate-800/80 border border-slate-700/80 rounded-xl px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-purple-500/40 focus:border-purple-500/40 outline-none transition-all placeholder:text-slate-600" placeholder={`Gemini Key #${i + 1}`} />
                                <button onClick={() => removeKey(setGeminiKeys, geminiKeys, i)} className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                            </div>
                        ))}
                        <button onClick={() => addKey(setGeminiKeys, geminiKeys)} className="w-full py-2.5 rounded-xl border border-dashed border-slate-700 text-xs font-medium text-slate-500 hover:text-purple-400 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all flex items-center justify-center gap-1.5"><Plus size={14} /> Thêm Key mới</button>
                        <button onClick={handleTestGeminiKeys} disabled={geminiTestStatus === 'testing'} className="w-full mt-2 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-fuchsia-600/20 border border-purple-500/30 text-xs font-bold text-purple-300 hover:from-purple-600/30 hover:to-fuchsia-600/30 hover:text-purple-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            {geminiTestStatus === 'testing' ? <><RefreshCw size={14} className="animate-spin" /> Đang kiểm tra...</> : <><Wifi size={14} /> Test API Keys</>}
                        </button>
                        {geminiTestStatus && geminiTestStatus !== 'testing' && geminiTestStatus.results && (
                            <div className="mt-3 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                {geminiTestStatus.results.map((r, i) => (
                                    <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border text-xs ${r.ok ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${r.ok ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                            {r.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`font-bold font-mono ${r.ok ? 'text-emerald-300' : 'text-red-300'}`}>{r.key}</p>
                                            <p className={`mt-0.5 ${r.ok ? 'text-emerald-400/70' : 'text-red-400/70'}`}>
                                                {r.ok ? `✓ Hoạt động tốt — ${r.model}` : `✗ ${r.error}`}
                                            </p>
                                        </div>
                                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${r.ok ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                                            {r.ok ? 'PASS' : 'FAIL'}
                                        </span>
                                    </div>
                                ))}
                                <p className="text-[10px] text-slate-500 text-center pt-1">Kết quả test chỉ mang tính tham khảo tại thời điểm kiểm tra</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* YOUTUBE API KEYS CARD */}
                <div className="bg-slate-900/60 backdrop-blur-md border border-white/5 rounded-2xl shadow-2xl ring-1 ring-white/5 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-800/80 bg-gradient-to-r from-red-500/5 to-transparent flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-red-500/10 flex items-center justify-center border border-red-500/20">
                                <Youtube size={18} className="text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-white">YouTube Data API Keys</h3>
                                <p className="text-[10px] text-slate-500">Dùng để truy vấn dữ liệu kênh & video</p>
                            </div>
                        </div>
                        <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">{youtubeKeys.filter(k => k.trim()).length} keys</span>
                    </div>
                    <div className="p-6 space-y-3">
                        {youtubeKeys.map((k, i) => (
                            <div key={i} className="flex gap-2 items-center group">
                                <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-500 border border-slate-700 shrink-0">{i + 1}</div>
                                <input type="password" value={k} onChange={(e) => updateKey(setYoutubeKeys, youtubeKeys, i, e.target.value)} className="flex-1 bg-slate-800/80 border border-slate-700/80 rounded-xl px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-red-500/40 focus:border-red-500/40 outline-none transition-all placeholder:text-slate-600" placeholder={`YouTube Key #${i + 1}`} />
                                <button onClick={() => removeKey(setYoutubeKeys, youtubeKeys, i)} className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                            </div>
                        ))}
                        <button onClick={() => addKey(setYoutubeKeys, youtubeKeys)} className="w-full py-2.5 rounded-xl border border-dashed border-slate-700 text-xs font-medium text-slate-500 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 transition-all flex items-center justify-center gap-1.5"><Plus size={14} /> Thêm Key mới</button>
                    </div>
                </div>

                {/* EXCHANGE RATE CARD */}
                <div className="bg-slate-900/60 backdrop-blur-md border border-white/5 rounded-2xl shadow-2xl ring-1 ring-white/5 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-800/80 bg-gradient-to-r from-emerald-500/5 to-transparent flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                            <DollarSign size={18} className="text-emerald-400" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-white">Tỷ giá & Tài chính</h3>
                            <p className="text-[10px] text-slate-500">Cấu hình tỷ giá để tính KPI doanh thu</p>
                        </div>
                    </div>
                    <div className="p-6">
                        <label className="text-xs font-medium text-slate-400 mb-2 block">Tỷ giá USD → VND</label>
                        <div className="relative">
                            <input type="number" value={dbExchangeRate} onChange={(e) => setDbExchangeRate(e.target.value)} className="w-full bg-slate-800/80 border border-slate-700/80 rounded-xl px-4 py-3 text-white text-lg font-bold focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/40 outline-none transition-all placeholder:text-slate-600" placeholder="25000" />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500">VND/USD</span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1"><Calculator size={10} /> Dùng để quy đổi doanh thu YouTube (USD) sang VND.</p>
                    </div>
                </div>

                {/* COMPETITOR TRACKING CONFIG CARD */}
                <div className="bg-slate-900/60 backdrop-blur-md border border-white/5 rounded-2xl shadow-2xl ring-1 ring-white/5 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-800/80 bg-gradient-to-r from-amber-500/5 to-transparent flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                            <Eye size={18} className="text-amber-400" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-white">Theo Dõi Đối Thủ</h3>
                            <p className="text-[10px] text-slate-500">Cấu hình vòng đời video & quyền truy cập</p>
                        </div>
                    </div>
                    <div className="p-6 space-y-5">
                        <div>
                            <label className="text-xs font-medium text-slate-400 mb-2 block">Số ngày Highlight Video</label>
                            <input type="number" min="1" value={localHighlightDays} onChange={(e) => setLocalHighlightDays(e.target.value)} className="w-full bg-slate-800/80 border border-slate-700/80 rounded-xl px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 outline-none transition-all" placeholder="7" />
                            <p className="text-[10px] text-slate-500 mt-1.5">Video vượt quá số ngày này sẽ được tô sáng màu Tím.</p>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-400 mb-2 block">Cấp quyền nhân viên</label>
                            <div className="p-3 bg-slate-800/50 border border-slate-700/80 rounded-xl space-y-1.5 max-h-36 overflow-y-auto custom-scrollbar">
                                {allStaff.length === 0 ? <p className="text-xs text-slate-500 italic py-2 text-center">Chưa có staff nào</p> : allStaff.map(s => {
                                    const isChecked = localCompetitorAccess.includes(s.name);
                                    return (
                                        <label key={s.id} className={`flex items-center gap-2.5 text-sm cursor-pointer px-3 py-2 rounded-lg transition-all ${isChecked ? 'bg-amber-500/10 text-white border border-amber-500/20' : 'text-slate-400 hover:bg-slate-700/50 border border-transparent'}`}>
                                            <input type="checkbox" checked={isChecked} onChange={(e) => {
                                                if (e.target.checked) setLocalCompetitorAccess([...localCompetitorAccess, s.name]);
                                                else setLocalCompetitorAccess(localCompetitorAccess.filter(name => name !== s.name));
                                            }} className="rounded bg-slate-800 border-slate-700 text-amber-500 focus:ring-amber-500/50" />
                                            <User size={14} className={isChecked ? 'text-amber-400' : 'text-slate-600'} />
                                            <span className="font-medium">{s.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1.5"><Shield size={10} className="inline mr-0.5" />Admin luôn có quyền. Nhân sự được chọn sẽ thấy mục Theo dõi đối thủ.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* NEW SECTION: INTRODUCED WEBSITES */}
            <div className="mt-6 border-t border-slate-800 pt-6">
                <label className="text-sm font-bold text-white mb-4 block flex items-center gap-2"><Globe className="text-purple-400" size={18} /> Quản lý Trang Web Đề Xuất (Trung tâm chỉ huy)</label>
                <div className="space-y-4">
                    {localWebsites.map((site, i) => (
                        <div key={i} className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 relative group">
                            <button onClick={() => handleRemoveWebsite(i)} className="absolute top-2 right-2 text-slate-500 hover:text-red-400"><X size={16} /></button>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <input type="text" value={site.title} onChange={(e) => handleUpdateWebsite(i, 'title', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Tiêu đề trang web..." />
                                </div>
                                <div>
                                    <input type="text" value={site.url} onChange={(e) => handleUpdateWebsite(i, 'url', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Link (URL)..." />
                                </div>
                                <div>
                                    <input type="text" value={site.displayText} onChange={(e) => handleUpdateWebsite(i, 'displayText', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Text nút bấm (vd: Mở trang)..." />
                                </div>
                                <div>
                                    <input type="text" value={site.logoUrl} onChange={(e) => handleUpdateWebsite(i, 'logoUrl', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm" placeholder="Link Ảnh Logo/Icon (tuỳ chọn)..." />
                                </div>
                            </div>
                        </div>
                    ))}
                    <button onClick={handleAddWebsite} className="text-sm font-medium text-purple-400 hover:text-purple-300 flex items-center gap-1"><Plus size={16} /> Thêm Trang Web mới</button>
                </div>
            </div>

            <HopeLabSettings firebaseUser={firebaseUser} />

            {/* SAVE BUTTON - STICKY */}
            <div className="sticky bottom-4 z-30">
                <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-[0_-10px_40px_-10px_rgba(0,0,0,0.5)] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></div>
                        <p className="text-xs text-slate-400">Thay đổi chưa được lưu sẽ mất khi rời trang</p>
                    </div>
                    <Button onClick={handleSaveKey} icon={Save} className="px-8 shadow-xl shadow-blue-500/20">Lưu tất cả Cấu hình</Button>
                </div>
            </div>
        </div>
    );
};

const GoogleLoginScreen = ({ onLogin, onCopyLoginLink, embeddedBrowser = '', authStatus = 'signed_out', errorMessage = '' }) => (
    <div className="login-hero min-h-screen flex items-center justify-center p-4 sm:p-6">
        <video className="login-hero-video" muted autoPlay loop playsInline preload="metadata" aria-hidden="true" src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260405_170732_8a9ccda6-5cff-4628-b164-059c500a2b41.mp4" />
        <div className="login-hero-shade" />
        <div className="login-card liquid-glass w-full max-w-lg rounded-[2rem] p-6 sm:p-10 text-center">
            <div className="login-brand"><span className="login-brand-mark"><Shield size={18} /></span><span>HopeTeam</span><span className="login-private">Internal</span></div>
            <p className="login-eyebrow">TEAM OPERATIONS</p>
            <h1 className="login-title">Big Hopes,<br /><em>Bigger Future</em></h1>
            <p className="text-white/65 text-sm sm:text-base mt-5 leading-relaxed max-w-md mx-auto">Đây là công cụ nội bộ của HopeTeam. Nếu bạn là thành viên trong team, hãy đăng nhập bằng Google và chờ admin liên kết tài khoản với hồ sơ nhân sự.</p>
            {authStatus === 'pending' && <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-300 text-sm">Yêu cầu đang chờ admin liên kết với hồ sơ nhân sự.</div>}
            {embeddedBrowser && <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/25 rounded-xl text-amber-200 text-sm leading-relaxed"><strong>Không thể đăng nhập trong {embeddedBrowser}.</strong><br />Google chặn đăng nhập từ trình duyệt nhúng. Hãy nhấn dấu <strong>•••</strong> và chọn mở bằng Safari hoặc Chrome.</div>}
            {errorMessage && <div className="mt-6 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">{errorMessage}</div>}
            <button onClick={embeddedBrowser ? onCopyLoginLink : onLogin} disabled={authStatus === 'loading'} className="google-login-button"><span className="google-g">G</span><span>{authStatus === 'loading' ? 'Đang xác thực...' : embeddedBrowser ? 'Sao chép link mở Safari/Chrome' : 'Tiếp tục với Google'}</span><ArrowRight size={18} /></button>
            <p className="text-[11px] text-white/30 mt-5">Quyền truy cập được kiểm soát bởi Google Identity & HopeTeam Admin</p>
        </div>
    </div>
);

const RoleBadge = ({ role }) => {
    const resolved = role === 'admin' ? 'ADMIN' : role === 'staff' ? 'STAFF' : 'MEMBER';
    const style = role === 'admin' ? 'role-badge-admin' : role === 'staff' ? 'role-badge-staff' : 'role-badge-member';
    return <span className={`role-badge ${style}`}>{resolved}</span>;
};

const AccessApprovalPanel = ({ requests = [], staff = [], onApprove, onReject, onRefresh, loading }) => {
    const [selections, setSelections] = useState({});
    if (!requests.length) return <Card className="mb-8"><div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"><div className="flex items-center gap-3"><Shield size={20} className="text-emerald-400" /><div><p className="text-white font-semibold">Quyền truy cập Google</p><p className="text-sm text-slate-500">Không có tài khoản nào đang chờ duyệt.</p></div></div><Button variant="secondary" icon={RefreshCw} disabled={loading} onClick={onRefresh}>Kiểm tra yêu cầu mới</Button></div></Card>;
    return <Card className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5"><div className="flex items-center gap-3"><ShieldAlert className="text-amber-400" /><div><h3 className="text-white font-semibold">Tài khoản chờ gán nhân sự</h3><p className="text-sm text-slate-500">Chỉ duyệt khi email Google đúng với thành viên tương ứng.</p></div></div><Button variant="secondary" icon={RefreshCw} disabled={loading} onClick={onRefresh}>Tải lại</Button></div>
        <div className="space-y-3">{requests.map(request => {
            const selected = selections[request.id] || { staffId: '' };
            return <div key={request.id} className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_auto] gap-3 items-center p-3 rounded-xl bg-white/5 border border-white/5">
                <div className="min-w-0"><p className="text-white text-sm font-medium truncate">{request.displayName || request.email || 'Tài khoản Google chưa có tên'}</p><p className="text-slate-500 text-xs truncate">{request.email || `UID: ${request.googleUid || request.id}`}</p></div>
                <select value={selected.staffId} onChange={e => setSelections({ ...selections, [request.id]: { staffId: e.target.value } })} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"><option value="">Chọn nhân sự</option>{staff.map(person => <option key={person.id} value={person.id}>{person.name}</option>)}</select>
                <div className="grid grid-cols-2 gap-2"><Button disabled={!selected.staffId || loading} onClick={() => onApprove(request.id, selected.staffId)}>Duyệt</Button><Button variant="secondary" disabled={loading} onClick={() => onReject(request.id)} className="text-red-400 border-red-500/20 hover:bg-red-500/10">Từ chối</Button></div>
            </div>;
        })}</div>
    </Card>;
};

const Sidebar = ({ activeTab, setActiveTab, isMobileOpen, setIsMobileOpen, userRole, currentUserData, onLogout, competitorAccess = [] }) => {
    useEffect(() => {
        if (!isMobileOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previousOverflow; };
    }, [isMobileOpen]);

    const menuItems = [
        { id: 'dashboard', label: 'Trung tâm Chỉ huy', icon: LayoutDashboard, visible: true },
        { id: 'tasks', label: 'Quản lý Task', icon: ListTodo, visible: true },
        { id: 'unused-books', label: 'Kho Sách Chờ', icon: BookOpen, visible: userRole === 'admin' || userRole === 'staff' },
        { id: 'competitors', label: 'Theo dõi đối thủ', icon: Eye, visible: userRole === 'admin' || (currentUserData && competitorAccess.includes(currentUserData.name)) },
        { id: 'revenue-board', label: 'Bảng Doanh Thu', icon: DollarSign, visible: true },
        { id: 'channels', label: 'Quản lý Kênh', icon: Youtube, visible: true },
        { id: 'resources', label: 'Kho Tài nguyên', icon: Folder, visible: true },
        { id: 'nas', label: 'Kho NAS', icon: HardDrive, visible: true },
        { id: 'voice', label: 'Phòng họp', icon: Headphones, visible: true },
        { id: 'team-fund', label: 'Quỹ Nội Bộ', icon: PiggyBank, visible: true },
        { id: 'channel-health', label: 'Sức khỏe Hệ Thống', icon: ShieldAlert, visible: true },
        { id: 'staff', label: 'Nhân sự & Hoa hồng', icon: Users, visible: userRole === 'admin' },
        { id: 'settings', label: 'Cài đặt', icon: Settings, visible: userRole === 'admin' },
    ].filter(item => item.visible);

    return (
        <>
            {isMobileOpen && <div className="mobile-menu-backdrop fixed inset-0 bg-black/70 md:hidden backdrop-blur-sm" onClick={() => setIsMobileOpen(false)} />}
            <div className={`app-sidebar fixed md:sticky md:top-0 md:h-screen inset-y-0 left-0 w-72 liquid-glass border-r border-white/5 transform transition-transform duration-300 ease-in-out flex flex-col ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
                <div className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg shadow-black/30 ring-1 ring-white/20">
                            <BookOpen className="text-black" size={20} />
                        </div>
                        <div>
                            <span className="font-bold text-xl text-white tracking-tight block">HopeTeam</span>
                            <span className="text-[10px] text-slate-400 font-medium tracking-widest uppercase">Big Hopes, Bigger Future</span>
                        </div>
                    </div>
                    <button onClick={() => setIsMobileOpen(false)} className="md:hidden text-slate-400 hover:text-white"><X size={24} /></button>
                </div>

                <div className="px-6 py-4">
                    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/5 backdrop-blur-md">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs text-black font-bold shadow-lg ring-1 ring-white/20 bg-[#DEDBC8] overflow-hidden">
                            {currentUserData?.avatar ? <img src={currentUserData.avatar} alt={currentUserData.name} className="w-full h-full object-cover" /> : (currentUserData?.name || (userRole === 'admin' ? 'Admin' : 'Member')).slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Current Session</p>
                            <div className="flex items-center gap-2 min-w-0"><p className="text-sm font-bold text-white truncate">{currentUserData?.name || (userRole === 'admin' ? 'Administrator' : 'Team Member')}</p><RoleBadge role={userRole} /></div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 px-4 py-2 space-y-1 overflow-y-auto custom-scrollbar">
                    {menuItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => { setActiveTab(item.id); setIsMobileOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-300 group ${activeTab === item.id
                                ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/15'
                                : 'text-slate-400 hover:bg-white/5 hover:text-white hover:pl-5'}`}
                        >
                            <div className={`p-1.5 rounded-lg transition-colors ${activeTab === item.id ? 'bg-[#DEDBC8] text-black' : 'text-slate-500 group-hover:text-slate-300'}`}>
                                <item.icon size={18} />
                            </div>
                            <span className="font-medium text-sm tracking-wide">{item.label}</span>
                            {activeTab === item.id && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#DEDBC8] shadow-[0_0_10px_rgba(222,219,200,0.65)]"></div>}
                        </button>
                    ))}
                </div>

                <div className="p-4 border-t border-white/5">
                    <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all text-xs font-bold border border-transparent hover:border-red-500/20">
                        <LogOut size={16} /> ĐĂNG XUẤT
                    </button>
                </div>
            </div>
        </>
    );
};

// --- UNUSED BOOKS VIEW ---
const UnusedBooksView = ({ unusedBooks, onAddBook, onDeleteBook, onUpdateBook }) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null); // For Edit
    const [searchTerm, setSearchTerm] = useState('');

    // Bulk Add State
    const [bulkText, setBulkText] = useState('');
    const [isBulkMode, setIsBulkMode] = useState(false);

    const filteredBooks = unusedBooks.filter(book =>
        book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (book.link && book.link.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleSingleAdd = async (e) => {
        e.preventDefault();
        const title = e.target.title.value;
        const link = e.target.link.value;
        await onAddBook({ title, link });
        setIsAddModalOpen(false);
    };

    const handleBulkAdd = async () => {
        if (!bulkText.trim()) return;
        const lines = bulkText.split('\n');
        let count = 0;
        for (const line of lines) {
            if (!line.trim()) continue;
            const parts = line.split('|');
            if (parts.length >= 2) {
                const title = parts[0].trim();
                const link = parts.slice(1).join('|').trim(); // Join back in case link has |
                if (title && link) {
                    await onAddBook({ title, link });
                    count++;
                }
            }
        }
        alert(`Đã thêm thành công ${count} sách!`);
        setBulkText('');
        setIsAddModalOpen(false);
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (selectedBook) {
            await onUpdateBook(selectedBook.id, { title: selectedBook.title, link: selectedBook.link });
            setSelectedBook(null);
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2"><BookOpen className="text-pink-500" /> Kho Sách Chờ</h2>
                <Button onClick={() => { setIsBulkMode(false); setIsAddModalOpen(true); }} icon={Plus}>Thêm Sách Mới</Button>
            </div>

            {/* SEARCH & FILTER */}
            <div className="flex gap-4 mb-6">
                <div className="app-search flex-1 flex items-center px-4 py-2.5 max-w-md">
                    <Search size={18} className="text-slate-500 mr-3" />
                    <input
                        type="text"
                        placeholder="Tìm kiếm sách..."
                        className="app-search-input bg-transparent border-none outline-none text-white text-sm w-full"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="ml-auto">
                    <Button variant="secondary" onClick={() => { setIsBulkMode(true); setIsAddModalOpen(true); }} icon={FileText}>Nhập Hàng Loạt (Bulk Upload)</Button>
                </div>
            </div>

            {/* BOOKS GRID/LIST */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBooks.length > 0 ? filteredBooks.map(book => (
                    <div key={book.id} className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl hover:border-pink-500/30 transition-all group relative">
                        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 flex gap-2 transition-opacity">
                            <button onClick={() => setSelectedBook(book)} className="p-1.5 rounded bg-slate-800 text-blue-400 hover:text-white"><Edit2 size={14} /></button>
                            <button onClick={() => onDeleteBook(book.id, book.title)} className="p-1.5 rounded bg-slate-800 text-red-400 hover:text-white"><Trash2 size={14} /></button>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center text-pink-500 shrink-0">
                                <BookOpen size={20} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h4 className="text-white font-medium text-sm line-clamp-2 md:line-clamp-1" title={book.title}>{book.title}</h4>
                                <a href={book.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline truncate block mt-1 flex items-center gap-1">
                                    <LinkIcon size={10} /> {book.link}
                                </a>
                                <p className="text-[10px] text-slate-500 mt-2 font-mono">Added: {timeAgo(book.createdAt?.toDate())}</p>
                            </div>
                        </div>
                    </div>
                )) : (
                    <div className="col-span-full py-12 text-center text-slate-500 border border-dashed border-slate-800 rounded-xl">
                        <BookOpen size={48} className="mx-auto mb-3 opacity-20" />
                        <p>Chưa có sách nào trong kho.</p>
                    </div>
                )}
            </div>

            {/* ADD/BULK MODAL */}
            {isAddModalOpen && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-white">{isBulkMode ? 'Nhập Sách Hàng Loạt' : 'Thêm Sách Mới'}</h3>
                            <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-white"><X size={24} /></button>
                        </div>

                        {isBulkMode ? (
                            <div className="space-y-4">
                                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                    <p className="text-xs text-blue-400 font-bold mb-1">Định dạng nhập liệu:</p>
                                    <code className="text-[10px] text-slate-300 block font-mono bg-black/20 p-2 rounded">
                                        Tên Sách 1 | https://link1.com<br />
                                        Tên Sách 2 | https://link2.com
                                    </code>
                                </div>
                                <textarea
                                    className="w-full h-48 bg-slate-950 border border-slate-700 rounded-lg p-3 text-white text-sm font-mono focus:border-blue-500 outline-none"
                                    placeholder="Paste danh sách vào đây..."
                                    value={bulkText}
                                    onChange={e => setBulkText(e.target.value)}
                                />
                                <Button onClick={handleBulkAdd} className="w-full" icon={Save}>Xử lý & Lưu</Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSingleAdd} className="space-y-4">
                                <div>
                                    <label className="text-xs text-slate-400 mb-1 block">Tên sách</label>
                                    <input name="title" required className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white" />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 mb-1 block">Link Download</label>
                                    <input name="link" required className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white" />
                                </div>
                                <Button type="submit" className="w-full">Thêm Sách</Button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* EDIT MODAL */}
            {selectedBook && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
                        <h3 className="text-lg font-bold text-white mb-4">Chỉnh sửa Sách</h3>
                        <form onSubmit={handleEdit} className="space-y-4">
                            <div>
                                <label className="text-xs text-slate-400 mb-1 block">Tên sách</label>
                                <input
                                    value={selectedBook.title}
                                    onChange={e => setSelectedBook({ ...selectedBook, title: e.target.value })}
                                    required
                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 mb-1 block">Link Download</label>
                                <input
                                    value={selectedBook.link}
                                    onChange={e => setSelectedBook({ ...selectedBook, link: e.target.value })}
                                    required
                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setSelectedBook(null)} className="flex-1 py-2 bg-slate-800 text-slate-300 rounded text-sm hover:bg-slate-700">Hủy</button>
                                <Button type="submit" className="flex-1">Lưu thay đổi</Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
// --- TEAM FUND VIEW ---
const TeamFundView = ({ userRole, revenues, channels, staff, fundTransactions, fundPercentage, exchangeRate, onAddTransaction, onDeleteTransaction }) => {
    const [isAddTxModalOpen, setIsAddTxModalOpen] = useState(false);
    const [newTx, setNewTx] = useState({ title: '', amount: '', type: 'expense', category: 'Food & Drinks', date: new Date().toISOString().split('T')[0] });
    const [selectedMonth, setSelectedMonth] = useState('all');

    const safeRevenues = Array.isArray(revenues) ? revenues : [];

    // Get unique months from revenue data
    const availableMonths = useMemo(() => {
        const months = [...new Set(safeRevenues.map(r => r.month).filter(Boolean))];
        return months.sort((a, b) => b.localeCompare(a));
    }, [safeRevenues]);

    // Filter revenues by selected month
    const filteredRevenues = selectedMonth === 'all' ? safeRevenues : safeRevenues.filter(r => r.month === selectedMonth);

    const totalRevenueUSD = filteredRevenues.reduce((acc, rev) => acc + (parseFloat(rev.amount) || 0), 0);
    const totalRevenueVND = totalRevenueUSD * exchangeRate;
    const totalFundContribution = totalRevenueVND * (fundPercentage / 100);

    const safeTx = Array.isArray(fundTransactions) ? fundTransactions : [];
    const totalExpenses = safeTx.filter(tx => tx.type === 'expense').reduce((acc, tx) => acc + (parseFloat(tx.amount) || 0), 0);
    const totalIncome = safeTx.filter(tx => tx.type === 'income').reduce((acc, tx) => acc + (parseFloat(tx.amount) || 0), 0);
    const currentBalance = totalFundContribution + totalIncome - totalExpenses;

    const categories = [
        { id: 'Food & Drinks', label: 'Ăn uống', icon: Utensils, color: 'bg-pink-500', text: 'text-pink-500' },
        { id: 'Equipment', label: 'Thiết bị', icon: Monitor, color: 'bg-emerald-500', text: 'text-emerald-500' },
        { id: 'Events', label: 'Sự kiện', icon: PartyPopper, color: 'bg-purple-500', text: 'text-purple-500' },
        { id: 'Others', label: 'Khác', icon: Coffee, color: 'bg-slate-500', text: 'text-slate-500' }
    ];

    const getCatIcon = (catId) => categories.find(c => c.id === catId)?.icon || Coffee;
    const getCatColor = (catId) => categories.find(c => c.id === catId)?.color || 'bg-slate-500';
    const getCatText = (catId) => categories.find(c => c.id === catId)?.text || 'text-slate-500';

    // FIX: Only count contribution for staff who actually have revenue
    const staffContributions = staff.map(person => {
        const pChannels = channels.filter(c => c.manager === person.name);
        const pRevUSD = pChannels.reduce((acc, ch) => acc + filteredRevenues.filter(r => r.channelId === ch.id).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), 0);
        // Monthly breakdown for this person
        const monthlyBreakdown = availableMonths.map(month => {
            const monthRevs = safeRevenues.filter(r => r.month === month);
            const mRevUSD = pChannels.reduce((acc, ch) => acc + monthRevs.filter(r => r.channelId === ch.id).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), 0);
            return { month, revUSD: mRevUSD, contributionVND: (mRevUSD * exchangeRate) * (fundPercentage / 100) };
        }).filter(m => m.revUSD > 0);
        return { ...person, revUSD: pRevUSD, contribution: (pRevUSD * exchangeRate) * (fundPercentage / 100), monthlyBreakdown };
    }).sort((a, b) => b.contribution - a.contribution);

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-white tracking-tight">Quản Lý Quỹ Team</h2>
                    <p className="text-slate-400 mt-1">Theo dõi thu chi và ngân sách hoạt động của đội ngũ.</p>
                </div>
                {userRole === 'admin' && <Button onClick={() => setIsAddTxModalOpen(true)} icon={Plus} className="bg-indigo-600 hover:bg-indigo-500">Thêm Giao Dịch</Button>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-4 space-y-6">
                    <div className="bg-gradient-to-br from-slate-900 to-indigo-950/30 border border-slate-800 rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-2xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-all"></div>
                        <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mb-6 shadow-inner ring-1 ring-white/5 border border-slate-700">
                            <PiggyBank className="text-indigo-400" size={32} />
                        </div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Tổng Số Dư Quỹ</p>
                        <div className="flex items-baseline gap-2 mb-8">
                            <span className="text-4xl font-black text-white">{new Intl.NumberFormat('vi-VN').format(currentBalance)}</span>
                            <span className="text-lg font-bold text-indigo-400">VND</span>
                        </div>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-white">Chi tiêu tháng</h3>
                        </div>
                        <div className="space-y-6">
                            {categories.map(cat => {
                                const catSpent = safeTx.filter(tx => tx.type === 'expense' && tx.category === cat.id).reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
                                if (catSpent === 0) return null;
                                return (
                                    <div key={cat.id}>
                                        <div className="flex justify-between items-center mb-2 text-sm"><span className="text-slate-300 flex items-center gap-2"><cat.icon size={14} className={cat.text} /> {cat.label}</span><span className="font-bold text-white">{new Intl.NumberFormat('vi-VN').format(catSpent)}đ</span></div>
                                        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full ${cat.color} rounded-full`} style={{ width: `${(catSpent / totalExpenses) * 100}%` }}></div></div>
                                    </div>
                                );
                            })}
                            {totalExpenses === 0 && <p className="text-center text-slate-500 text-sm py-4">Chưa có chi tiêu.</p>}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-8 space-y-6">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                        <div className="mb-6"><h3 className="text-lg font-bold text-white">Lịch sử giao dịch</h3></div>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                            {safeTx.length > 0 ? safeTx.map(tx => {
                                const isExp = tx.type === 'expense';
                                const Icon = isExp ? getCatIcon(tx.category) : TrendingUp;
                                return (
                                    <div key={tx.id} className="flex items-center gap-4 p-4 rounded-2xl bg-slate-800/30 border border-slate-800/50 hover:bg-slate-800/80 transition-colors group">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-inner ${isExp ? getCatColor(tx.category) : 'bg-emerald-500'} bg-opacity-20`}><Icon size={20} className={isExp ? getCatText(tx.category) : 'text-emerald-500'} /></div>
                                        <div className="flex-1 min-w-0"><h4 className="text-base font-bold text-slate-200 truncate">{tx.title}</h4><p className="text-xs text-slate-500">{tx.date} • {isExp ? tx.category : 'Thu nhập'}</p></div>
                                        <div className="text-right"><p className={`text-base font-bold font-mono ${isExp ? 'text-red-400' : 'text-emerald-400'}`}>{isExp ? '-' : '+'}{new Intl.NumberFormat('vi-VN').format(tx.amount)}đ</p></div>
                                        {userRole === 'admin' && <button onClick={() => onDeleteTransaction(tx.id)} className="ml-2 p-2 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={16} /></button>}
                                    </div>
                                );
                            }) : <div className="text-center py-10 text-slate-500">Chưa có giao dịch nào.</div>}
                        </div>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
                        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2"><Trophy className="text-yellow-500" size={20} /> Bảng Vàng Đóng Góp</h3>
                            <div className="flex items-center gap-2">
                                <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-indigo-500 cursor-pointer">
                                    <option value="all">Tất cả tháng</option>
                                    {availableMonths.map(m => <option key={m} value={m}>Tháng {parseInt(m.split('-')[1])}/{m.split('-')[0]}</option>)}
                                </select>
                                <span className="text-xs text-slate-500 bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700">Trích {fundPercentage}%</span>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {staffContributions.map((person, idx) => (
                                <details key={idx} className="group/detail">
                                    <summary className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/30 border border-slate-700/50 cursor-pointer hover:bg-slate-800/60 transition-colors list-none [&::-webkit-details-marker]:hidden">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${idx === 0 ? 'bg-yellow-500 text-black shadow-lg shadow-yellow-500/20' : idx === 1 ? 'bg-slate-300 text-black' : idx === 2 ? 'bg-amber-700 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>{idx + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 min-w-0"><h4 className="font-bold text-sm text-slate-200 truncate">{person.name}</h4><RoleBadge role={person.accessRole || 'member'} /></div>
                                            <p className="text-[10px] text-slate-500">{formatUSD(person.revUSD)} doanh thu</p>
                                        </div>
                                        <div className="font-mono font-bold text-emerald-400 text-sm shrink-0">+{new Intl.NumberFormat('vi-VN').format(person.contribution)}đ</div>
                                        <ChevronDown size={14} className="text-slate-500 group-open/detail:rotate-180 transition-transform shrink-0" />
                                    </summary>
                                    {person.monthlyBreakdown.length > 0 ? (
                                        <div className="mt-2 ml-11 mr-2 p-3 rounded-xl bg-slate-950/50 border border-slate-800 space-y-2">
                                            <div className="grid grid-cols-3 gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider pb-2 border-b border-slate-800">
                                                <span>Tháng</span><span className="text-right">Doanh Thu</span><span className="text-right">Trích Quỹ</span>
                                            </div>
                                            {person.monthlyBreakdown.map(m => (
                                                <div key={m.month} className="grid grid-cols-3 gap-2 text-xs py-1.5 border-b border-slate-800/30 last:border-0">
                                                    <span className="text-slate-400 font-medium">T{parseInt(m.month.split('-')[1])}/{m.month.split('-')[0]}</span>
                                                    <span className="text-right text-white font-mono">{formatUSD(m.revUSD)}</span>
                                                    <span className="text-right text-emerald-400 font-mono font-bold">+{new Intl.NumberFormat('vi-VN').format(m.contributionVND)}đ</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="mt-2 ml-11 mr-2 p-3 rounded-xl bg-slate-950/50 border border-slate-800 text-xs text-slate-500 text-center">Chưa có doanh thu</div>
                                    )}
                                </details>
                            ))}
                            {staffContributions.length === 0 && <p className="text-center text-slate-500 text-sm py-4">Chưa có dữ liệu đóng góp.</p>}
                        </div>
                    </div>
                </div>
            </div>

            {isAddTxModalOpen && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
                        <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-800/50"><h3 className="font-bold text-white">Thêm Giao Dịch</h3><button onClick={() => setIsAddTxModalOpen(false)} className="text-slate-400 hover:text-white"><X size={20} /></button></div>
                        <form onSubmit={(e) => { e.preventDefault(); onAddTransaction({ ...newTx, amount: parseFloat(newTx.amount) }); setIsAddTxModalOpen(false); }} className="p-6 space-y-4">
                            <div><label className="text-xs text-slate-400 mb-1 block">Tên giao dịch</label><input required value={newTx.title} onChange={e => setNewTx({ ...newTx, title: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none focus:border-indigo-500" /></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-xs text-slate-400 mb-1 block">Số tiền (VND)</label><input required type="number" value={newTx.amount} onChange={e => setNewTx({ ...newTx, amount: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none font-mono" /></div>
                                <div><label className="text-xs text-slate-400 mb-1 block">Ngày</label><input required type="date" value={newTx.date} onChange={e => setNewTx({ ...newTx, date: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none" /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-xs text-slate-400 mb-1 block">Phân loại</label><select value={newTx.type} onChange={e => setNewTx({ ...newTx, type: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none"><option value="expense">Chi tiêu (-)</option><option value="income">Thu nhập (+)</option></select></div>
                                {newTx.type === 'expense' && <div><label className="text-xs text-slate-400 mb-1 block">Danh mục</label><select value={newTx.category} onChange={e => setNewTx({ ...newTx, category: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none">{categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select></div>}
                            </div>
                            <div className="pt-4 flex gap-3"><button type="button" onClick={() => setIsAddTxModalOpen(false)} className="flex-1 py-2.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition font-bold">Hủy</button><button type="submit" className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition font-bold">Lưu giao dịch</button></div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- COMPETITORS VIEW ---
const CompetitorsView = ({ competitors, onAdd, onUpdate, onDelete, canEdit, competitorHighlightDays, youtubeApiKey }) => {
    const [newLink, setNewLink] = useState('');
    const [isFetching, setIsFetching] = useState(false);
    const [isCheckingAll, setIsCheckingAll] = useState(false);
    const [editNoteId, setEditNoteId] = useState(null);
    const [editNoteText, setEditNoteText] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newLink.trim()) return;
        setIsFetching(true);
        const data = await fetchYouTubeVideoRealData(newLink, youtubeApiKey);
        setIsFetching(false);
        if (data) {
            if (data.isDead) {
                alert("Video không tồn tại hoặc đã bị xóa!");
                return;
            }
            await onAdd({
                link: newLink,
                title: data.title,
                channelTitle: data.channelTitle,
                publishedAt: data.publishedAt || '',
                note: '',
                isDead: false
            });
            setNewLink('');
            alert("Đã thêm thành công!");
        }
    };

    const handleCheckLive = async (comp) => {
        setIsFetching(true);
        const data = await fetchYouTubeVideoRealData(comp.link, youtubeApiKey);
        setIsFetching(false);
        if (data && data.isDead) {
            await onUpdate(comp.id, { isDead: true });
            alert("Video này đã chết (Bị khóa/xóa)!");
        } else if (data) {
            await onUpdate(comp.id, { isDead: false, title: data.title, channelTitle: data.channelTitle });
            alert("Video vẫn đang Live!");
        }
    };

    const handleCheckAll = async () => {
        if (!window.confirm("Hệ thống sẽ kiểm tra trạng thái toàn bộ video trong danh sách (sẽ bỏ qua những video đã chết). Việc này có thể mất vài chục giây tùy số lượng. Tiếp tục?")) return;
        setIsCheckingAll(true);
        let countDead = 0;
        let countLive = 0;
        for (const comp of competitors) {
            if (comp.isDead) continue;
            const data = await fetchYouTubeVideoRealData(comp.link, youtubeApiKey);
            if (data && data.isDead) {
                await onUpdate(comp.id, { isDead: true });
                countDead++;
            } else if (data) {
                await onUpdate(comp.id, { isDead: false, title: data.title, channelTitle: data.channelTitle });
                countLive++;
            }
            // Delay for quota friendliness
            await new Promise(r => setTimeout(r, 400));
        }
        setIsCheckingAll(false);
        alert(`Hoàn tất kiểm tra!\n- Video duy trì Live: ${countLive}\n- Phát hiện mới Chết: ${countDead}`);
    };

    const calcDaysLive = (publishedAt) => {
        if (!publishedAt) return 0;
        const pubDate = new Date(publishedAt);
        const now = new Date();
        const diff = Math.floor((now - pubDate) / (1000 * 60 * 60 * 24));
        return diff >= 0 ? diff : 0;
    };

    const filteredCompetitors = competitors.filter(c =>
        (c.title || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (c.channelTitle || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (c.note || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (c.link || '').toLowerCase().includes(searchKeyword.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Eye className="text-purple-500" /> Theo Dõi Video Đối Thủ
                </h2>
                <div className="flex items-center gap-3">
                    <div className="text-xs text-slate-400 bg-slate-900 border border-slate-700 px-3 py-1.5 rounded-lg flex items-center gap-2">
                        Highlight những video sống hơn: <strong className="text-purple-400">{competitorHighlightDays || 7} ngày</strong>
                    </div>
                </div>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
                {canEdit ? (
                    <form onSubmit={handleAdd} className="flex flex-1 gap-2 w-full md:w-auto">
                        <input required value={newLink} onChange={e => setNewLink(e.target.value)} className="w-full md:w-80 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-500 outline-none transition-colors" placeholder="Dán link video YouTube..." />
                        <button type="submit" disabled={isFetching || isCheckingAll} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-bold transition flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap">
                            {isFetching ? <RefreshCw className="animate-spin" size={16} /> : <Plus size={16} />}
                            Thêm
                        </button>
                    </form>
                ) : <div className="flex-1"></div>}
                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto items-center">
                    <div className="relative app-search w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <input type="text" value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)} placeholder="Tìm kiếm theo từ khóa..." className="app-search-input w-full pl-9 pr-3 py-2 text-white text-sm" />
                    </div>
                    {canEdit && (
                        <button onClick={handleCheckAll} disabled={isCheckingAll || isFetching} className="w-full md:w-auto px-4 py-2 bg-slate-800 border border-slate-600 hover:bg-slate-700 text-white text-sm rounded-lg font-bold flex items-center justify-center gap-2 transition disabled:opacity-50 whitespace-nowrap">
                            {isCheckingAll ? <RefreshCw className="animate-spin" size={16} /> : <Activity size={16} />}
                            {isCheckingAll ? 'Đang check...' : 'Kiểm tra toàn bộ'}
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden shadow-xl overflow-x-auto min-h-[400px]">
                <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                        <tr className="border-b border-slate-800 text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-900/80">
                            <th className="p-4 pl-6 font-medium">Video / Channel</th>
                            <th className="p-4 font-medium">Vòng Đời</th>
                            <th className="p-4 font-medium w-64">Ghi Chú Tên Sách</th>
                            <th className="p-4 pr-6 text-right font-medium">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                        {filteredCompetitors.map(comp => {
                            const daysLive = calcDaysLive(comp.publishedAt);
                            const isHighlighted = !comp.isDead && daysLive >= (competitorHighlightDays || 7);
                            return (
                                <tr key={comp.id} className={`transition-colors group ${isHighlighted ? 'bg-purple-900/20 hover:bg-purple-900/30' : 'hover:bg-slate-800/30'} ${comp.isDead ? 'opacity-50 grayscale' : ''}`}>
                                    <td className="p-4 pl-6">
                                        <div className="flex flex-col gap-1">
                                            <a href={comp.link} target="_blank" rel="noopener noreferrer" className={`font-bold text-sm hover:underline truncate block max-w-[300px] ${isHighlighted ? 'text-purple-300' : 'text-white'}`} title={comp.title}>{comp.title || <span className="italic text-slate-500">Đang tải...</span>}</a>
                                            <span className="text-xs text-slate-500 flex items-center gap-1"><Youtube size={10} className="text-red-500" /> {comp.channelTitle || 'Unknown'}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 align-middle">
                                        {comp.isDead ? (
                                            <span className="px-2 py-1 bg-red-500/10 text-red-500 border border-red-500/20 rounded font-bold text-xs uppercase">Đã Chết</span>
                                        ) : (
                                            <span className={`font-bold text-sm ${isHighlighted ? 'text-purple-400' : 'text-emerald-400'}`}>{daysLive} ngày</span>
                                        )}
                                        <p className="text-[10px] text-slate-500 mt-1 font-mono">{comp.publishedAt ? new Date(comp.publishedAt).toLocaleDateString('vi-VN') : ''}</p>
                                    </td>
                                    <td className="p-4 align-middle">
                                        {editNoteId === comp.id ? (
                                            <div className="flex items-center gap-2">
                                                <input autoFocus value={editNoteText} onChange={e => setEditNoteText(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white text-xs outline-none" />
                                                <button onClick={() => { onUpdate(comp.id, { note: editNoteText }); setEditNoteId(null); }} className="text-emerald-400 hover:text-emerald-300"><CheckCircle2 size={16} /></button>
                                                <button onClick={() => setEditNoteId(null)} className="text-slate-500 hover:text-slate-300"><X size={16} /></button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between group/note cursor-pointer" onClick={() => { if (canEdit) { setEditNoteId(comp.id); setEditNoteText(comp.note || ''); } }}>
                                                <span className={`text-xs ${comp.note ? 'text-slate-300' : 'text-slate-600 italic'}`}>{comp.note || (canEdit ? 'Bấm để note tên sách...' : 'Không có ghi chú')}</span>
                                                {canEdit && <Edit2 size={12} className="text-slate-500 opacity-0 group-hover/note:opacity-100 transition-opacity" />}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 align-middle pr-6">
                                        <div className="flex items-center justify-end gap-2">
                                            {canEdit && (
                                                <>
                                                    <button onClick={() => handleCheckLive(comp)} disabled={isCheckingAll} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-xs font-bold rounded-lg border border-slate-700 transition flex items-center gap-1" title="Check Live API">
                                                        <Activity size={14} /> {isCheckingAll ? '...' : 'Check'}
                                                    </button>
                                                    <button onClick={() => onDelete(comp.id)} disabled={isCheckingAll} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg transition disabled:opacity-50 opacity-0 group-hover:opacity-100" title="Xóa"><Trash2 size={16} /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredCompetitors.length === 0 && (
                            <tr><td colSpan="4" className="p-16 text-center text-slate-500"><Eye size={48} className="mx-auto mb-4 opacity-50 text-slate-500" /><p className="font-medium text-lg text-slate-400">Không tìm thấy hoặc chưa có video nào</p></td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const getDateKey = (value = new Date()) => {
    const date = value instanceof Date ? value : new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const addDaysToDateKey = (dateKey, days) => {
    const [year, month, day] = String(dateKey || '').split('-').map(Number);
    if (!year || !month || !day) return '';
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
};
const getStrikeExpiryKey = strike => strike?.expiresAt || addDaysToDateKey(strike?.strikeDate, 90);
const getDaysUntilDateKey = (dateKey, todayKey = getDateKey()) => {
    if (!dateKey) return null;
    const end = new Date(`${dateKey}T00:00:00Z`);
    const start = new Date(`${todayKey}T00:00:00Z`);
    if (Number.isNaN(end.getTime()) || Number.isNaN(start.getTime())) return null;
    return Math.ceil((end.getTime() - start.getTime()) / 86400000);
};
const isActiveCopyrightStrike = (strike, todayKey = getDateKey()) => {
    const expiryKey = getStrikeExpiryKey(strike);
    // Date-based strikes are authoritative: an accidental early "resolved" flag
    // must never end the 90-day countdown.
    if (expiryKey) return expiryKey > todayKey;
    return strike?.status !== 'resolved' && strike?.status !== 'expired';
};
const formatDateKeyVi = dateKey => {
    if (!dateKey) return 'Chưa có ngày';
    const [year, month, day] = dateKey.split('-');
    return `${day}/${month}/${year}`;
};

// --- CHANNEL HEALTH VIEW ---
const ChannelHealthView = ({ userRole, currentUserData, strikes = [], channels = [], onAddStrike, onDeleteStrike }) => {
    const todayKey = getDateKey();
    const bktChannels = channels.filter(channel => channel.category === 'monetized')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'vi'));
    const [selectedChannelId, setSelectedChannelId] = useState('');
    const [strikeDate, setStrikeDate] = useState(todayKey);
    const [note, setNote] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const managedChannels = userRole === 'admin' ? channels : channels.filter(channel => currentUserData && channel.manager === currentUserData.name);
    const allowedChannelIds = new Set(managedChannels.map(channel => channel.id));
    const allowedEmails = new Set(managedChannels.map(channel => channel.email?.toLowerCase()).filter(Boolean));
    const visibleStrikes = (userRole === 'member' ? strikes.filter(strike =>
        allowedChannelIds.has(strike.channelId) || (strike.email && allowedEmails.has(strike.email.toLowerCase()))
    ) : strikes).slice().sort((a, b) => String(getStrikeExpiryKey(b) || '').localeCompare(String(getStrikeExpiryKey(a) || '')));
    const selectedChannel = bktChannels.find(channel => channel.id === selectedChannelId);
    const activeStrikeCount = selectedChannel ? strikes.filter(strike =>
        (strike.channelId === selectedChannel.id || (!strike.channelId && strike.email && selectedChannel.email && strike.email.toLowerCase() === selectedChannel.email.toLowerCase()))
        && isActiveCopyrightStrike(strike, todayKey)
    ).length : 0;
    const expiryPreview = addDaysToDateKey(strikeDate, 90);

    const handleSubmit = async event => {
        event.preventDefault();
        if (!selectedChannel) return alert('Hãy chọn một kênh BKT.');
        if (activeStrikeCount >= 2) return alert('Kênh này đã có 2 gậy bản quyền đang hiệu lực. Không thể thêm gậy thứ 3.');
        setIsSaving(true);
        try {
            await onAddStrike({
                channelId: selectedChannel.id, channelName: selectedChannel.name || '', manager: selectedChannel.manager || '',
                email: selectedChannel.email || '', strikeDate, expiresAt: expiryPreview, note: note.trim(),
            });
            setNote('');
        } catch (error) {
            alert(error?.message || 'Không thể lưu gậy bản quyền.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-5 animate-in fade-in">
            <section className="dashboard-panel channel-health-hero">
                <div className="channel-health-heading"><span className="channel-health-icon"><ShieldAlert size={24} /></span>
                    <div><h2 className="dashboard-title">Sức khỏe Hệ thống Kênh</h2><p>Theo dõi gậy bản quyền và thời điểm an toàn để đăng bài trở lại.</p></div>
                </div>
                <div className="channel-health-rule"><strong>Tối đa 2 gậy đang hiệu lực</strong><span>Gậy tự hết hạn sau 90 ngày. Gậy thứ 3 có thể khiến kênh bị chấm dứt.</span></div>
            </section>

            {userRole === 'admin' && (
                <form className="dashboard-panel strike-entry-panel" onSubmit={handleSubmit}>
                    <div className="strike-entry-title"><div><span className="eyebrow">ADMIN CONTROL</span><h3>Thêm gậy cho kênh BKT</h3></div>
                        {selectedChannel && <span className={`strike-count-badge ${activeStrikeCount >= 2 ? 'danger' : ''}`}>{activeStrikeCount}/2 gậy hiệu lực</span>}
                    </div>
                    <div className="strike-entry-grid">
                        <label><span>Kênh BKT</span><select value={selectedChannelId} onChange={event => setSelectedChannelId(event.target.value)} required>
                            <option value="">Chọn kênh cần theo dõi</option>
                            {bktChannels.map(channel => <option key={channel.id} value={channel.id}>{channel.name} — {channel.manager || 'Chưa gán'}</option>)}
                        </select></label>
                        <label><span>Ngày bị strike</span><input type="date" value={strikeDate} max={todayKey} onChange={event => setStrikeDate(event.target.value)} required /></label>
                        <label className="strike-note-field"><span>Ghi chú (không bắt buộc)</span><input value={note} onChange={event => setNote(event.target.value)} placeholder="Video bị gỡ, đơn vị khiếu nại..." maxLength={240} /></label>
                    </div>
                    <div className="strike-entry-footer"><div className="strike-expiry-preview"><Clock size={16} /> Hết hạn dự kiến <strong>{formatDateKeyVi(expiryPreview)}</strong></div>
                        <button type="submit" className="hope-button" disabled={isSaving || !selectedChannelId || activeStrikeCount >= 2}><Plus size={17} /> {isSaving ? 'Đang lưu...' : 'Thêm gậy bản quyền'}</button>
                    </div>
                </form>
            )}

            <div className="strike-list">
                {visibleStrikes.length === 0 ? (
                    <div className="dashboard-panel strike-empty-state"><span><Shield size={34} /></span><h3>Hệ thống đang an toàn</h3><p>Chưa có gậy bản quyền nào được ghi nhận cho các kênh bạn quản lý.</p></div>
                ) : visibleStrikes.map(strike => {
                    const channel = channels.find(item => item.id === strike.channelId || (item.email && strike.email && item.email.toLowerCase() === strike.email.toLowerCase()));
                    const channelName = channel?.name || strike.channelName || `Kênh chưa xác định${strike.email ? ` (${strike.email})` : ''}`;
                    const manager = channel?.manager || strike.manager || 'Chưa gán';
                    const expiryKey = getStrikeExpiryKey(strike);
                    const daysLeft = getDaysUntilDateKey(expiryKey, todayKey);
                    const hasExpiry = Boolean(expiryKey);
                    const isExpired = hasExpiry && daysLeft !== null && daysLeft <= 0;
                    const isLegacyResolved = !hasExpiry && (strike.status === 'resolved' || strike.status === 'expired');
                    const isEnded = isExpired || isLegacyResolved;
                    const isLegacyEmail = !strike.strikeDate;
                    const source = String(strike.source || '').toLowerCase();
                    const isEmailSource = source !== 'manual' && (['email', 'gmail', 'gmail_scan', 'email_scan'].includes(source) || (!source && Boolean(strike.email && (strike.content || strike.snippet || strike.subject))));
                    return (
                        <article key={strike.id} className={`strike-card ${isEnded ? 'is-safe' : 'is-active'}`}>
                            <div className="strike-card-icon"><AlertOctagon size={22} /></div>
                            <div className="strike-card-body">
                                <div className="strike-card-topline"><div className="min-w-0"><h3>{channelName}</h3><p>Quản lý: <strong>{manager}</strong> <span className="strike-source-label">{isEmailSource ? 'Tự động từ email' : 'Admin nhập tay'}</span></p></div>
                                    <span className={`strike-status ${isEnded ? 'safe' : daysLeft !== null && daysLeft <= 14 ? 'urgent' : ''}`}>
                                        {isExpired ? 'Đã hết gậy' : isLegacyResolved ? 'Đã xử lý' : daysLeft === null ? 'Cần xử lý' : `Còn ${daysLeft} ngày`}
                                    </span>
                                </div>
                                {isLegacyEmail ? <div className="strike-legacy-content">{strike.content || strike.snippet || 'Cảnh báo được ghi nhận từ email.'}</div> : <>
                                    <div className="strike-timeline"><div><span>Ngày bị strike</span><strong>{formatDateKeyVi(strike.strikeDate)}</strong></div><ArrowRight size={16} /><div><span>Hết hạn 90 ngày</span><strong>{formatDateKeyVi(expiryKey)}</strong></div></div>
                                    {!isEnded && daysLeft !== null && <div className="strike-progress" aria-label={`Còn ${daysLeft} ngày`}><span style={{ width: `${Math.max(3, Math.min(100, ((90 - daysLeft) / 90) * 100))}%` }} /></div>}
                                    {strike.note && <p className="strike-note">{strike.note}</p>}
                                </>}
                            </div>
                            {userRole === 'admin' && <div className="strike-card-actions">
                                <button onClick={() => { if (window.confirm('Xóa vĩnh viễn bản ghi này khỏi hệ thống?')) onDeleteStrike(strike.id); }} className="strike-delete-button" title="Xóa bản ghi"><Trash2 size={16} /></button>
                            </div>}
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

// --- MAIN APP COMPONENT ---
/** Standalone apps that own a URL: /hopelab and /fb (FB Studio keeps its sub-tab in /fb/<tab>). */
const tabFromPath = () => {
    const path = window.location.pathname;
    if (path === '/hopelab') return 'hopelab';
    if (path === '/fb' || path.startsWith('/fb/')) return 'fb';
    return '';
};

export default function App() {
    const cachedGoogleSession = useMemo(() => getCachedGoogleSession(), []);
    const storyFrameLogin = useMemo(() => readStoryFrameRequest(window.location.search), []);
    const [storyFrameBusy, setStoryFrameBusy] = useState(false);
    const [storyFrameAuthError, setStoryFrameAuthError] = useState('');
    const viralLogin = useMemo(() => readViralRequest(window.location.search), []);
    const [isAuthenticated, setIsAuthenticated] = useState(!!cachedGoogleSession);
    const [authStatus, setAuthStatus] = useState(cachedGoogleSession ? 'approved' : 'loading');
    const [authError, setAuthError] = useState('');
    const embeddedBrowser = getEmbeddedBrowserName();
    const [pendingAccess, setPendingAccess] = useState([]);
    const [approvingAccess, setApprovingAccess] = useState(false);
    const [userRole, setUserRole] = useState(cachedGoogleSession?.role || 'staff');
    const [currentUserData, setCurrentUserData] = useState(cachedGoogleSession?.staff || null);
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState(() => tabFromPath() || localStorage.getItem('activeTab') || 'dashboard');
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [youtubeApiKey, setYoutubeApiKey] = useState('');
    const [exchangeRate, setExchangeRate] = useState(25000); // Mặc định 25k
    const [introducedWebsites, setIntroducedWebsites] = useState([]);

    // NEW: Competitors Settings
    const [competitorHighlightDays, setCompetitorHighlightDays] = useState(7);
    const [competitorAccess, setCompetitorAccess] = useState([]);

    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [logs, setLogs] = useState([]);
    const [unreadLogs, setUnreadLogs] = useState(false);
    const [quickSearch, setQuickSearch] = useState('');
    const [nasQuery, setNasQuery] = useState('');
    const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false);

    // FIX: Session state for Notice Popup (Only show once per session)
    const [sessionNoticeShown, setSessionNoticeShown] = useState(false);

    // State để highlight task khi click từ log
    // State để highlight task khi click từ log
    const [highlightedTaskId, setHighlightedTaskId] = useState(null);
    const [pendingTaskFilter, setPendingTaskFilter] = useState(null); // Filter assignee từ Dashboard

    useEffect(() => {
        if (activeTab !== 'fb') localStorage.setItem('activeTab', activeTab);
        const routed = tabFromPath();
        if (activeTab === 'hopelab' && routed !== 'hopelab') window.history.pushState({}, '', '/hopelab');
        if (activeTab === 'fb' && routed !== 'fb') window.history.pushState({}, '', '/fb');
        if (activeTab !== 'hopelab' && activeTab !== 'fb' && routed) window.history.pushState({}, '', '/');
    }, [activeTab]);

    useEffect(() => {
        const handlePopState = () => setActiveTab(tabFromPath() || localStorage.getItem('activeTab') || 'dashboard');
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    useEffect(() => {
        if (!auth) return;
        setPersistence(auth, browserLocalPersistence).catch(error => {
            console.error('Firebase persistence error:', error);
            setAuthError(getGoogleAuthErrorMessage(error));
        });
    }, []);

    useEffect(() => {
        if (!auth) return;
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                if (localStorage.getItem('hopeteam_google_login_started') !== '1') {
                    await signOut(auth);
                    setUser(null); setIsAuthenticated(false); setCurrentUserData(null); setAuthStatus('signed_out');
                    return;
                }
                setAuthStatus('loading');
                try {
                    // A network blip or a cold server must not sign the user out: retry a few times before giving up
                    let response;
                    for (let attempt = 0; ; attempt += 1) {
                        try {
                            const token = await currentUser.getIdToken(attempt > 0);
                            response = await fetch('/api/google-access', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                            if (response.status < 500 && response.status !== 401) break;
                        } catch (networkError) {
                            if (attempt >= 3) throw networkError;
                        }
                        if (attempt >= 3) break;
                        await new Promise(resolve => setTimeout(resolve, 1500 * (attempt + 1)));
                    }
                    const responseText = await response.text();
                    let access;
                    try { access = JSON.parse(responseText); }
                    catch (e) { throw new Error(responseText || 'API xác thực không trả về dữ liệu hợp lệ.'); }
                    if (response.status === 202) {
                        localStorage.removeItem('hopeteam_cached_session');
                        setUser(null); setIsAuthenticated(false); setCurrentUserData(null); setAuthStatus('pending');
                    } else if (response.ok && access.status === 'approved') {
                        localStorage.setItem('hopeteam_cached_session', JSON.stringify({ role: access.role, staff: access.staff }));
                        setUser(currentUser); setCurrentUserData(access.staff); setUserRole(access.role); setIsAuthenticated(true); setAuthStatus('approved'); setAuthError('');
                    } else throw new Error(access.error || 'Không thể xác minh quyền truy cập.');
                } catch (error) {
                    localStorage.removeItem('hopeteam_cached_session');
                    setUser(null); setIsAuthenticated(false); setAuthStatus('error'); setAuthError(error.message);
                }
            } else {
                localStorage.removeItem('hopeteam_cached_session');
                setUser(null);
                setIsAuthenticated(false);
                setCurrentUserData(null);
                setAuthStatus('signed_out');
            }
        });
        return () => unsubscribe();
    }, []);

    const finishStoryFrameLogin = async () => {
        if (!storyFrameLogin?.valid || !user || !isAuthenticated || authStatus !== 'approved' || storyFrameBusy) return;
        setStoryFrameBusy(true);setStoryFrameAuthError('');
        const controller = new AbortController();
        const deadline = setTimeout(() => controller.abort(), 15000);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch('/api/storyframe-auth', {
                method: 'POST', signal: controller.signal,
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                body: JSON.stringify({ state: storyFrameLogin.state, callback: storyFrameLogin.callback, challenge: storyFrameLogin.challenge })
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(payload.error || 'Không thể cấp phiên StoryFrame.');
            if (payload.protocol !== 2 || payload.callback !== storyFrameLogin.callback || !/^[A-Za-z0-9_-]{43,128}$/.test(payload.code || '')) throw new Error('Phản hồi kết nối không hợp lệ. Hãy cập nhật đồng bộ web và app.');
            const callbackUrl = new URL(storyFrameLogin.callback);
            callbackUrl.searchParams.set('code', payload.code);
            callbackUrl.searchParams.set('state', storyFrameLogin.state);
            window.location.replace(callbackUrl.toString());
        } catch (error) {
            setStoryFrameAuthError(error.name === 'AbortError' ? 'Kết nối quá thời gian chờ. Hãy thử lại.' : error.message || 'Không thể kết nối StoryFrame.');
            setStoryFrameBusy(false);
        } finally { clearTimeout(deadline); }
    };
    useEffect(() => {
        if (!storyFrameLogin) return;
        const meta = document.createElement('meta');
        meta.name = 'referrer';meta.content = 'no-referrer';document.head.appendChild(meta);
        const title = document.title;document.title = 'StoryFrame · Kết nối HopeTeam';
        return () => { meta.remove();document.title = title; };
    }, [storyFrameLogin]);

    // Viral Studio (viral.hopeteam.xyz) staff sign-in: same one-time code + PKCE as StoryFrame, web callback.
    const finishViralLogin = async () => {
        if (!viralLogin?.valid || !user || !isAuthenticated || authStatus !== 'approved' || storyFrameBusy) return;
        setStoryFrameBusy(true);setStoryFrameAuthError('');
        const controller = new AbortController();
        const deadline = setTimeout(() => controller.abort(), 15000);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch('/api/viral-auth', {
                method: 'POST', signal: controller.signal,
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                body: JSON.stringify({ state: viralLogin.state, callback: viralLogin.callback, challenge: viralLogin.challenge })
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(payload.error || 'Không thể đăng nhập Viral Studio.');
            if (payload.protocol !== 'viral-1' || payload.callback !== viralLogin.callback || !/^[A-Za-z0-9_-]{43,128}$/.test(payload.code || '')) throw new Error('Phản hồi kết nối không hợp lệ. Hãy thử lại.');
            const callbackUrl = new URL(viralLogin.callback);
            callbackUrl.searchParams.set('code', payload.code);
            callbackUrl.searchParams.set('state', viralLogin.state);
            window.location.replace(callbackUrl.toString());
        } catch (error) {
            setStoryFrameAuthError(error.name === 'AbortError' ? 'Kết nối quá thời gian chờ. Hãy thử lại.' : error.message || 'Không thể kết nối Viral Studio.');
            setStoryFrameBusy(false);
        } finally { clearTimeout(deadline); }
    };
    useEffect(() => {
        if (!viralLogin) return;
        const meta = document.createElement('meta');
        meta.name = 'referrer';meta.content = 'no-referrer';document.head.appendChild(meta);
        const title = document.title;document.title = 'Viral Studio · Kết nối HopeTeam';
        return () => { meta.remove();document.title = title; };
    }, [viralLogin]);

    // Data Fetching
    const [tasks, setTasks] = useState([]);
    const [channels, setChannels] = useState([]);
    const [staff, setStaff] = useState([]);
    const [resources, setResources] = useState([]);
    const [revenues, setRevenues] = useState([]);
    const [notices, setNotices] = useState([]);
    const [unusedBooks, setUnusedBooks] = useState([]);
    const [fundTransactions, setFundTransactions] = useState([]);
    const [fundPercentage, setFundPercentage] = useState(3); // Mặc định 3%
    const [competitors, setCompetitors] = useState([]);
    const [strikes, setStrikes] = useState([]);
    const [copyrightBooks, setCopyrightBooks] = useState([]);

    useEffect(() => {
        if (!user || !db) return;

        const getColRef = (colName) => collection(db, 'artifacts', appId, 'public', 'data', colName);

        const handleError = (err, name) => {
            console.error(`Lỗi tải ${name}:`, err);
        };

        // Updated Query: Sort tasks ascending (nearest date first)
        const unsubTasks = onSnapshot(query(getColRef('tasks'), orderBy('date', 'asc')),
            (snap) => setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Tasks')
        );

        const unsubChannels = onSnapshot(getColRef('channels'),
            (snap) => setChannels(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Channels')
        );

        const unsubStaff = onSnapshot(getColRef('staff'),
            (snap) => setStaff(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Staff')
        );

        const unsubResources = onSnapshot(query(getColRef('resources'), orderBy('createdAt', 'desc')),
            (snap) => setResources(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Resources')
        );

        const unsubRevenues = onSnapshot(query(getColRef('revenues'), orderBy('createdAt', 'desc')),
            (snap) => setRevenues(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Revenues')
        );

        const unsubNotices = onSnapshot(query(getColRef('notices'), orderBy('createdAt', 'desc')),
            (snap) => setNotices(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Notices')
        );

        const unsubUnusedBooks = onSnapshot(query(getColRef('unused_books'), orderBy('createdAt', 'desc')),
            (snap) => setUnusedBooks(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'UnusedBooks')
        );


        const unsubFundTx = onSnapshot(query(getColRef('fundTransactions'), orderBy('date', 'desc')),
            (snap) => setFundTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'FundTransactions')
        );

        const unsubCompetitors = onSnapshot(query(getColRef('competitors'), orderBy('createdAt', 'desc')),
            (snap) => setCompetitors(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Competitors')
        );

        const unsubStrikes = onSnapshot(query(getColRef('channel_strikes'), orderBy('createdAt', 'desc')),
            (snap) => setStrikes(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => handleError(err, 'Channel Strikes')
        );

        const unsubCopyrightBooks = onSnapshot(getColRef('copyright_books'),
            (snap) => setCopyrightBooks(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => String(b.updatedKey || '').localeCompare(String(a.updatedKey || '')))),
            (err) => handleError(err, 'Copyright Books')
        );

        const unsubLogs = onSnapshot(query(collection(db, 'artifacts', appId, 'public', 'data', 'logs'), orderBy('createdAt', 'desc'), limit(50)),
            (snap) => {
                const newLogs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setLogs(newLogs);
                if (newLogs.length > 0) setUnreadLogs(true);
            },
            (err) => handleError(err, 'Logs')
        );

        const unsubSettings = onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'global_config'), (docSnap) => {
            if (docSnap.exists()) {
                const settingsData = docSnap.data();
                if (settingsData.geminiApiKey) setApiKey(settingsData.geminiApiKey);
                if (settingsData.youtubeApiKey) setYoutubeApiKey(settingsData.youtubeApiKey);
                if (settingsData.exchangeRate) setExchangeRate(settingsData.exchangeRate);
                if (settingsData.introducedWebsites) setIntroducedWebsites(settingsData.introducedWebsites);
                if (settingsData.competitorHighlightDays) setCompetitorHighlightDays(settingsData.competitorHighlightDays);
                if (settingsData.competitorAccess) setCompetitorAccess(settingsData.competitorAccess);
            }
        }, (err) => handleError(err, 'Settings'));

        return () => { unsubTasks(); unsubChannels(); unsubStaff(); unsubLogs(); unsubResources(); unsubRevenues(); unsubNotices(); unsubUnusedBooks(); unsubFundTx(); unsubCompetitors(); unsubStrikes(); unsubCopyrightBooks(); unsubSettings(); };
    }, [user]);

    const addLog = async (action, details, type = 'info', metadata = {}) => {
        if (!user) return;
        const authorName = userRole === 'admin' ? 'Admin' : (currentUserData ? currentUserData.name : 'Unknown');
        try { await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'logs'), { action, details, type, metadata, createdAt: serverTimestamp(), user: userRole, author: authorName }); } catch (e) { console.error("Log error", e); }
    };

    const handleLogout = () => {
        localStorage.removeItem('hopeteam_google_login_started');
        localStorage.removeItem('hopeteam_cached_session');
        signOut(auth);
        setIsAuthenticated(false);
        setCurrentUserData(null);
        localStorage.removeItem('audiobook_internal_auth');
        localStorage.removeItem('audiobook_user_role');
        localStorage.removeItem('audiobook_current_staff');
    }

    const handleGoogleLogin = async () => {
        setAuthError('');
        if (embeddedBrowser) {
            setAuthError(`Google không hỗ trợ đăng nhập trong ${embeddedBrowser}. Hãy mở hopeteam.xyz bằng Safari hoặc Chrome.`);
            return;
        }
        setAuthStatus('loading');
        try {
            localStorage.setItem('hopeteam_google_login_started', '1');
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });
            await signInWithPopup(auth, provider);
        } catch (error) {
            localStorage.removeItem('hopeteam_google_login_started');
            setAuthStatus('signed_out');
            setAuthError(getGoogleAuthErrorMessage(error));
        }
    };

    const handleCopyLoginLink = async () => {
        const loginUrl = 'https://hopeteam.xyz';
        try {
            await navigator.clipboard.writeText(loginUrl);
            setAuthError('Đã sao chép hopeteam.xyz. Hãy dán đường dẫn vào Safari hoặc Chrome để đăng nhập.');
        } catch (_) {
            window.prompt('Sao chép đường dẫn này và mở bằng Safari hoặc Chrome:', loginUrl);
        }
    };

    const loadPendingAccess = async () => {
        if (!auth.currentUser || userRole !== 'admin') return;
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch('/api/google-access', { headers: { Authorization: `Bearer ${token}` } });
            if (response.ok) setPendingAccess((await response.json()).requests || []);
        } catch (error) { console.error('Pending access error', error); }
    };

    useEffect(() => {
        if (!isAuthenticated || userRole !== 'admin' || !user) return;
        loadPendingAccess();
        const refreshPending = () => loadPendingAccess();
        const interval = window.setInterval(refreshPending, 300000);
        window.addEventListener('focus', refreshPending);
        return () => { window.clearInterval(interval); window.removeEventListener('focus', refreshPending); };
    }, [isAuthenticated, userRole, user]);

    const handleApproveAccess = async (requestUid, staffId) => {
        setApprovingAccess(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch('/api/google-access', { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ requestUid, staffId }) });
            if (!response.ok) throw new Error((await response.json()).error || 'Không thể duyệt tài khoản');
            await loadPendingAccess();
        } catch (error) { alert(error.message); }
        finally { setApprovingAccess(false); }
    };

    const handleRejectAccess = async (requestUid) => {
        if (!window.confirm('Từ chối và xóa yêu cầu đăng nhập này?')) return;
        setApprovingAccess(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch('/api/google-access', { method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ requestUid }) });
            if (!response.ok) throw new Error((await response.json()).error || 'Không thể từ chối tài khoản');
            await loadPendingAccess();
        } catch (error) { alert(error.message); }
        finally { setApprovingAccess(false); }
    };

    // Action Handlers
    const handleAddTask = async (task) => {
        if (user) {
            const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'tasks'), { ...task, createdAt: serverTimestamp() });
            addLog('Thêm Task', `Task "${task.bookTitle}" đã được tạo`, 'success', { taskId: docRef.id });
        }
    };

    const handleSaveCopyright = async (id, data) => {
        if (!user || !['admin', 'staff', 'member'].includes(userRole)) throw new Error('Chỉ thành viên đã được duyệt mới có thể cập nhật Kho bản quyền.');
        const duplicate = copyrightBooks.find(record => record.id !== id && (record.normalizedTitle === data.normalizedTitle || (record.normalizedAliases || []).includes(data.normalizedTitle) || data.normalizedAliases.some(alias => alias === record.normalizedTitle || (record.normalizedAliases || []).includes(alias))));
        if (duplicate) throw new Error('Tên sách hoặc alias đã tồn tại trong hồ sơ ' + duplicate.title + '.');
        const revisionKey = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + '-' + user.uid;
        const payload = { ...data, revisionKey, updatedKey: new Date().toISOString(), updatedBy: currentUserData?.name || user.displayName || userRole, updatedAt: serverTimestamp() };
        if (id) await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'copyright_books', id), payload);
        else await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'copyright_books'), { ...payload, createdAt: serverTimestamp() });
        addLog('Kho bản quyền', (id ? 'Cập nhật ' : 'Thêm ') + data.title + ' — ' + (data.status === 'copyright' ? 'Có bản quyền' : 'An toàn'), data.status === 'copyright' ? 'warning' : 'success');
    };

    const handleDeleteCopyright = async record => {
        if (userRole !== 'admin') return;
        if (!window.confirm('Xóa hồ sơ bản quyền ' + record.title + '? Các task sẽ trở về trạng thái Chưa kiểm tra.')) return;
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'copyright_books', record.id));
        addLog('Kho bản quyền', 'Đã xóa hồ sơ ' + record.title, 'warning');
    };
    const handleDeleteTask = async (id, taskTitle) => {
        if (user) {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'tasks', id));
            addLog('Xóa Task', `Đã xóa task: "${taskTitle || 'Không rõ'}"`, 'warning');
        }
    };

    const handleUpdateTask = async (id, data, taskTitle) => {
        if (user) {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'tasks', id), data);

            let logDetail = `Task "${taskTitle || '...'}" đã cập nhật`;
            if (data.status) logDetail = `Task "${taskTitle}" chuyển sang trạng thái: ${data.status}`;
            if (data.isUnsafe !== undefined) logDetail = `Task "${taskTitle}" được đánh dấu: ${data.isUnsafe ? 'Rủi ro' : 'An toàn'}`;

            addLog('Cập nhật Task', logDetail, 'info', { taskId: id });
        }
    };

    const handleAddChannel = async (d) => {
        if (user) {
            const timestamps = { createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
            if (d.category === 'eligible') timestamps.eligibleSince = serverTimestamp();
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'channels'), { ...d, ...timestamps });
            addLog('Thêm Kênh', `Kênh ${d.name} được thêm`, 'success');
        }
    };
    const handleDeleteChannel = async (id) => { if (user) { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'channels', id)); addLog('Xóa Kênh', `Đã xóa 1 kênh`, 'warning'); } };

    const handleAddStaff = async (d) => { if (user) { await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'staff'), d); addLog('Thêm Nhân sự', `Nhân viên ${d.name} gia nhập`, 'success'); } };
    const handleUpdateStaff = async (id, d) => { if (user) { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'staff', id), d); } };
    const handleDeleteStaff = async (id) => { if (user) { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'staff', id)); addLog('Xóa Nhân sự', `Đã xóa nhân viên`, 'warning'); } };

    const handleAddResource = async (res) => { if (user) { await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'resources'), { ...res, createdAt: serverTimestamp() }); addLog('Tài nguyên', `Đã chia sẻ ${res.type}: ${res.title}`, 'success'); } };
    const handleDeleteResource = async (id) => { if (user) { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'resources', id)); } };

    const handleRefreshChannel = async (id, data) => {
        if (user) {
            const currentChannel = channels.find(channel => channel.id === id);
            const updates = { ...data, updatedAt: serverTimestamp() };
            if (data.category === 'eligible' && currentChannel?.category !== 'eligible') {
                updates.eligibleSince = serverTimestamp();
            }
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'channels', id), updates);
        }
    };
    const handleSaveRevenue = async (data) => {
        if (user) {
            // Use setDoc with generated ID to ensure one entry per channel per month (Upsert)
            const docId = `${data.channelId}_${data.month}`;
            await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'revenues', docId), { ...data, updatedAt: serverTimestamp(), createdAt: data.createdAt || serverTimestamp() }, { merge: true });
            addLog('Doanh thu', `Đã cập nhật doanh thu tháng ${data.month}`, 'success');
        }
    };

    const handleUpdateResource = async (id, data) => { if (user) { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'resources', id), data); } };

    // NOTICE BOARD HANDLERS
    const handleAddNotice = async (notice) => { if (user) { await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'notices'), { ...notice, createdAt: serverTimestamp() }); addLog('Thông báo', `Admin đã đăng thông báo mới`, 'info'); } };
    const handleDeleteNotice = async (id) => { if (user) { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'notices', id)); } };

    // UNUSED BOOKS HANDLERS
    const handleAddUnusedBook = async (book) => { if (user) { await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'unused_books'), { ...book, createdAt: serverTimestamp() }); addLog('Kho Sách', `Thêm sách: ${book.title}`, 'info'); } };
    const handleDeleteUnusedBook = async (id, title) => { if (user) { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'unused_books', id)); addLog('Kho Sách', `Xóa sách: ${title}`, 'warning'); } };
    const handleUpdateUnusedBook = async (id, data) => { if (user) { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'unused_books', id), data); addLog('Kho Sách', `Cập nhật sách`, 'info'); } };

    // COMPETITORS HANDLERS
    const handleAddCompetitor = async (comp) => { if (user) { await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'competitors'), { ...comp, createdAt: serverTimestamp() }); addLog('Đối thủ', `Bắt đầu theo dõi: ${comp.title}`, 'success'); } };
    const handleUpdateCompetitor = async (id, data) => { if (user) { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'competitors', id), data); } };
    const handleDeleteCompetitor = async (id) => { if (user) { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'competitors', id)); addLog('Đối thủ', `Ngừng theo dõi 1 kênh/video`, 'warning'); } };

    // STRIKE HANDLERS
    const handleAddStrike = async data => {
        if (userRole !== 'admin') throw new Error('Chỉ admin được thêm gậy bản quyền.');
        const todayKey = getDateKey();
        const activeCount = strikes.filter(strike =>
            strike.channelId === data.channelId && isActiveCopyrightStrike(strike, todayKey)
        ).length;
        if (activeCount >= 2) throw new Error('Kênh này đã có 2 gậy đang hiệu lực. Không thể thêm gậy thứ 3.');
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'channel_strikes'), {
            ...data, source: 'manual', status: 'active', createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        addLog('Sức khỏe', `Thêm gậy bản quyền cho ${data.channelName}; hết hạn ${formatDateKeyVi(data.expiresAt)}`, 'warning');
    };
    const handleDeleteStrike = async id => {
        if (userRole !== 'admin') return;
        try {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'channel_strikes', id));
        } catch (error) {
            console.error('Delete error:', error);
            alert('Lỗi khi xóa bản ghi này.');
        }
    };

    // Xử lý click vào log (Đã nâng cấp)
    const handleLogClick = (targetId, tab) => {
        setActiveTab(tab);
        if (tab === 'tasks' && targetId) {
            setHighlightedTaskId(targetId);
            setTimeout(() => setHighlightedTaskId(null), 3000);
        }
        setIsNotifOpen(false);
    };

    if (!auth) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">Chưa cấu hình Firebase.</div>;
    if (viralLogin) return <ViralConnect request={viralLogin} ready={!!user && isAuthenticated && authStatus === 'approved'} loading={authStatus === 'loading' || (isAuthenticated && !user)} busy={storyFrameBusy} error={storyFrameAuthError || authError} pending={authStatus === 'pending'} staff={currentUserData} email={user?.email || ''} onLogin={handleGoogleLogin} onApprove={finishViralLogin} onSwitch={handleLogout} />;
    if (storyFrameLogin) return <StoryFrameConnect request={storyFrameLogin} ready={!!user && isAuthenticated && authStatus === 'approved'} loading={authStatus === 'loading' || (isAuthenticated && !user)} busy={storyFrameBusy} error={storyFrameAuthError || authError} pending={authStatus === 'pending'} staff={currentUserData} email={user?.email || ''} onLogin={handleGoogleLogin} onApprove={finishStoryFrameLogin} onSwitch={handleLogout} />;
    if (!isAuthenticated) return <GoogleLoginScreen onLogin={handleGoogleLogin} onCopyLoginLink={handleCopyLoginLink} embeddedBrowser={embeddedBrowser} authStatus={authStatus} errorMessage={storyFrameAuthError || authError} />;

    const hasTeamWideAccess = userRole === 'admin' || userRole === 'staff';
    const visibleTasks = hasTeamWideAccess || !currentUserData ? tasks : tasks.filter(t => t.assignee === currentUserData.name);
    const visibleChannels = hasTeamWideAccess || !currentUserData ? channels : channels.filter(c => c.manager === currentUserData.name);
    const visibleStaff = hasTeamWideAccess || !currentUserData ? staff : staff.filter(s => s.id === currentUserData.id || s.name === currentUserData.name);

    // One provider at the root of both layouts keeps the call alive while switching tabs.
    const withVoice = (content) => (
        <VoiceProvider db={db} auth={auth} appId={appId} currentUid={user?.uid}>
        <NasProvider db={db} auth={auth} appId={appId} currentUid={user?.uid}>
            {content}
            <VoiceMiniBar hidden={activeTab === 'voice'} onOpen={() => setActiveTab('voice')} />
        </NasProvider>
        </VoiceProvider>
    );

    if (activeTab === 'fb') {
        return withVoice(
            <div className="hopelab-standalone-shell">
                <header className="hopelab-standalone-header">
                    <button type="button" className="hopelab-standalone-brand" onClick={() => setActiveTab('dashboard')}>
                        <FbMark size={34} />
                        <div><strong>FB Studio</strong><small>HopeTeam Facebook</small></div>
                    </button>
                    <div className="hopelab-standalone-session">
                        <button type="button" className="hopelab-back-dashboard" onClick={() => setActiveTab('dashboard')}>
                            <ChevronLeft size={15} /> Về dashboard
                        </button>
                        <div className="hopelab-session-person">
                            {currentUserData?.avatar ? <img src={currentUserData.avatar} alt={currentUserData.name || ''} /> : <span>{(currentUserData?.name || 'HT').slice(0, 2).toUpperCase()}</span>}
                            <div><strong>{currentUserData?.name || 'HopeTeam'}</strong><small>{userRole}</small></div>
                        </div>
                        <button type="button" className="hopelab-standalone-logout" onClick={handleLogout} title="Đăng xuất"><LogOut size={16} /></button>
                    </div>
                </header>
                <main className="hopelab-standalone-main">
                    <FbStudio firebaseUser={user} />
                </main>
            </div>
        );
    }

    if (activeTab === 'hopelab') {
        return withVoice(
            <div className="hopelab-standalone-shell">
                <header className="hopelab-standalone-header">
                    <button type="button" className="hopelab-standalone-brand" onClick={() => setActiveTab('dashboard')}>
                        <span><BookOpen size={18} /></span>
                        <div><strong>HopeLab</strong><small>Audiobook Discovery</small></div>
                    </button>
                    <div className="hopelab-standalone-session">
                        <button type="button" className="hopelab-back-dashboard" onClick={() => setActiveTab('dashboard')}>
                            <ChevronLeft size={15} /> Về dashboard
                        </button>
                        <div className="hopelab-session-person">
                            {currentUserData?.avatar ? <img src={currentUserData.avatar} alt={currentUserData.name || ''} /> : <span>{(currentUserData?.name || 'HT').slice(0, 2).toUpperCase()}</span>}
                            <div><strong>{currentUserData?.name || 'HopeTeam'}</strong><small>{userRole}</small></div>
                        </div>
                        <button type="button" className="hopelab-standalone-logout" onClick={handleLogout} title="Đăng xuất"><LogOut size={16} /></button>
                    </div>
                </header>
                <main className="hopelab-standalone-main">
                    <HopeLabView firebaseUser={user} userRole={userRole} currentUserData={currentUserData} staff={visibleStaff} channels={visibleChannels} tasks={tasks} onNavigate={(tab) => setActiveTab(tab)} />
                </main>
            </div>
        );
    }

    const normalizeQuickSearch = (value = '') => value.toLocaleLowerCase('vi-VN').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').trim();
    const quickSearchItems = [
        { id: 'dashboard', label: 'Trung tâm Chỉ huy', description: 'Dashboard tổng hợp', icon: LayoutDashboard, keywords: 'trang chủ dashboard tổng quan thống kê thông báo chiến thần cần đẩy nhanh' },
        { id: 'tasks', label: 'Quản lý Task', description: 'Task và lịch đăng bài', icon: ListTodo, keywords: 'task công việc nhiệm vụ lịch đăng sách chờ đăng đã xong ai lên lịch' },
        { id: 'unused-books', label: 'Kho Sách Chờ', description: 'Danh sách sách chưa sử dụng', icon: BookOpen, keywords: 'kho sách chờ unused book bulk upload', visible: userRole === 'admin' || userRole === 'staff' },
        { id: 'competitors', label: 'Theo dõi đối thủ', description: 'Kênh và video đối thủ', icon: Eye, keywords: 'đối thủ competitor theo dõi video keyword từ khóa', visible: userRole === 'admin' || (currentUserData && competitorAccess.includes(currentUserData.name)) },
        { id: 'revenue-board', label: 'Bảng Doanh Thu', description: 'Doanh thu và bảng lương', icon: DollarSign, keywords: 'doanh thu revenue tiền lương hoa hồng usd vnd bảng lương' },
        { id: 'channels', label: 'Quản lý Kênh', description: 'Danh sách kênh YouTube', icon: Youtube, keywords: 'kênh youtube channel bkt subscriber quản lý kênh' },
        { id: 'resources', label: 'Kho Tài nguyên', description: 'Tài nguyên dùng chung', icon: Folder, keywords: 'tài nguyên resource file stock mail thư mục' },
        { id: 'nas', label: 'Kho NAS', description: 'Audiobook đã lưu trên NAS', icon: HardDrive, keywords: 'kho nas lưu trữ backup tải về download audio mp3 m4b torrent synology' },
        { id: 'voice', label: 'Phòng họp', description: 'Voice chat và chia sẻ màn hình', icon: Headphones, keywords: 'phòng họp voice chat gọi thoại share screen chia sẻ màn hình hướng dẫn discord meeting' },
        { id: 'team-fund', label: 'Quỹ Nội Bộ', description: 'Giao dịch và đóng góp', icon: PiggyBank, keywords: 'quỹ nội bộ fund giao dịch đóng góp tiền' },
        { id: 'channel-health', label: 'Sức khỏe Hệ Thống', description: 'Cảnh báo hệ thống kênh', icon: ShieldAlert, keywords: 'sức khỏe hệ thống strike gậy bản quyền cảnh báo email', visible: true },
        { id: 'staff', label: 'Nhân sự & Hoa hồng', description: 'Hồ sơ, role và tài khoản chờ duyệt', icon: Users, keywords: 'nhân sự thành viên staff member admin role hoa hồng duyệt google lương', visible: userRole === 'admin' },
        { id: 'settings', label: 'Cài đặt', description: 'Cấu hình hệ thống', icon: Settings, keywords: 'cài đặt setting cấu hình api key google firebase tỷ giá', visible: userRole === 'admin' },
    ].filter(item => item.visible !== false);
    const normalizedQuickSearch = normalizeQuickSearch(quickSearch);
    const quickSearchResults = normalizedQuickSearch
        ? quickSearchItems.filter(item => normalizeQuickSearch(`${item.label} ${item.description} ${item.keywords}`).includes(normalizedQuickSearch)).slice(0, 7)
        : [];
    const openQuickSearchItem = (item) => {
        setActiveTab(item.id);
        setQuickSearch('');
        setIsQuickSearchOpen(false);
    };

    return withVoice(
        <div className="app-shell min-h-screen bg-slate-950 flex font-sans text-slate-200 selection:bg-blue-500/30">
            <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen} userRole={userRole} currentUserData={currentUserData} onLogout={handleLogout} competitorAccess={competitorAccess} />
            <div className="flex-1 flex flex-col min-w-0 relative z-10">
                <header className="app-header h-16 flex items-center justify-between px-6 sticky top-0 z-40 liquid-glass border-b border-white/5 transition-all">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setIsMobileOpen(true)} className="md:hidden text-slate-400 hover:text-white"><Menu size={24} /></button>
                        <div className="relative app-search app-search-quick hidden sm:block">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                            <input type="search" value={quickSearch} onChange={e => { setQuickSearch(e.target.value); setIsQuickSearchOpen(true); }} onFocus={() => setIsQuickSearchOpen(true)} onBlur={() => window.setTimeout(() => setIsQuickSearchOpen(false), 150)} onKeyDown={e => { if (e.key === 'Enter' && quickSearchResults[0]) openQuickSearchItem(quickSearchResults[0]); if (e.key === 'Escape') setIsQuickSearchOpen(false); }} placeholder="Tìm kiếm nhanh..." aria-label="Tìm nhanh chức năng" aria-expanded={isQuickSearchOpen && !!quickSearch} className="app-search-input py-1.5 pl-9 pr-4 text-sm text-slate-200 w-72" />
                            {isQuickSearchOpen && quickSearch && <div className="quick-search-menu">
                                {quickSearchResults.length ? quickSearchResults.map(item => { const QuickIcon = item.icon; return <button key={item.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => openQuickSearchItem(item)} className="quick-search-result"><span className="quick-search-icon"><QuickIcon size={16} /></span><span className="min-w-0"><strong>{item.label}</strong><small>{item.description}</small></span><ArrowRight size={14} className="ml-auto shrink-0" /></button>; }) : <div className="quick-search-empty">Không tìm thấy mục phù hợp</div>}
                                <NasQuickResults query={quickSearch} onOpen={item => { setNasQuery(item.work); setActiveTab('nas'); setQuickSearch(''); setIsQuickSearchOpen(false); }} />
                            </div>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 relative">
                        <a href="/prompt" className="header-prompt-launch header-prompt-featured" aria-label="Mở trang tạo prompt audiobook" title="Tạo prompt audiobook">
                            <Sparkles size={16} aria-hidden="true" />
                            <span className="header-prompt-launch-label">Tạo prompt</span>
                        </a>
                        <button
                            type="button"
                            className="header-hopelab-launch"
                            onClick={() => setActiveTab('fb')}
                            aria-label="Mở FB Studio"
                            title="FB Studio — nuôi page Facebook"
                        >
                            <span className="header-hopelab-launch-icon"><FbMark size={18} /></span>
                            <span className="header-hopelab-launch-label">FB Studio</span>
                        </button>
                        <button
                            type="button"
                            className="header-hopelab-launch"
                            onClick={() => setActiveTab('hopelab')}
                            aria-label="Mở HopeLab Research"
                            title="Mở HopeLab Research"
                        >
                            <span className="header-hopelab-launch-icon"><Sparkles size={16} /></span>
                            <span className="header-hopelab-launch-label">HopeLab</span>
                        </button>
                        <div className="flex items-center gap-1 text-emerald-500 text-xs bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20">
                            <Wifi size={12} /> Online
                        </div>
                        <button onClick={() => { setIsNotifOpen(!isNotifOpen); setUnreadLogs(false); }} className="relative text-slate-400 hover:text-white transition-colors p-2 rounded-full hover:bg-slate-800">
                            <Bell size={20} />
                            {unreadLogs && <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-slate-900 animate-pulse"></span>}
                        </button>
                        <NotificationPanel isOpen={isNotifOpen} onClose={() => setIsNotifOpen(false)} logs={logs} onLogClick={handleLogClick} userRole={userRole} currentUserData={currentUserData} staff={staff} />
                    </div>
                </header>

                {/* REALTIME TOAST NOTIFICATION FOR ADMIN */}
                {userRole === 'admin' && <AdminActivityToast logs={logs} currentUserData={currentUserData} />}

                {/* STAFF TASK REMINDER TOAST */}
                {currentUserData && <StaffTaskReminderToast tasks={tasks.filter(t => t.assignee === currentUserData.name)} currentUserData={currentUserData} onNavigate={(tab) => setActiveTab(tab)} />}

                <main className="flex-1 p-4 md:p-8 overflow-y-auto">
                    <div className="max-w-7xl mx-auto">
                        {activeTab === 'dashboard' && <DashboardView tasks={userRole === 'admin' ? visibleTasks : tasks.filter(task => task.assignee === currentUserData?.name)} channels={userRole === 'admin' ? visibleChannels : channels.filter(channel => channel.manager === currentUserData?.name)} staff={userRole === 'admin' ? visibleStaff : staff.filter(person => person.id === currentUserData?.id || person.name === currentUserData?.name)} allStaff={staff} allRevenues={revenues} allChannels={channels} currentUserData={userRole === 'admin' ? null : currentUserData} apiKey={apiKey} onNavigate={(tab, filter) => { setActiveTab(tab); if (filter) setPendingTaskFilter(filter); }} notices={notices} onAddNotice={handleAddNotice} onDeleteNotice={handleDeleteNotice} userRole={userRole} revenues={revenues} exchangeRate={exchangeRate} sessionNoticeShown={sessionNoticeShown} setSessionNoticeShown={setSessionNoticeShown} introducedWebsites={introducedWebsites} strikes={userRole === 'member' ? strikes.filter(strike => strike.channelId ? channels.some(channel => channel.id === strike.channelId && channel.manager === currentUserData?.name) : channels.some(channel => channel.manager === currentUserData?.name && channel.email && strike.email && channel.email.toLowerCase() === strike.email.toLowerCase())) : strikes} />}
                        {activeTab === 'tasks' && <TasksView copyrightBooks={copyrightBooks} userRole={userRole} currentUserData={currentUserData} onSaveCopyright={handleSaveCopyright} onDeleteCopyright={handleDeleteCopyright} tasks={visibleTasks} channels={visibleChannels} staff={visibleStaff} onAddTask={handleAddTask} onDeleteTask={handleDeleteTask} onUpdateTask={handleUpdateTask} canEdit={true} canCreate={true} canUseAi={hasTeamWideAccess} canUseRepository={hasTeamWideAccess} canReturnTask={true} canDelete={userRole === 'admin'} canManageCopyright={['admin', 'staff', 'member'].includes(userRole)} canDeleteCopyright={userRole === 'admin'} highlightedTaskId={highlightedTaskId} apiKey={apiKey} unusedBooks={unusedBooks} onDeleteUnusedBook={handleDeleteUnusedBook} initialFilterAssignee={pendingTaskFilter} onClearInitialFilter={() => setPendingTaskFilter(null)} onReturnToLibrary={handleAddUnusedBook} />}
                        {activeTab === 'hopelab' && <HopeLabView firebaseUser={user} userRole={userRole} currentUserData={currentUserData} staff={visibleStaff} channels={visibleChannels} tasks={tasks} onNavigate={(tab) => setActiveTab(tab)} />}
                        {activeTab === 'unused-books' && <UnusedBooksView unusedBooks={unusedBooks} onAddBook={handleAddUnusedBook} onDeleteBook={handleDeleteUnusedBook} onUpdateBook={handleUpdateUnusedBook} />}
                        {activeTab === 'voice' && <VoiceView userRole={userRole} currentUid={user?.uid} />}
                        {activeTab === 'nas' && <NasView userRole={userRole} initialQuery={nasQuery} />}
                        {activeTab === 'team-fund' && <TeamFundView userRole={userRole} revenues={revenues} channels={channels} staff={staff} fundTransactions={fundTransactions} fundPercentage={fundPercentage} exchangeRate={exchangeRate} onAddTransaction={async (tx) => { if (userRole === 'admin') await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'fundTransactions'), { ...tx, createdAt: serverTimestamp() }); }} onDeleteTransaction={async (id) => { if (userRole === 'admin') await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'fundTransactions', id)); }} />}
                        {activeTab === 'channel-health' && <ChannelHealthView userRole={userRole} currentUserData={currentUserData} strikes={strikes} channels={channels} onAddStrike={handleAddStrike} onDeleteStrike={handleDeleteStrike} />}
                        {(activeTab === 'channels' || activeTab === 'resources') && <ResourcesView viewMode={activeTab} channels={visibleChannels} staff={visibleStaff} onAddChannel={handleAddChannel} onDeleteChannel={handleDeleteChannel} canEdit={true} canCreateChannel={true} canEditResources={true} canEditRevenue={true} canMoveToMonetized={true} canDelete={userRole === 'admin'} canManageMonetization={userRole === 'admin'} apiKey={apiKey} youtubeApiKey={youtubeApiKey} resources={resources} onAddResource={handleAddResource} onDeleteResource={handleDeleteResource} onUpdateResource={handleUpdateResource} onRefreshChannel={handleRefreshChannel} onSaveRevenue={handleSaveRevenue} revenues={revenues} exchangeRate={exchangeRate} />}
                        {userRole === 'admin' && activeTab === 'staff' && <><AccessApprovalPanel requests={pendingAccess} staff={staff} onApprove={handleApproveAccess} onReject={handleRejectAccess} onRefresh={loadPendingAccess} loading={approvingAccess} /><StaffView staff={staff} tasks={tasks} channels={channels} onAddStaff={handleAddStaff} onUpdateStaff={handleUpdateStaff} onDeleteStaff={handleDeleteStaff} canEdit={true} canDelete={true} apiKey={apiKey} onSaveRevenue={handleSaveRevenue} revenues={revenues} exchangeRate={exchangeRate} /></>}
                        {userRole === 'admin' && activeTab === 'settings' && <SettingsView apiKey={apiKey} setApiKey={setApiKey} exchangeRate={exchangeRate} setExchangeRate={setExchangeRate} introducedWebsites={introducedWebsites} setIntroducedWebsites={setIntroducedWebsites} competitorHighlightDays={competitorHighlightDays} setCompetitorHighlightDays={setCompetitorHighlightDays} competitorAccess={competitorAccess} setCompetitorAccess={setCompetitorAccess} allStaff={staff} firebaseUser={user} />}
                        {activeTab === 'competitors' && <CompetitorsView competitors={competitors} onAdd={handleAddCompetitor} onUpdate={handleUpdateCompetitor} onDelete={handleDeleteCompetitor} canEdit={userRole === 'admin' || (currentUserData && competitorAccess.includes(currentUserData.name))} competitorHighlightDays={competitorHighlightDays || 7} youtubeApiKey={youtubeApiKey} />}
                        {activeTab === 'revenue-board' && <RevenueBoardView staff={visibleStaff} channels={visibleChannels} revenues={revenues} exchangeRate={exchangeRate} />}
                    </div>
                </main>
            </div>
        </div>
    );
}
